import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import appConfig from "./appConfig";
import Login from "./Login";
import FinishSetup from "./FinishSetup";
import { likeExact } from "./accountSetup";
import CSVImport from "./CSVImport";
import GoogleSync from "./GoogleSync";
import QuoteTemplate from "./QuoteTemplate";
import Measurements from "./Measurements";
import AdminPanel from "./AdminPanel";
import Help from "./Help";
import { APP_VERSION, APP_BUILD, APP_BUILD_DATE } from "./appVersion";
import { fmtMoney, normalizeTax, presetFor, regionsFor, COUNTRIES, CURRENCIES, DEFAULT_TAX } from "./taxConfig"; // PATCH oct18-tax
import { fmtDate, fmtDateTime, fmtTime } from "./formatDate"; // PATCH oct21-ui-customer, oct21-ui-more
import { cleanCity } from "./cleanCity"; // PATCH oct21-ui-fix2
// PATCH oct21-ui-fix: autoComplete="bp-off" stops Chrome address autofill on customer fields and search boxes
import { App as CapApp } from "@capacitor/app";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";

var VOIP_ENABLED = false;
var VOIP_PROVIDER = "openphone";

function makeCall(phone) {
  if (VOIP_ENABLED && VOIP_PROVIDER === "openphone") {
    window.open("openphone://call?number=" + phone, "_blank");
  } else {
    window.location.href = "tel:" + phone;
  }
}
// v98: quote a value for PostgREST .or() ilike filters. Unquoted, a comma or
// parenthesis in the search text (e.g. "Smith, John") breaks the whole query.
function orLike(term) {
  return '"%' + String(term).replace(/\\/g, "\\\\").replace(/"/g, '\\"') + '%"';
}
// v98 #5: sign-out clears everything EXCEPT device preferences.
var KEEP_ON_SIGNOUT = ["darkMode", "blindspro_exact_alarm_asked"];
function clearLocalKeepPrefs() {
  var keep = {};
  KEEP_ON_SIGNOUT.forEach(function (k) { var v = localStorage.getItem(k); if (v !== null) keep[k] = v; });
  localStorage.clear();
  Object.keys(keep).forEach(function (k) { localStorage.setItem(k, keep[k]); });
}
// v98 #1: Android 14+ turns exact alarms OFF by default for new installs
// (we no longer use USE_EXACT_ALARM). Ask once so reminders fire on time.
async function maybeAskExactAlarm() {
  try {
    if (Capacitor.getPlatform() !== "android") return;
    if (localStorage.getItem("blindspro_exact_alarm_asked")) return;
    var r = await LocalNotifications.checkExactNotificationSetting();
    if (!r || r.exact_alarm === "granted") return;
    localStorage.setItem("blindspro_exact_alarm_asked", "1");
    var ok = window.confirm("Allow exact reminders?\n\nSo job and personal reminders arrive right on time, turn on \"Alarms & reminders\" for BlindsPro CRM on the next screen.");
    if (ok) await LocalNotifications.changeExactNotificationSetting();
  } catch (e) {
    // Plugin version without this API, or not supported: skip silently.
  }
}
function normalizePhone(phone) {
  if (!phone) return "";
  var d = String(phone).replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  return d;
}
// PATCH dup-phone: Postgres unique violation (23505) on a phone column
function isDupPhone(error) {
  if (!error || error.code !== "23505") return false;
  var t = [error.message, error.details, error.hint].join(" ").toLowerCase();
  return t.indexOf("phone") !== -1;
}
var DUP_PHONE_MSG = "This phone number already belongs to another customer.";
// PATCH dup-phone2: find another customer in this company with the same last 10 digits.
// Returns the match or null. A failed lookup never blocks the save.
async function findPhoneOwner(phone, excludeId) {
  var d = String(phone || "").replace(/\D/g, "");
  if (d.length < 10 || !appConfig.companyId) return null;
  var last10 = d.slice(-10);
  var q = supabase.from("customers").select("id,full_name").eq("company_id", appConfig.companyId).ilike("phone_digits", "%" + last10);
  if (excludeId) q = q.neq("id", excludeId);
  var { data, error } = await q.limit(1);
  if (error || !data || data.length === 0) return null;
  return data[0];
}
// v103b: WhatsApp needs digits with country code. 10 digit numbers get a leading 1 (Canada/US).
function waNum(phone) {
  var d = String(phone || "").replace(/\D/g, "");
  if (d.length === 10) d = "1" + d;
  return d;
}
// PATCH oct16-fixes (#7): address plus area, area skipped if the address already has it.
function fullAddr(cu) {
  var addr = String((cu && cu.address) || "").trim().replace(/,(\s*,)+/g, ",").replace(/^[\s,]+|[\s,]+$/g, ""); // PATCH oct16-addrcomma, oct21-ui-more (bug 2)
  var area = String((cu && cu.area) || "").trim();
  if (!area) return addr;
  if (!addr) return area;
  return addr.toLowerCase().indexOf(area.toLowerCase()) === -1 ? addr + ", " + area : addr;
}
// v103b: local YYYY-MM-DD. toISOString() gives the UTC date, which is tomorrow after 5 PM in BC.
function localDateStr(dt) {
  var d = new Date(dt);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}
// v103b: PostgREST returns max 1000 rows per request. Page through all of them.
// buildQuery must return a query with a stable .order() so pages don't overlap.
async function fetchAllRows(buildQuery) {
  var all = [];
  var from = 0;
  var size = 1000;
  while (true) {
    var { data, error } = await buildQuery().range(from, from + size - 1);
    if (error) return { data: null, error: error };
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < size) break;
    from += size;
  }
  return { data: all, error: null };
}
// Session 31: plan limits. Trial companies are on "pro" (set by register_company).
var CUSTOMER_LIMITS = { starter: 200, pro: 500, business: Infinity };
function planLabel(plan) { return plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "current"; }
// How many more customers this company can add (Infinity = no limit).
// Unknown plan or a failed count does not block here; the database check is the final guard.
async function customerRoomLeft(plan) {
  var lim = CUSTOMER_LIMITS[plan];
  if (!lim || lim === Infinity) return Infinity;
  var { count, error } = await supabase.from("customers").select("id", { count: "exact", head: true }).eq("company_id", appConfig.companyId);
  if (error) return Infinity;
  return Math.max(0, lim - (count || 0));
}
function customerLimitMsg(plan) {
  var msg = "Customer limit reached. Your " + planLabel(plan) + " plan allows up to " + CUSTOMER_LIMITS[plan] + " customers.";
  if (!Capacitor.isNativePlatform()) msg += " Upgrade your plan to add more.";
  return msg;
}
// Session 31: WhatsApp template limits (count includes the 5 seeded at signup).
var TEMPLATE_LIMITS = { starter: 5, pro: 10, business: Infinity };
async function templateRoomLeft(plan) {
  var lim = TEMPLATE_LIMITS[plan];
  if (!lim || lim === Infinity) return Infinity;
  var { count, error } = await supabase.from("whatsapp_templates").select("id", { count: "exact", head: true }).eq("company_id", appConfig.companyId);
  if (error) return Infinity;
  return Math.max(0, lim - (count || 0));
}
function templateLimitMsg(plan) {
  var msg = "Template limit reached. Your " + planLabel(plan) + " plan allows up to " + TEMPLATE_LIMITS[plan] + " WhatsApp templates. Delete one to add a new one.";
  if (!Capacitor.isNativePlatform()) msg += " Or upgrade your plan to add more.";
  return msg;
}
// INPUT SANITIZATION - strip dangerous HTML/script characters
// PATCH audit-3to9 (#6): React already makes text safe, so notes are saved as typed.
// The old version turned < > into &lt; &gt; and cut words like "donation=".
function sanitize(str) {
  if (!str) return "";
  return String(str).trim();
}
// Old notes were saved with &lt; &gt;. Show them as < >.
function unescNote(s) {
  return String(s || "").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
}
// NATIVE REMINDER NOTIFICATIONS - fire even when app is closed/backgrounded
function reminderNotifId(id) {
  var str = String(id);
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 2147483647 || 1;
}
async function scheduleReminderNotification(id, remindAtISOString, title, body) {
  try {
    var fireDate = new Date(remindAtISOString);
    if (isNaN(fireDate.getTime()) || fireDate.getTime() <= Date.now()) return;
    var notifId = reminderNotifId(id);
    await LocalNotifications.cancel({ notifications: [{ id: notifId }] });
    await LocalNotifications.schedule({
      notifications: [{ id: notifId, title: title, body: body, channelId: "reminders_v2", ongoing: true, schedule: { at: fireDate, allowWhileIdle: true, exact: true } }],
    });
  } catch (e) {
    console.log("Notification schedule error:", e);
  }
}
async function cancelReminderNotification(id) {
  try {
    await LocalNotifications.cancel({ notifications: [{ id: reminderNotifId(id) }] });
  } catch (e) {}
}
// PATCH oct17-photo: shrink a photo before upload (max 1600 px, JPEG).
// Returns a Blob, or null if this device cannot read the image (for example HEIC).
function shrinkImage(file) {
  var MAX = 1600;
  return new Promise(function (resolve) {
    var url = null;
    function done(b) { try { if (url) URL.revokeObjectURL(url); } catch (_) {} resolve(b); }
    try {
      url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          if (!w || !h) { done(null); return; }
          var scale = Math.min(1, MAX / Math.max(w, h));
          var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
          var canvas = document.createElement("canvas");
          canvas.width = cw; canvas.height = ch;
          var ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, cw, ch);
          ctx.drawImage(img, 0, 0, cw, ch);
          canvas.toBlob(function (b) { done(b && b.size > 0 ? b : null); }, "image/jpeg", 0.82);
        } catch (e) { done(null); }
      };
      img.onerror = function () { done(null); };
      img.src = url;
    } catch (e) { done(null); }
  });
}
// PATCH oct18-notes: split the notes text into separate notes. Each note starts with a
// date line like [2026-10-18, 11:18:00 a.m.]. Text before the first date line is one note too.
var NOTE_HEAD = /^\[[^\]]*\d[^\]]*:[^\]]*\]$/;
function parseNotes(notes) {
  var blocks = [];
  var cur = null;
  String(notes || "").split("\n").forEach(function (ln) {
    var t = ln.replace(/\r$/, "");
    if (NOTE_HEAD.test(t.trim())) {
      cur = { header: t.trim(), lines: [] };
      blocks.push(cur);
    } else {
      if (!cur) { cur = { header: "", lines: [] }; blocks.push(cur); }
      cur.lines.push(t);
    }
  });
  return blocks.map(function (b) {
    var ls = b.lines.slice();
    while (ls.length && !ls[ls.length - 1].trim()) ls.pop();
    while (ls.length && !ls[0].trim()) ls.shift();
    return { header: b.header, body: ls.join("\n") };
  }).filter(function (b) { return b.header || b.body; });
}
function joinNotes(blocks) {
  return blocks.map(function (b) {
    if (!b.header) return b.body;
    return b.body ? b.header + "\n" + b.body : b.header;
  }).join("\n\n");
}
function findNote(blocks, blk) {
  for (var i = 0; i < blocks.length; i++) if (blocks[i].header === blk.header && blocks[i].body === blk.body) return i;
  return -1;
}
// PATCH oct18-tax: logo is saved in the database, so it is small (max 256 px, PNG keeps transparency)
function readFileAsDataUrl(file) {
  return new Promise(function (resolve) {
    try {
      var r = new FileReader();
      r.onload = function (e) { resolve(e.target.result); };
      r.onerror = function () { resolve(null); };
      r.readAsDataURL(file);
    } catch (e) { resolve(null); }
  });
}
function shrinkLogoSrc(src) {
  var MAX = 256;
  return new Promise(function (resolve) {
    try {
      var img = new Image();
      img.onload = function () {
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          if (!w || !h) { resolve(null); return; }
          var scale = Math.min(1, MAX / Math.max(w, h));
          var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
          var canvas = document.createElement("canvas");
          canvas.width = cw; canvas.height = ch;
          var ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, cw, ch);
          var out = canvas.toDataURL("image/png");
          if (!out || out.length > 380000) {
            ctx.globalCompositeOperation = "destination-over";
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, cw, ch);
            out = canvas.toDataURL("image/jpeg", 0.85);
          }
          resolve(out && out.indexOf("data:image/") === 0 && out.length <= 400000 ? out : null);
        } catch (e) { resolve(null); }
      };
      img.onerror = function () { resolve(null); };
      img.src = src;
    } catch (e) { resolve(null); }
  });
}
// PATCH oct18-tax: Quote History line. Older quotes have no tax_lines and show GST.
function quoteTaxSummary(q) {
  var cur = q.currency || "CAD";
  var parts = ["Subtotal: " + fmtMoney(q.subtotal || 0, cur)];
  if (Array.isArray(q.tax_lines) && q.tax_lines.length) {
    q.tax_lines.forEach(function (t) { parts.push((q.tax_mode === "included" ? "incl. " : "") + t.name + ": " + fmtMoney(t.amount || 0, cur)); });
  } else if (!Array.isArray(q.tax_lines)) {
    parts.push("GST: " + fmtMoney(q.gst || 0, cur));
  }
  parts.push("Total: " + fmtMoney(q.total || 0, cur));
  return parts.join(" | ");
}
// PATCH oct21-ui-home: line icons (stroke 2, 24 viewBox, currentColor). No emoji.
var ICONS = {
  search: [{ c: [11, 11, 7] }, "M21 21l-4.35-4.35"],
  userPlus: ["M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2", { c: [9, 7, 4] }, "M19 8v6", "M22 11h-6"],
  phone: ["M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"],
  bell: ["M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9", "M13.73 21a2 2 0 0 1-3.46 0"],
  clock: [{ c: [12, 12, 9] }, "M12 7v5l3 2"],
  megaphone: ["M3 11v2a1 1 0 0 0 1 1h3l5 4V6L7 10H4a1 1 0 0 0-1 1z", "M16 9a4 4 0 0 1 0 6", "M19 6a8 8 0 0 1 0 12"],
  chat: ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"],
  file: ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6", "M8 13h8", "M8 17h5"],
  ruler: ["M3 8h18v8H3z", "M7 8v3", "M11 8v4", "M15 8v3", "M19 8v4"],
  map: ["M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2z", "M9 4v14", "M15 6v14"],
  whatsapp: ["M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.1-5.4A8.5 8.5 0 1 1 21 11.5z", "M9 9.5c.5 2.5 2.5 4.5 5 5"],
  users: ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", { c: [9, 7, 4] }, "M23 21v-2a4 4 0 0 0-3-3.87", "M16 3.13a4 4 0 0 1 0 7.75"],
  home: ["M3 10.5L12 3l9 7.5", "M5 9.5V21h14V9.5", "M10 21v-6h4v6"],
  chart: ["M3 20h18", "M7 16v-6", "M12 16V5", "M17 16v-4"],
  calendar: ["M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z", "M16 2v4", "M8 2v4", "M3 10h18"],
  wallet: ["M20 7H5a2 2 0 0 1 0-4h13v4", "M3 5v14a2 2 0 0 0 2 2h15V7", "M16 14h.01"],
  settings: ["M4 21v-7", "M4 10V3", "M12 21v-9", "M12 8V3", "M20 21v-5", "M20 12V3", "M1 14h6", "M9 8h6", "M17 16h6"],
  moon: ["M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"],
  sun: [{ c: [12, 12, 4] }, "M12 2v2", "M12 20v2", "M4.93 4.93l1.41 1.41", "M17.66 17.66l1.41 1.41", "M2 12h2", "M20 12h2", "M4.93 19.07l1.41-1.41", "M17.66 6.34l1.41-1.41"],
  chevronDown: ["M6 9l6 6 6-6"],
  chevronUp: ["M18 15l-6-6-6 6"],
  window: ["M4 3h16v18H4z", "M4 12h16", "M12 3v18"],
  x: ["M18 6L6 18", "M6 6l12 12"],
  image: ["M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z", { c: [8.5, 8.5, 1.5] }, "M21 15l-5-5L5 21"],
  receipt: ["M4 2v20l3-2 3 2 2-2 2 2 3-2 3 2V2l-3 2-3-2-2 2-2-2-3 2z", "M8 8h8", "M8 12h8", "M8 16h5"],
  card: ["M3 5h18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z", "M1 10h22"],
  help: [{ c: [12, 12, 9] }, "M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3", "M12 17h.01"],
  info: [{ c: [12, 12, 9] }, "M12 16v-4", "M12 8h.01"],
  smartphone: ["M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z", "M12 18h.01"], // PATCH oct21-ui-lists
  upload: ["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4", "M17 8l-5-5-5 5", "M12 3v12"],
  chevronRight: ["M9 18l6-6-6-6"],
  pencil: ["M12 20h9", "M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"],
  trash: ["M3 6h18", "M8 6V4h8v2", "M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6", "M10 11v6", "M14 11v6"],
  nav: ["M3 11l19-9-9 19-2-8-8-2z"],
  mail: ["M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z", "M22 6l-10 7L2 6"], // PATCH oct21-ui-email
  download: ["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4", "M7 10l5 5 5-5", "M12 15V3"],
  check: ["M20 6L9 17l-5-5"],
  arrowLeft: ["M19 12H5", "M12 19l-7-7 7-7"], // PATCH oct21-ui-dash
  chevronLeft: ["M15 18l-6-6 6-6"],
  lock: ["M5 11h14v10H5z", "M8 11V7a4 4 0 0 1 8 0v4"],
  send: ["M22 2L11 13", "M22 2l-7 20-4-9-9-4 20-7z"], // PATCH oct21-ui-more
  phoneOff: ["M10.7 13.3a16 16 0 0 0 3 2.3l1.3-1.3a2 2 0 0 1 2.1-.45c.9.34 1.85.57 2.8.7A2 2 0 0 1 22 16.9v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.13.96.36 1.9.7 2.8a2 2 0 0 1-.45 2.1L8.1 9.9", "M23 1L1 23"],
  voicemail: [{ c: [6, 12, 4] }, { c: [18, 12, 4] }, "M6 16h12"],
  camera: ["M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z", { c: [12, 13, 4] }],
  link: ["M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71", "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"],
};
function Icon({ name, size, w }) { /* PATCH oct21-ui-bold */
  var parts = ICONS[name] || [];
  var s = size || 22;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w || 2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
      {parts.map((p, i) => (typeof p === "string" ? <path key={i} d={p} /> : <circle key={i} cx={p.c[0]} cy={p.c[1]} r={p.c[2]} />))}
    </svg>
  );
}
// PATCH oct21-ui-dash: white tile, number in colour
function KpiCard({ label, value, color, theme }) {
  return (
    <div style={{ background: theme ? theme.card : "white", border: "1px solid " + (theme ? theme.cardBorder : "#E2E7E6"), borderRadius: 14, padding: "14px 16px", flex: 1, minWidth: 130 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: theme ? theme.subText : "#3A4645", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color }}>{value}</div>
    </div>
  );
}
// PATCH oct21-ui-dash: bug 4, a status of NEW counts and shows as Lead
function normStatus(s) {
  s = String(s || "").trim().toLowerCase();
  return s === "new" ? "lead" : s;
}
function BusinessNameSetup({ onSave }) {
  var [name, setName] = useState(localStorage.getItem("blindspro_business_name") || appConfig.companyName);
  var [logo, setLogo] = useState(() => localStorage.getItem("businessLogo") || "");
  function handleLogo(e) {
    var f = e.target.files[0];
    if (!f) return;
    var r = new FileReader();
    r.onload = (ev) => setLogo(ev.target.result);
    r.readAsDataURL(f);
  }
  function handleSave() {
    if (!name.trim()) return;
    localStorage.setItem("businessName", name.trim());
    localStorage.setItem("blindspro_business_name", name.trim());
    if (logo) localStorage.setItem("businessLogo", logo);
    onSave(name.trim());
  }
  return (
    <div
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
    >
      <div style={{ background: "white", borderRadius: 16, padding: 28, maxWidth: 360, width: "100%" }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 20, textAlign: "center", color: "#1a1a1a" }}>Welcome!</h2>
        <p style={{ margin: "0 0 16px", fontSize: 14, color: "#555", textAlign: "center" }}>Set up your business profile to get started.</p>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <label style={{ cursor: "pointer", display: "inline-block" }}>
            {logo ? (
              <img src={logo} alt="Logo" style={{ width: 90, height: 90, borderRadius: 12, objectFit: "contain", border: "2px solid #2F6F6B" }} />
            ) : (
              <div
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: 12,
                  background: "#f0f2f5",
                  border: "2px dashed #2F6F6B",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                }}
              >
                <span style={{ fontSize: 28 }}>🖼️</span>
                <span style={{ fontSize: 11, color: "#2F6F6B", fontWeight: "bold", marginTop: 4 }}>Upload Logo</span>
              </div>
            )}
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogo} />
          </label>
          {logo && (
            <div style={{ fontSize: 11, color: "#2F6F6B", marginTop: 4, cursor: "pointer" }} onClick={() => setLogo("")}>
              ✕ Remove
            </div>
          )}
        </div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your business name"
          style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #2F6F6B", fontSize: 15, boxSizing: "border-box", marginBottom: 14, outline: "none" }}
        />
        <button
          onClick={handleSave}
          style={{ width: "100%", padding: 13, background: "#2F6F6B", color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer", fontWeight: "bold" }}
        >
          Save & Continue
        </button>
      </div>
    </div>
  );
}

function App() {
  const [session, setSession] = useState(null);
  // v98: getSession() + INITIAL_SESSION + TOKEN_REFRESHED each hand us a new
  // session object. Effects keyed on the user id run once per sign-in
  // instead of 3x per load (duplicate fetches, notification channel, timers).
  const sessionUserId = (session && session.user && session.user.id) || null;
  const [authLoading, setAuthLoading] = useState(true);
  const [companyPlan, setCompanyPlan] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [trialChecked, setTrialChecked] = useState(false);
  // Session 32: companies.status (active / trial / past_due / cancelled / inactive / disabled)
  const [companyStatus, setCompanyStatus] = useState(null);
  // Company lookup result: loading / found / none (login with no company) / error
  const [companyState, setCompanyState] = useState("loading");
  const [resolvedCompanyId, setResolvedCompanyId] = useState(null);
  // Paywall Monthly/Yearly toggle lives here so it survives re-renders
  const [billingInterval, setBillingInterval] = useState("monthly");
  // PATCH oct18-tax: company tax and currency, logo from the database, note being edited
  const [companyTax, setCompanyTax] = useState(DEFAULT_TAX);
  const [taxEdit, setTaxEdit] = useState(null);
  const [taxSaving, setTaxSaving] = useState(false);
  // PATCH oct20-minsqft: company minimum sq ft per window
  const [companyMinSqft, setCompanyMinSqft] = useState(9);
  const [minSqftEdit, setMinSqftEdit] = useState(null);
  const [minSqftSaving, setMinSqftSaving] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(() => { try { return localStorage.getItem("businessLogo") || ""; } catch (_) { return ""; } });
  const [logoBusy, setLogoBusy] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null); // PATCH oct18-qedit
  const [settingsOpen, setSettingsOpen] = useState(null); // PATCH oct21-ui-settings
  const [showTplForm, setShowTplForm] = useState(false); const [lastFU, setLastFU] = useState(null); /* PATCH oct21-ui-bold */ // PATCH oct21-ui-more
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === "PASSWORD_RECOVERY") return;
      if (localStorage.getItem("blindspro_registering")) return;
      setSession(s);
      setAuthLoading(false);
    });
    return () => listener?.subscription?.unsubscribe();
  }, []);
  // Plan / trial check. Runs once the company lookup has finished (no fixed wait).
  useEffect(() => {
    if (!session) return;
    if (companyState === "none" || companyState === "error") { setTrialChecked(true); return; }
    if (companyState !== "found" || !resolvedCompanyId) return;
    var cancelled = false;
    (async () => {
      var { data, error } = await supabase
        .from("companies")
        .select("plan, trial, trial_expires_at, status, tax_country, tax_region, tax_lines, tax_mode, currency, logo_data, min_sqft") // PATCH oct18-tax, oct20-minsqft
        .eq("company_id", resolvedCompanyId)
        .maybeSingle();
      if (cancelled) return;
      if (!data && !error) {
        // The company stored on this device no longer exists for this login
        ["blindspro_business_name", "businessName", "blindspro_company_id", "blindspro_cached_email"].forEach(function (k) {
          try { localStorage.removeItem(k); } catch (_) {}
        });
        setCompanyState("none");
        setTrialChecked(true);
        return;
      }
      if (data) {
        setCompanyPlan(data.plan);
        setCompanyStatus(data.status || null);
        setCompanyTax(normalizeTax(data)); // PATCH oct18-tax
        setCompanyMinSqft(data.min_sqft != null ? Number(data.min_sqft) : 9); // PATCH oct20-minsqft
        syncCompanyLogo(data.logo_data, resolvedCompanyId);
        if (data.trial && !data.trial_expires_at) setTrialExpired(true); /* trial with no end date = expired */
        if (data.trial && data.trial_expires_at) {
          var expiry = new Date(data.trial_expires_at);
          var now = new Date();
          var daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
          setTrialDaysLeft(daysLeft);
          if (now > expiry) setTrialExpired(true);
        }
      }
      setTrialChecked(true);
    })();
    return () => { cancelled = true; };
  }, [sessionUserId, companyState, resolvedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCheckout(priceId, planName) {
    setCheckoutLoading(planName);
    try {
      var companyId = localStorage.getItem("blindspro_company_id");
      var bName = localStorage.getItem("blindspro_business_name");
      var email = session?.user?.email;
      var accessToken = session?.access_token || "";
      var res = await fetch("https://www.blindsprocrm.com/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priceId, email, businessName: bName, companyId, accessToken }),
      });
      var data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert(data.error || "Error creating checkout. Please try again."); // Session 32
    } catch (e) {
      alert("Error: " + e.message);
    }
    setCheckoutLoading(false);
  }

  // Session 32: Stripe Customer Portal (owner + web only; the server refuses app requests too)
  const [portalLoading, setPortalLoading] = useState(false);
  async function openBillingPortal() {
    setPortalLoading(true);
    try {
      var sess = await supabase.auth.getSession();
      var accessToken = sess?.data?.session?.access_token || "";
      var res = await fetch("https://www.blindsprocrm.com/api/create-portal-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken, companyId: appConfig.companyId }),
      });
      var data = await res.json();
      if (data.url) { window.location.href = data.url; return; }
      alert(data.error || "Could not open billing. Please try again.");
    } catch (e) {
      alert("Error: " + e.message);
    }
    setPortalLoading(false);
  }

  var [businessName, setBusinessName] = useState(() => localStorage.getItem("businessName") || "");
  var [showNameSetup, setShowNameSetup] = useState(false);
  useEffect(() => {
    if (!session) return;
    // FIXED: previously skipped this whole lookup whenever ANY business name
    // was cached, regardless of which account it belonged to. On a device/
    // account switch this meant the new company's company_id/access_token
    // never got resolved or bridged to native, leaving the PREVIOUS company's
    // stale data active indefinitely (including for background contact sync).
    // Now: only skip if the cache actually belongs to the email that's signed
    // in right now. On mismatch, clear native auth data immediately (fail
    // closed) while the fresh lookup below resolves the correct company.
    var _cachedEmail = localStorage.getItem("blindspro_cached_email");
    var _sessionEmail = (session.user && session.user.email || "").toLowerCase();
    if (localStorage.getItem("blindspro_business_name") && localStorage.getItem("blindspro_company_id") && _cachedEmail === _sessionEmail) {
      setResolvedCompanyId(localStorage.getItem("blindspro_company_id"));
      setCompanyState("found");
      return;
    }
    if (window.AndroidBridge) {
      try { window.AndroidBridge.clearAuthData(); } catch (_) {}
      // #4 (v97): clearAuthData() also wipes the refresh token that
      // hybridStorage mirrored at sign-in. Re-mirror the CURRENT session so
      // background sync stays on the same refresh-token chain as JS.
      try {
        if (session.refresh_token && typeof window.AndroidBridge.saveSessionTokens === "function") {
          window.AndroidBridge.saveSessionTokens(session.access_token || "", session.refresh_token, String(session.expires_at || 0));
        }
      } catch (_) {}
    }
    // Auto-sync all customers to the phone's native Contacts on login.
    // Called from all three company-resolution paths below (owner email,
    // user_settings, team_members). Previously only the owner path had it,
    // so fallback/staff sign-ins never synced. Not awaited, so the UI isn't
    // held up by the multi-page fetch.
    const autoSyncPhoneContacts = async (companyId) => {
      if (!window.AndroidContacts || !companyId) return;
      if (window.__contactSyncRunning) return;
      window.__contactSyncRunning = true;
      try {
        // Page through in batches of 1000 (PostgREST caps one response).
        // Ordered by id so pages are stable - without ORDER BY, range()
        // pages can overlap or skip rows.
        const allContacts = [];
        const pageSize = 1000;
        let from = 0;
        while (true) {
          const { data: page, error: pageErr } = await supabase
            .from("customers")
            .select("full_name,phone")
            .eq("company_id", companyId)
            .not("phone", "is", null)
            .neq("phone", "")
            .order("id", { ascending: true })
            .range(from, from + pageSize - 1);
          if (pageErr) {
            // Never sync a partial list: native side deletes existing
            // contacts first, so a partial fetch would wipe the rest.
            console.log("Contact sync: page fetch failed, skipping", pageErr);
            return;
          }
          if (!page || page.length === 0) break;
          allContacts.push(...page);
          if (page.length < pageSize) break;
          from += pageSize;
        }
        if (allContacts.length === 0) return;
        console.log("Contact sync: sending " + allContacts.length + " contacts to native");
        window.__contactSyncDone = (count) => {
          if (count > 0) showToast(count + " contacts synced to phone!");
          delete window.__contactSyncDone;
        };
        window.AndroidContacts.syncAllContacts(JSON.stringify(allContacts));
      } catch (err) {
        console.log("Contact sync failed:", err);
      } finally {
        window.__contactSyncRunning = false;
      }
    };
    const loadBusinessName = async () => {
      try {
        // Look up company directly by owner_email
        // FIXED: was comparing raw-case session email against owner_email —
        // same case-mismatch pattern found in team_members/owner_email data
        // (stored emails with different upper/lower case). Normalize with ilike so a future
        // case drift doesn't silently break business name / company_id load.
        const { data: co } = await supabase
          .from("companies")
          .select("company_id, business_name")
          .ilike("owner_email", likeExact(session.user.email))
          .single();
        if (co && co.business_name) {
          localStorage.setItem("blindspro_business_name", co.business_name);
          localStorage.setItem("businessName", co.business_name);
          localStorage.setItem("blindspro_company_id", co.company_id);
          localStorage.setItem("blindspro_cached_email", (session.user.email || "").toLowerCase());
          // Bridge auth data to Android native (for PhoneListenerService caller lookup)
          if (window.AndroidBridge) {
            try {
              const _sess = await supabase.auth.getSession();
              const _tok = _sess?.data?.session?.access_token || "";
              window.AndroidBridge.saveAuthData(co.company_id, _tok);
            } catch (_) {}
          }
          setBusinessName(co.business_name); setResolvedCompanyId(co.company_id); setCompanyState("found");
          autoSyncPhoneContacts(co.company_id);
          // Reload all data now that company_id is set
          fetchCustomers("", "all", "all");
          fetchAreas();
          fetchStatusCounts();
          fetchUpcomingJobs();
          fetchUpcomingRemindersAndSync();
          checkReminders();
          checkWhatsAppReminders();
          fetchWaTemplates();
          afterCompanyResolved();
          return;
        }
        // Fallback: look up company_id via user_settings (e.g. localStorage was cleared)
        const { data: us } = await supabase
          .from("user_settings")
          .select("company_id")
          .eq("key", "user_id")
          .eq("value", session.user.id)
          .single();
        if (us && us.company_id) {
          const { data: co2 } = await supabase
            .from("companies")
            .select("company_id, business_name")
            .eq("company_id", us.company_id)
            .single();
          if (co2 && co2.business_name) {
            localStorage.setItem("blindspro_business_name", co2.business_name);
            localStorage.setItem("businessName", co2.business_name);
            localStorage.setItem("blindspro_company_id", co2.company_id);
            localStorage.setItem("blindspro_cached_email", (session.user.email || "").toLowerCase());
            // Bridge auth data to Android native (for PhoneListenerService caller lookup)
            if (window.AndroidBridge) {
              try {
                const _sess2 = await supabase.auth.getSession();
                const _tok2 = _sess2?.data?.session?.access_token || "";
                window.AndroidBridge.saveAuthData(co2.company_id, _tok2);
              } catch (_) {}
            }
            setBusinessName(co2.business_name); setResolvedCompanyId(co2.company_id); setCompanyState("found");
            autoSyncPhoneContacts(co2.company_id);
            fetchCustomers("", "all", "all");
            fetchAreas();
            fetchStatusCounts();
            fetchUpcomingJobs();
            fetchUpcomingRemindersAndSync();
            checkReminders();
            checkWhatsAppReminders();
            fetchWaTemplates();
            afterCompanyResolved();
            return;
          }
        }
        // Third fallback: look up via team_members by email (for staff accounts)
        const { data: tm } = await supabase
          .from("team_members")
          .select("company_id, role, name")
          .ilike("email", likeExact(session.user.email))
          .single();
        if (tm && tm.company_id) {
          const { data: co3 } = await supabase
            .from("companies")
            .select("company_id, business_name")
            .eq("company_id", tm.company_id)
            .single();
          if (co3 && co3.business_name) {
            localStorage.setItem("blindspro_business_name", co3.business_name);
            localStorage.setItem("businessName", co3.business_name);
            localStorage.setItem("blindspro_company_id", co3.company_id);
            localStorage.setItem("blindspro_role", tm.role || "staff");
            localStorage.setItem("blindspro_cached_email", (session.user.email || "").toLowerCase());
            if (window.AndroidBridge) {
              try {
                const _sess3 = await supabase.auth.getSession();
                const _tok3 = _sess3?.data?.session?.access_token || "";
                window.AndroidBridge.saveAuthData(co3.company_id, _tok3);
              } catch (_) {}
            }
            setBusinessName(co3.business_name); setResolvedCompanyId(co3.company_id); setCompanyState("found");
            autoSyncPhoneContacts(co3.company_id);
            fetchCustomers("", "all", "all");
            fetchAreas();
            fetchStatusCounts();
            fetchUpcomingJobs();
            fetchUpcomingRemindersAndSync();
            checkReminders();
            checkWhatsAppReminders();
            fetchWaTemplates();
            afterCompanyResolved();
            return;
          }
        }

        // No company found for this login: show Finish Account Setup
        setCompanyState("none");
      } catch (e) {
        // Lookup failed (for example no connection): show a retry screen
        setCompanyState("error");
      }
    };
    loadBusinessName();
  }, [sessionUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [phoneSearch, setPhoneSearch] = useState(""); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showPersonalReminder, setShowPersonalReminder] = useState(false);
  const [showPersonalReminderForm, setShowPersonalReminderForm] = useState(false);
  const [showWaTemplatesHome, setShowWaTemplatesHome] = useState(false);
  const [showManageContacts, setShowManageContacts] = useState(false);
  const [phoneImportContacts, setPhoneImportContacts] = useState([]);
  // Phone numbers already shown in a previous Import from Phone session.
  // Backed by the phone_import_seen table (not localStorage), so it
  // survives sign-out and the 8-hour idle auto-signout.
  const [phoneImportSeenSet, setPhoneImportSeenSet] = useState(new Set());
  const [showImportPhoneInfo, setShowImportPhoneInfo] = useState(false);
  const [showSearchInfo, setShowSearchInfo] = useState(false);
  const [phoneImportLoading, setPhoneImportLoading] = useState(false);
  const [phoneImportProgress, setPhoneImportProgress] = useState(null);
  const [phoneImportSelected, setPhoneImportSelected] = useState([]);
  const [phoneImportAccount, setPhoneImportAccount] = useState('all');
  const [phoneImportSearch, setPhoneImportSearch] = useState('');
  const [editingHomeJob, setEditingHomeJob] = useState(null);
  const [editingHomeReminder, setEditingHomeReminder] = useState(null);
  const [personalReminderDate, setPersonalReminderDate] = useState("");
  const [personalReminderTime, setPersonalReminderTime] = useState("");
  const [personalReminderMsg, setPersonalReminderMsg] = useState("");
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showFollowUpHistory, setShowFollowUpHistory] = useState(false);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [personalReminders, setPersonalReminders] = useState([]);
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [schedPrice, setSchedPrice] = useState("");
  const [schedReminderOffsets, setSchedReminderOffsets] = useState([]);
  const [schedNotes, setSchedNotes] = useState("");
  const [schedCustomOffsetValue, setSchedCustomOffsetValue] = useState("");
  const [schedCustomOffsetUnit, setSchedCustomOffsetUnit] = useState("hours");
  const [addingReminderJobId, setAddingReminderJobId] = useState(null);
  const [addReminderOffsets, setAddReminderOffsets] = useState([]);
  const [addReminderCustomValue, setAddReminderCustomValue] = useState("");
  const [addReminderCustomUnit, setAddReminderCustomUnit] = useState("hours");
  const REMINDER_OFFSET_OPTIONS = [
    { hours: 1, label: "1 hour before" },
    { hours: 12, label: "12 hours before" },
    { hours: 24, label: "1 day before" },
  ];
  const [customerJobs, setCustomerJobs] = useState([]);
  const [editingJob, setEditingJob] = useState(null);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [reminderMessage, setReminderMessage] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [quickNote, setQuickNote] = useState({});
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterArea, setFilterArea] = useState("all");
  const [sortMode, setSortMode] = useState(null); // null = default, "name" = A-Z, "newest" = newest first
  const searchDebounceRef = useRef(null);
  // v98 #5: only the most recently issued fetchCustomers() may update the list.
  const customersFetchSeq = useRef(0);
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      // FIXED: this used to fire unconditionally 400ms after every mount
      // (since `search` starts as ""), racing against the properly-guarded
      // company_id load path (line ~672) and loadBusinessName(). If the
      // company_id network lookup took longer than 400ms, this fired first
      // with an empty company_id, got a correct-but-premature 0-row result,
      // and stomped the real data once it arrived (search never changes
      // again, so this effect never re-ran to correct itself). Guard it so
      // it only runs once company_id is actually available.
      if (!appConfig.companyId) return;
      fetchCustomers(search, filterStatus, filterArea);
    }, 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const [allAreas, setAllAreas] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // True, unfiltered company-wide total — only updates when no search term
  // is active, so the top badge/subtitle never shows a search-filtered
  // number. totalCount above still reflects the current search results
  // (used by the "Customers (N)" list header, which is meant to change
  // while searching).
  const [companyCustomerCount, setCompanyCustomerCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [toast, setToast] = useState(null);
  const [dueReminders, setDueReminders] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [showWhatsAppTemplates, setShowWhatsAppTemplates] = useState(false);
  const [waTemplates, setWaTemplates] = useState([]);
  const [newTemplateLabel, setNewTemplateLabel] = useState("");
  const [newTemplateText, setNewTemplateText] = useState("");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [currentScreen, setCurrentScreen] = useState("list");
  const currentScreenRef = useRef("list");
  currentScreenRef.current = currentScreen;
  const [dashboardData, setDashboardData] = useState({});
  const [calendarJobs, setCalendarJobs] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [waReminders, setWaReminders] = useState([]);
  const [showWaReminders, setShowWaReminders] = useState(false);
  const [notContactedDays, setNotContactedDays] = useState(14);
  const [notContactedList, setNotContactedList] = useState([]);
  const [showNotContacted, setShowNotContacted] = useState(false);
  const [showLastContacted, setShowLastContacted] = useState(false);
  const [lastContactedDays, setLastContactedDays] = useState(7);
  const [lastContactedList, setLastContactedList] = useState([]);
  const [lastContactedLoading, setLastContactedLoading] = useState(false);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [referreeList, setReferreeList] = useState([]);
  // Customers referred BY the open profile (shown under Details)
  const [referredList, setReferredList] = useState([]);
  useEffect(() => {
    var rid = selectedCustomer && selectedCustomer.id;
    if (!rid || !appConfig.companyId) { setReferredList([]); return; }
    var cancelled = false;
    supabase.from("customers").select("*").eq("company_id", appConfig.companyId).eq("referred_by_id", rid).order("full_name", { ascending: true })
      .then(({ data }) => { if (!cancelled) setReferredList(data || []); });
    return () => { cancelled = true; };
  }, [selectedCustomer && selectedCustomer.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [notContactedLoading, setNotContactedLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showRecentCalls, setShowRecentCalls] = useState(false);
  // PATCH audit-3to9 (#8): Recent Calls loads the 50 most recently contacted from the whole list
  const [recentCallsList, setRecentCallsList] = useState([]);
  useEffect(() => {
    if (!showRecentCalls || !appConfig.companyId) return;
    var cancelled = false;
    supabase.from("customers").select("*").eq("company_id", appConfig.companyId).not("last_contacted", "is", null)
      .order("last_contacted", { ascending: false }).limit(50)
      .then(({ data }) => { if (!cancelled && data) setRecentCallsList(data); });
    return () => { cancelled = true; };
  }, [showRecentCalls]); // eslint-disable-line react-hooks/exhaustive-deps
  // const [showReviewPicker, setShowReviewPicker] = useState(false); // Phase 7 cleanup
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [teamCode, setTeamCode] = useState(() => localStorage.getItem("teamCode") || "");
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [chatPhoto, setChatPhoto] = useState(null);
  const chatEndRef = useRef(null);
  const [showBlast, setShowBlast] = useState(false);
  const [blastSearch, setBlastSearch] = useState("");
  const [blastStatus, setBlastStatus] = useState("all");
  const [blastArea, setBlastArea] = useState("all");
  const [blastSelected, setBlastSelected] = useState([]);
  const [blastMessage, setBlastMessage] = useState("");
  const [blastTemplate, setBlastTemplate] = useState("");
  // v103b: WA Blast searches the whole database, not the 50 loaded customers
  const [blastResults, setBlastResults] = useState([]);
  const blastNameMap = useRef({});
  useEffect(() => {
    if (!showBlast || !appConfig.companyId) return;
    var cancelled = false;
    var t = setTimeout(async () => {
      var bq = blastSearch.trim().replace(/\s+/g, " ");
      var { data, error } = await fetchAllRows(() => {
        var q = supabase.from("customers").select("id,full_name,phone,area,job_status,customer_type").eq("company_id", appConfig.companyId).not("phone", "is", null).neq("phone", "");
        if (bq) q = q.ilike("full_name", "%" + bq + "%");
        if (blastStatus !== "all") q = q.or("job_status.eq." + blastStatus + ",customer_type.eq." + blastStatus);
        if (blastArea !== "all") q = q.eq("area", blastArea);
        return q.order("full_name", { ascending: true }).order("id", { ascending: true });
      });
      if (cancelled || error || !data) return;
      data.forEach((r) => { blastNameMap.current[r.phone] = r.full_name; });
      setBlastResults(data);
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [showBlast, blastSearch, blastStatus, blastArea]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [quoteCustomer, setQuoteCustomer] = useState(null);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [referralSearch, setReferralSearch] = useState("");
  const [referralResults, setReferralResults] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);
  const reminderSoundPlayed = useRef(false);
  const phoneInputRef = useRef(null); // eslint-disable-line no-unused-vars
  const searchInputRef = useRef(null);
  const [newCustomer, setNewCustomer] = useState({ full_name: "", phone: "", email: "", address: "", area: "", customer_type: "lead", product_interest: "", notes: "" });

  // Phase 4: Accounting
  const [acctFilter, setAcctFilter] = useState("all");
  const [acctJobs, setAcctJobs] = useState([]);
  const [acctLoading, setAcctLoading] = useState(false);
  const [acctDash, setAcctDash] = useState({ collected: 0, outstanding: 0, paidCount: 0, unpaidCount: 0 });
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentEdit, setPaymentEdit] = useState({ payment_status: "unpaid", payment_method: "", deposit_amount: "", payment_date: "" });

  // Phase 5: Route
  const [routeJobs, setRouteJobs] = useState([]);
  const [routeLoading, setRouteLoading] = useState(false);

  // Phase 6: Quote history
  const [showQuoteHistory, setShowQuoteHistory] = useState(false);
  const [showMeasurementHistory, setShowMeasurementHistory] = useState(false);
  const [measurementHistory, setMeasurementHistory] = useState([]);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [quoteHistory, setQuoteHistory] = useState([]);
  // PATCH oct21-ui-customer: quote and measurement counts on the customer screen, unsaved details flag
  const [custCounts, setCustCounts] = useState({ cid: null, quotes: null, measurements: null });
  const [detailsDirty, setDetailsDirty] = useState(false);
  useEffect(() => {
    var cid = selectedCustomer && selectedCustomer.id;
    if (!cid || showQuote || showMeasurements) return;
    fetchCustCounts(cid);
  }, [selectedCustomer && selectedCustomer.id, showQuote, showMeasurements]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!editMode) setDetailsDirty(false); }, [editMode]);

  useEffect(() => {
    localStorage.setItem("darkMode", darkMode);
    try { document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light"); } catch (_) {} // PATCH oct21-ui-base
  }, [darkMode]);

  // ── SINGLE DEVICE LOGIN — check every 1 min + on app resume ──
  useEffect(() => {
    if (!session) return;
    async function checkSession() {
      var localSessionId = localStorage.getItem("blindspro_session_id");
      if (!localSessionId) return; // no session id stored — skip check
      var companyId = appConfig.companyId;
      if (!companyId) return;
      // FIXED: read the same per-user key that Login.js now writes, instead
      // of a shared per-company one — otherwise a teammate logging in would
      // still incorrectly sign everyone else at the company out.
      var { data } = await supabase
        .from("user_settings")
        .select("value")
        .eq("key", "active_session_" + session.user.id)
        .eq("company_id", companyId)
        .maybeSingle();
      if (data && data.value && data.value !== localSessionId) {
        // Another device logged in — sign out this one
        await supabase.auth.signOut();
        clearLocalKeepPrefs();
        // Tell the Login screen why (set AFTER clear so it survives)
        localStorage.setItem("blindspro_signout_reason", "other_device");
        window.location.reload();
      }
    }
    checkSession(); // check on load
    var iv = setInterval(checkSession, 60 * 1000); // check every 1 min
    // Also check the moment the app/tab comes back to the foreground
    // (Android pauses timers in the background, so resume must trigger it).
    function onVisible() { if (document.visibilityState === "visible") checkSession(); }
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
  }, [sessionUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SESSION TIMEOUT — auto sign out after 8 hours idle ──
  useEffect(() => {
    if (!session) return;
    const TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        // FIXED: this used to only clear 2 of the ~5 keys the app stores,
        // leaving blindspro_role and blindspro_session_id behind on a shared
        // device. That stale role could then incorrectly hide/show features
        // for whoever logs in next. Now does a full clear, same as the
        // manual Sign Out button already correctly does.
        await supabase.auth.signOut();
        clearLocalKeepPrefs();
        window.location.reload();
      }, TIMEOUT_MS);
    };
    const events = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "click"];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset(); // start timer on login
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [sessionUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  var C = { teal: "#2F6F6B", green: "#2E7D5E", blue: "#1E5FA8", orange: "#C97A1A", red: "#B33A3A", purple: "#6A3FA0", amber: "#D4860F" };
  var theme = { // PATCH oct21-ui-base
    bg: darkMode ? "#121615" : "#E2E7E6", // PATCH oct21-ui-bg
    card: darkMode ? "#1A201F" : "#FFFFFF",
    cardBorder: darkMode ? "#2C3433" : "#E2E7E6",
    text: darkMode ? "#E8EEED" : "#1C2B2A",
    subText: darkMode ? "#B8C2C1" : "#3A4645",
    mutedText: darkMode ? "#8E9998" : "#5F6B6A",
    inputBg: darkMode ? "#1F2625" : "#FFFFFF",
    inputBorder: darkMode ? "#3A4443" : "#CBD3D2",
    sectionBg: darkMode ? "#161B1A" : "#F7F9F8",
  };
  var SC = { lead: "#2563A8", consulted: "#C2700A", quoted: "#6D4AA8", ordered: "#0E7490", installing: "#92400E", done: "#2F7D4F" }; // PATCH oct21-ui-home
  var SL = ["lead", "consulted", "quoted", "ordered", "installing", "done"];
  var FU = {
    Called: { color: "#1E5FA8", icon: "📞", statusUpdate: null },
    Texted: { color: "#2E7D5E", icon: "💬", statusUpdate: null },
    "Sent Quote": { color: "#6A3FA0", icon: "📄", statusUpdate: "quoted" },
    "Installation Done": { color: "#2E7D5E", icon: "✅", statusUpdate: "done" },
    "No Answer": { color: "#666666", icon: "📵", statusUpdate: null },
    "Left Voicemail": { color: "#A06A1A", icon: "📨", statusUpdate: null },
  };
  var PS_COLOR = { unpaid: "#B33A3A", deposit: "#D4860F", paid: "#2E7D5E" };
  var PS_LABEL = { unpaid: "Unpaid", deposit: "Deposit", paid: "Paid ✓" };
  var IS = {
    width: "100%",
    padding: 10,
    marginBottom: 8,
    borderRadius: 6,
    border: "1px solid " + theme.inputBorder,
    fontSize: 14,
    boxSizing: "border-box",
    background: theme.inputBg,
    color: theme.text,
  };
  // PATCH oct21-ui-more: shared pieces of the new style
  var UI = (function () {
    var btnP = { minHeight: 44, padding: "0 16px", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: C.teal, color: "white", border: "1px solid " + C.teal, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none" };
    return {
      tealT: darkMode ? "#7CC2BB" : C.teal,
      tint: darkMode ? "#1C2E2C" : "#E6F0EF",
      wa: darkMode ? "#4CC38A" : "#1E7A45",
      waTint: darkMode ? "#16301F" : "#E5F4EA",
      green: darkMode ? "#5DB683" : "#2F7D4F",
      danger: darkMode ? "#F0776B" : "#B42318",
      btnP: btnP,
      btnS: Object.assign({}, btnP, { background: theme.card, color: theme.text, border: "1px solid " + theme.inputBorder }),
      lbl: { display: "block", fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 6 },
      inp: Object.assign({}, IS, { minHeight: 44, borderRadius: 10, fontSize: 15, marginBottom: 12, padding: "10px 12px", fontFamily: "inherit" }),
      card: { background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 14, padding: 16, marginBottom: 12 },
      hint: { fontSize: 13, color: theme.mutedText },
      title: { margin: 0, fontSize: 15, fontWeight: 600, color: theme.text },
      iconBtn: function (color, bg) { /* PATCH oct21-ui-bold: tinted or solid buttons */
        var b = { width: 44, height: 44, borderRadius: 10, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, textDecoration: "none", boxSizing: "border-box", padding: 0 };
        var dg = darkMode ? "#F0776B" : "#B42318", gr = darkMode ? "#5DB683" : "#2F7D4F", wa = darkMode ? "#4CC38A" : "#1E7A45";
        if (color === theme.text) return Object.assign(b, { border: "1px solid " + theme.cardBorder, background: theme.card, color: theme.text });
        if (color === dg) return Object.assign(b, { background: darkMode ? "#3A1F1D" : "#FBEAE8", color: dg });
        if (color === gr) return Object.assign(b, { background: "#2F7D4F", color: "white" });
        if (color === wa || bg) return Object.assign(b, { background: "#1E7A45", color: "white" });
        return Object.assign(b, { background: darkMode ? "#1C2E2C" : "#E6F0EF", color: darkMode ? "#7CC2BB" : C.teal });
      },
      round: function (bg) {
        return { width: 44, height: 44, borderRadius: 99, background: bg, color: "white", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, textDecoration: "none", padding: 0 };
      },
    };
  })();
  function boxHead(title, onClose) {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={UI.title}>{title}</h3>
        <button aria-label="Close" onClick={onClose} style={{ width: 44, height: 44, background: "transparent", border: "none", color: theme.subText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="x" size={20} />
        </button>
      </div>
    );
  }

  function fp(phone) {
    if (!phone) return "";
    var d = phone.replace(/\D/g, "");
    if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
    if (d.length === 10) return "+1 (" + d.slice(0, 3) + ") " + d.slice(3, 6) + "-" + d.slice(6);
    return phone;
  }
  function showToast(msg, type) {
    setToast({ message: msg, type: type || "success" });
    // PATCH audit-3to9 (#7): a new toast resets the timer so it gets its full 3 seconds
    clearTimeout(window.__bpToastTimer);
    window.__bpToastTimer = setTimeout(() => setToast(null), 3000);
  }

  // Detects a revoked/expired auth session (e.g. user signed in on another
  // device) and forces a clean logout instead of surfacing a confusing
  // generic error. Returns true if it handled the error as a logout.
  async function handleAuthError(error, fallbackMsg) {
    var msg = ((error && error.message) || "").toLowerCase();
    var isAuthError =
      (error && error.status === 401) ||
      msg.indexOf("jwt") !== -1 ||
      msg.indexOf("invalid token") !== -1 ||
      msg.indexOf("not authenticated") !== -1 ||
      msg.indexOf("refresh_token_not_found") !== -1;
    if (isAuthError) {
      // v98: native background sync and the app share one refresh-token
      // chain. A token error can just mean native rotated it first. Reload
      // the session (hybridStorage adopts native's newer token) and retry
      // the refresh once before signing anyone out.
      try {
        var _g = await supabase.auth.getSession();
        if (_g && _g.data && _g.data.session) {
          var _r = await supabase.auth.refreshSession();
          if (!_r.error && _r.data && _r.data.session) {
            showToast("Connection refreshed, please try again", "error");
            return true;
          }
        }
      } catch (_) {}
      showToast("You've been signed out (logged in on another device)", "error");
      await supabase.auth.signOut();
      clearLocalKeepPrefs();
      setTimeout(function () { window.location.reload(); }, 1500);
      return true;
    }
    showToast(fallbackMsg, "error");
    return false;
  }

  useEffect(() => {
    if (session) {
      LocalNotifications.requestPermissions().then(() => maybeAskExactAlarm()).catch(() => {});
      LocalNotifications.createChannel({
        id: "reminders_v2",
        name: "Reminders",
        importance: 5,
        visibility: 1,
        sound: "default",
        vibration: true,
      }).catch(() => {});
      // Only fetch data if company_id is already set in localStorage
      // If not set, loadBusinessName() will fetch it and trigger these after
      if (localStorage.getItem("blindspro_company_id")) {
        fetchCustomers();
        fetchAreas();
        checkReminders();
        fetchStatusCounts();
        fetchUpcomingJobs();
        fetchUpcomingRemindersAndSync();
        fetchWaTemplates();
        checkWhatsAppReminders();
      }
      setupTeam();
      afterCompanyResolved();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUserId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session) return;
    var iv = setInterval(checkReminders, 30 * 1000);
    return () => clearInterval(iv);
  }, [sessionUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    var handler = CapApp.addListener("backButton", () => {
      if (selectedCustomer) {
        setSelectedCustomer(null);
        setEditMode(false);
        setCustomerJobs([]);
        setShowFollowUpHistory(false);
        setShowQuote(false);
        setQuoteCustomer(null);
        // Removed setShowMeasurements(false) - Measurements has its own Back/Cancel button
        setShowQuoteHistory(false);
      } else if (currentScreen !== "list") {
        setCurrentScreen("list");
      }
    });
    return () => { handler.then(h => h.remove()); };
  }, [selectedCustomer, currentScreen]);
  useEffect(() => {
    if ("setAppBadge" in navigator) {
      dueReminders.length > 0 ? navigator.setAppBadge(dueReminders.length).catch(() => {}) : navigator.clearAppBadge().catch(() => {});
    }
  }, [dueReminders]);
  useEffect(() => {
    if (selectedCustomer) window.history.pushState({ page: "profile" }, "");
    var h = () => {
      setSelectedCustomer(null);
      setEditMode(false);
      setCustomerJobs([]);
      setShowFollowUpHistory(false);
      setShowQuote(false);
      setQuoteCustomer(null);
      // Removed setShowMeasurements(false) here - Measurements has its own Back/Cancel
      // buttons and this popstate handler was closing the screen unexpectedly during
      // async operations like Save (likely a spurious popstate event from the WebView).
      setShowQuoteHistory(false);
    };
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, [selectedCustomer]);
  useEffect(() => {
    if (dueReminders.length > 0 && !reminderSoundPlayed.current) {
      reminderSoundPlayed.current = true;
      try {
        var ctx = new (window.AudioContext || window.webkitAudioContext)();
        [0, 350, 700].forEach((delay) => {
          var o = ctx.createOscillator(),
            g = ctx.createGain();
          o.connect(g);
          g.connect(ctx.destination);
          o.frequency.value = 880;
          o.type = "sine";
          g.gain.setValueAtTime(0.3, ctx.currentTime + delay / 1000);
          g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay / 1000 + 0.45);
          o.start(ctx.currentTime + delay / 1000);
          o.stop(ctx.currentTime + delay / 1000 + 0.45);
        });
      } catch (e) {}
    }
    if (dueReminders.length === 0) reminderSoundPlayed.current = false;
  }, [dueReminders]);
  useEffect(() => {
    if (!session) return;
    // FIXED: this channel had no company_id filter, so every connected
    // client — from every tenant company — received every INSERT on the
    // shared "messages" table in realtime (cross-tenant chat leak). Wait
    // for company_id to be resolved (businessName loads at the same time)
    // and scope the subscription to it.
    var companyId = appConfig.companyId;
    if (!companyId) return;
    var sub = supabase
      .channel("messages")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages", filter: "company_id=eq." + companyId }, (p) => {
        if (currentScreenRef.current === "chat") {
          setChatMessages((prev) => [...prev, p.new]);
          setTimeout(() => {
            if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
          }, 100);
        } else {
          if (p.new.sender_email !== session?.user?.email) setUnreadCount((prev) => prev + 1);
        }
      })
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [sessionUserId, businessName]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (currentScreen === "chat") {
      setUnreadCount(0);
      fetchMessages();
    }
    if (currentScreen === "accounting") fetchAccountingData(acctFilter); // eslint-disable-line react-hooks/exhaustive-deps
    if (currentScreen === "route") fetchRouteJobs();
  }, [currentScreen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    var raw = new URLSearchParams(window.location.search).get("phone");
    if (raw && session && appConfig.companyId) {
      var n = normalizePhone(raw);
      setSearch(n); // PATCH oct21-ui-lists: one search box
      setShowCustomerList(true);
      fetchCustomers(n, "all", "all");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [sessionUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkReminders() {
    var { data } = await supabase.from("reminders").select("*,customers(full_name)").eq("company_id", appConfig.companyId).eq("done", false).lte("remind_at", new Date().toISOString());
    setDueReminders(data && data.length > 0 ? data : []);
  }
  async function checkWhatsAppReminders() {
    var now = new Date(),
      tom = new Date(now);
    tom.setDate(now.getDate() + 1);
    var s = new Date(tom.getFullYear(), tom.getMonth(), tom.getDate(), 0, 0, 0).toISOString();
    var e = new Date(tom.getFullYear(), tom.getMonth(), tom.getDate(), 23, 59, 59).toISOString();
    var { data } = await supabase.from("jobs").select("*,customers(full_name,phone)").eq("company_id", appConfig.companyId).eq("completed", false).gte("scheduled_at", s).lte("scheduled_at", e).order("scheduled_at", { ascending: true });
    if (data && data.length > 0) {
      setWaReminders(data);
      setShowWaReminders(true);
    }
  }
  async function fetchNotContacted(days) {
    setNotContactedLoading(true);
    var c = new Date();
    c.setDate(c.getDate() - (days || notContactedDays));
    var { data } = await supabase
      .from("customers")
      .select("*")
      .eq("company_id", appConfig.companyId)
      .or("last_contacted.lt." + c.toISOString() + ",last_contacted.is.null")
      .neq("job_status", "done")
      .order("last_contacted", { ascending: true, nullsFirst: true })
      .limit(100);
    setNotContactedList(data || []);
    setNotContactedLoading(false);
  }
  async function dismissReminder(id) {
    var { error: _dErr } = await supabase.from("reminders").update({ done: true }).eq("id", id); // PATCH save-errors
    if (_dErr) { showToast("Error updating reminder", "error"); return; }
    cancelReminderNotification(id);
    setDueReminders(prev => prev.filter((r) => r.id !== id));
    setPersonalReminders(prev => prev.filter((r) => r.id !== id));
    fetchUpcomingReminders();
  }
  async function addPersonalReminder() {
    if (!personalReminderDate || !personalReminderTime || !personalReminderMsg.trim()) {
      showToast("Please fill in all fields", "error");
      return;
    }
    var { data: newPersonalReminder, error } = await supabase.from("reminders").insert([{ contact_id: null, remind_at: new Date(personalReminderDate + "T" + personalReminderTime).toISOString(), message: personalReminderMsg, done: false, company_id: appConfig.companyId }]).select();
    if (error) {
      showToast("Error saving reminder", "error");
      return;
    }
    showToast("Personal reminder set!");
    if (newPersonalReminder && newPersonalReminder[0]) scheduleReminderNotification(newPersonalReminder[0].id, newPersonalReminder[0].remind_at, "📌 Personal Reminder", newPersonalReminder[0].message || "Reminder");
    setShowPersonalReminderForm(false);
    setPersonalReminderDate("");
    setPersonalReminderTime("");
    setPersonalReminderMsg("");
    fetchUpcomingReminders();
  }
  async function fetchAreas() {
    var { data } = await fetchAllRows(() => supabase.from("customers").select("area").eq("company_id", appConfig.companyId).order("id", { ascending: true })); // v103b
    if (data) setAllAreas(["all", ...Array.from(new Set(data.map((c) => c.area).filter(Boolean))).sort()]);
  }
  async function fetchStatusCounts() {
    var { data } = await fetchAllRows(() => supabase.from("customers").select("job_status,customer_type").eq("company_id", appConfig.companyId).order("id", { ascending: true })); // v103b
    if (data) {
      var c = { lead: 0, consulted: 0, quoted: 0, ordered: 0, installing: 0, done: 0, existing: 0 };
      data.forEach((x) => {
        var js = normStatus(x.job_status); if (js && c[js] !== undefined) c[js]++; // PATCH oct21-ui-dash
        if (x.customer_type === "existing") c.existing++;
      });
      setStatusCounts(c);
    }
  }
  async function fetchUpcomingJobs() {
    var todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    var { data } = await supabase.from("jobs").select("*,customers(full_name)").eq("company_id", appConfig.companyId).eq("completed", false).gte("scheduled_at", todayStart.toISOString()).order("scheduled_at", { ascending: true }).limit(20);
    if (data) setUpcomingJobs(data);
  }
  async function fetchUpcomingReminders() {
    var { data } = await supabase.from("reminders").select("*,customers(full_name)").eq("company_id", appConfig.companyId).eq("done", false).order("remind_at", { ascending: true }).limit(50);
    if (data) {
      setUpcomingReminders(data);
      setPersonalReminders(data.filter(r => !r.contact_id));
    }
  }
  // Full reschedule pass -- only for app startup, so native alarms are
  // guaranteed to match the database after a restart/reinstall/gap.
  // Mutation-driven call sites (add/edit/dismiss/done) already schedule
  // or cancel their own specific reminder's notification directly, so
  // they use the lighter fetchUpcomingReminders() above instead.
  async function fetchUpcomingRemindersAndSync() {
    var { data } = await supabase.from("reminders").select("*,customers(full_name)").eq("company_id", appConfig.companyId).eq("done", false).order("remind_at", { ascending: true }).limit(50);
    if (data) {
      setUpcomingReminders(data);
      setPersonalReminders(data.filter(r => !r.contact_id));
      data.forEach(function (r) {
        var title = r.contact_id && r.customers && r.customers.full_name ? ("🔔 " + r.customers.full_name) : "📌 Personal Reminder";
        scheduleReminderNotification(r.id, r.remind_at, title, r.message || "Reminder");
      });
    }
  }
  function renderOffsetPicker(selectedOffsets, setSelectedOffsets, customValue, setCustomValue, customUnit, setCustomUnit) {
    // PATCH oct21-ui-more: teal chips
    function toggleOffset(opt) {
      var exists = selectedOffsets.some((o) => o.hours === opt.hours);
      setSelectedOffsets(exists ? selectedOffsets.filter((o) => o.hours !== opt.hours) : [...selectedOffsets, opt]);
    }
    function addCustomOffset() {
      var num = parseFloat(customValue);
      if (!num || num <= 0) { showToast("Enter a valid number", "error"); return; }
      var hours = customUnit === "days" ? num * 24 : num;
      var unitWord = customUnit === "days" ? (num === 1 ? "day" : "days") : (num === 1 ? "hour" : "hours");
      var label = num + " " + unitWord + " before";
      if (selectedOffsets.some((o) => o.hours === hours)) { showToast("Already added", "error"); return; }
      setSelectedOffsets([...selectedOffsets, { hours: hours, label: label }]);
      setCustomValue("");
    }
    var customSelected = selectedOffsets.filter((o) => !REMINDER_OFFSET_OPTIONS.some((p) => p.hours === o.hours));
    var chip = (on) => ({ minHeight: 40, padding: "0 12px", borderRadius: 99, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: on ? C.teal : theme.card, color: on ? "white" : theme.text, border: "1px solid " + (on ? C.teal : theme.inputBorder), display: "inline-flex", alignItems: "center", gap: 6 });
    return (
      <div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          {REMINDER_OFFSET_OPTIONS.map((opt) => {
            var isSelected = selectedOffsets.some((o) => o.hours === opt.hours);
            return (
              <button key={opt.hours} aria-pressed={isSelected} onClick={() => toggleOffset(opt)} style={chip(isSelected)}>
                {isSelected && <Icon name="check" size={14} />}{opt.label}
              </button>
            );
          })}
          {customSelected.map((custom) => (
            <button key={custom.hours} aria-label={"Remove " + custom.label} onClick={() => setSelectedOffsets(selectedOffsets.filter((o) => o.hours !== custom.hours))} style={chip(true)}>
              {custom.label} <Icon name="x" size={14} />
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input type="number" aria-label="Custom amount" placeholder="Custom" value={customValue} onChange={(e) => setCustomValue(e.target.value)} style={{ ...UI.inp, width: 90, marginBottom: 0 }} />
          <select aria-label="Hours or days" value={customUnit} onChange={(e) => setCustomUnit(e.target.value)} style={{ ...UI.inp, flex: 1, width: "auto", minWidth: 0, marginBottom: 0 }}>
            <option value="hours">hours before</option>
            <option value="days">days before</option>
          </select>
          <button onClick={addCustomOffset} style={UI.btnS}>+ Add</button>
        </div>
      </div>
    );
  }
  async function createOffsetReminders(contactId, contactName, anchorAtISO, offsets) {
    var anchorMs = new Date(anchorAtISO).getTime();
    var _orFailed = 0; // PATCH oct16-fixes (#2)
    for (var oi = 0; oi < offsets.length; oi++) {
      var offsetObj = offsets[oi];
      var remindAtISO = new Date(anchorMs - offsetObj.hours * 60 * 60 * 1000).toISOString();
      var { data: newOffsetReminder, error: _orErr } = await supabase.from("reminders").insert([{ contact_id: contactId, remind_at: remindAtISO, message: "Job " + offsetObj.label, done: false, company_id: appConfig.companyId }]).select();
      if (_orErr) { _orFailed++; continue; }
      if (newOffsetReminder && newOffsetReminder[0]) scheduleReminderNotification(newOffsetReminder[0].id, remindAtISO, "🔔 " + (contactName || "Reminder"), newOffsetReminder[0].message);
    }
    if (_orFailed > 0) window.alert(_orFailed + " reminder" + (_orFailed === 1 ? "" : "s") + " could not be saved. Please add " + (_orFailed === 1 ? "it" : "them") + " again from Upcoming.");
    fetchUpcomingReminders();
  }
  function renderReminderEditForm(r, opts) {
    // PATCH oct21-ui-more: labels above fields, teal Save, trash icon
    var setE = (k, v) => setEditingHomeReminder({ ...editingHomeReminder, [k]: v });
    var dt = (
      <div key="dt" style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}><div style={UI.lbl}>Date</div><input type="date" value={editingHomeReminder.date} onChange={(e) => setE("date", e.target.value)} style={UI.inp} /></div>
        <div style={{ flex: 1 }}><div style={UI.lbl}>Time</div><input type="time" value={editingHomeReminder.time} onChange={(e) => setE("time", e.target.value)} style={UI.inp} /></div>
      </div>
    );
    var msg = (
      <div key="message">
        <div style={UI.lbl}>{opts.messageFirst ? "What to remember" : "Message"}</div>
        <input placeholder={opts.messagePlaceholder} value={editingHomeReminder.message} onChange={(e) => setE("message", e.target.value)} style={UI.inp} />
      </div>
    );
    var fields = opts.messageFirst ? [msg, dt] : [dt, msg];
    return (
      <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 10 }}>{opts.title}</div>
        {fields}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={async () => {
            var newRemindAt = new Date(editingHomeReminder.date + "T" + editingHomeReminder.time).toISOString();
            var { error } = await supabase.from("reminders").update({ remind_at: newRemindAt, message: editingHomeReminder.message }).eq("id", r.id);
            if (!error) { showToast("Reminder updated!"); scheduleReminderNotification(r.id, newRemindAt, r.contact_id ? ("🔔 " + (r.customers?.full_name || "Reminder")) : "📌 Personal Reminder", editingHomeReminder.message); setEditingHomeReminder(null); fetchUpcomingReminders(); }
            else showToast("Error updating reminder", "error");
          }} style={{ ...UI.btnP, flex: 1 }}>Save</button>
          <button onClick={() => setEditingHomeReminder(null)} style={{ ...UI.btnS, flex: 1 }}>Cancel</button>
          <button aria-label="Delete reminder" title="Delete reminder" onClick={async () => {
            if (!window.confirm("Delete this reminder?")) return; var { error: _rmErr } = await supabase.from("reminders").delete().eq("id", r.id); if (_rmErr) { showToast("Error deleting reminder", "error"); return; } // PATCH oct16-fixes (#1, #2)
            cancelReminderNotification(r.id);
            if (opts.isPersonal) setPersonalReminders(prev => prev.filter(x => x.id !== r.id));
            setEditingHomeReminder(null); fetchUpcomingReminders(); showToast("Reminder deleted");
          }} style={UI.iconBtn(UI.danger)}><Icon name="trash" size={18} w={2.5} /></button>
        </div>
      </div>
    );
  }
  async function fetchFollowUpHistory(cid) {
    var { data } = await supabase.from("follow_up_logs").select("*").eq("contact_id", cid).order("created_at", { ascending: false }).limit(20);
    if (data) setFollowUpHistory(data);
  }
  async function fetchWaTemplates() {
    var { data } = await supabase.from("whatsapp_templates").select("*").eq("company_id", appConfig.companyId).order("created_at", { ascending: true });
    if (data) setWaTemplates(data);
  }
  async function addWaTemplate() {
    if (!newTemplateLabel.trim() || !newTemplateText.trim()) {
      showToast("Please fill in label and message", "error");
      return;
    }
    // Session 31: plan template limit
    var _tplRoom = await templateRoomLeft(companyPlan);
    if (_tplRoom <= 0) { window.alert(templateLimitMsg(companyPlan)); return; }
    var { error: _tplErr } = await supabase.from("whatsapp_templates").insert([{ label: newTemplateLabel, text: newTemplateText, company_id: appConfig.companyId }]);
    if (_tplErr) { showToast("Error saving template", "error"); return; }
    setNewTemplateLabel("");
    setNewTemplateText("");
    setShowTplForm(false); // PATCH oct21-ui-more
    fetchWaTemplates();
    showToast("Template saved!");
  }
  async function updateWaTemplate(id, label, text) {
    var { error: _tuErr } = await supabase.from("whatsapp_templates").update({ label, text }).eq("id", id);
    if (_tuErr) { showToast("Error updating template", "error"); return; }
    setEditingTemplate(null);
    fetchWaTemplates();
    showToast("Template updated!");
  }
  async function deleteWaTemplate(id) {
    if (!window.confirm("Delete this template?")) return; // PATCH oct16-fixes (#1)
    var { error: _tdErr } = await supabase.from("whatsapp_templates").delete().eq("id", id);
    if (_tdErr) { showToast("Error deleting template", "error"); return; }
    fetchWaTemplates();
    showToast("Template deleted");
  }
  async function fetchCustomers(searchTerm, statusFilter, areaFilter, newestFirst, sortOverride) {
    // v103: trim + collapse spaces so "Ahmed " still finds "Ahmed"
    searchTerm = String(searchTerm ?? "").trim().replace(/\s+/g, " ");
    statusFilter = statusFilter ?? "all";
    areaFilter = areaFilter ?? "all";
    // v98 #5: never query without a company_id (returns a false 0)
    var issuedCompany = appConfig.companyId;
    if (!issuedCompany) return;
    var mySeq = ++customersFetchSeq.current;
    var unfiltered = !searchTerm && statusFilter === "all" && areaFilter === "all";
    setLoading(true);
    var q = supabase
      .from("customers")
      .select("*", { count: "exact" })
      .eq("company_id", appConfig.companyId)
      .limit(50);
    // v103: A-Z / Newest buttons pass the new sort directly (state updates late)
    var _sort = sortOverride !== undefined ? sortOverride : sortMode;
    if (_sort === "name") {
      q = q.order("full_name", { ascending: true });
    } else if (_sort === "newest") {
      q = q.order("created_at", { ascending: false });
    } else {
      q = q
        .order(newestFirst ? "created_at" : "last_contacted", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });
    }
    if (searchTerm) {
      var _t = orLike(searchTerm);
      var _orParts = ["full_name.ilike." + _t, "phone.ilike." + _t, "area.ilike." + _t, "product_interest.ilike." + _t];
      // Phone numbers are stored with dashes/brackets; phone_digits is the
      // generated digits-only column, so "6045551234" finds "604-555-1234".
      var _digits = String(searchTerm).replace(/\D/g, "");
      if (_digits.length === 11 && _digits.charAt(0) === "1") _digits = _digits.slice(1); // PATCH oct21-ui-lists
      if (_digits.length >= 3) _orParts.push("phone_digits.ilike." + orLike(_digits));
      if (_digits.length >= 7 && _digits.length < 11 && _digits.charAt(0) === "1") _orParts.push("phone_digits.ilike." + orLike(_digits.slice(1))); // PATCH oct21-ui-fix2
      q = q.or(_orParts.join(","));
    }
    if (statusFilter !== "all") q = q.or("job_status.eq." + statusFilter + ",customer_type.eq." + statusFilter);
    if (areaFilter !== "all") q = q.eq("area", areaFilter);
    var { data, error, count } = await q;
    // Company switched while this was in flight: discard.
    if (issuedCompany !== appConfig.companyId) {
      if (mySeq === customersFetchSeq.current) setLoading(false);
      return;
    }
    if (!error && count === 0) {
      // A 0 with no error can also mean the request went out without a
      // signed-in session (RLS returns no rows). Only trust it if signed in.
      var { data: _sd } = await supabase.auth.getSession();
      if (!_sd || !_sd.session) {
        if (mySeq === customersFetchSeq.current) setLoading(false);
        return;
      }
    }
    // Top badge = true company total: only from an unfiltered result
    // (previously a status/area filter with no search text overwrote it).
    if (!error && unfiltered) setCompanyCustomerCount(count || 0);
    // A newer fetch was issued after this one: its result wins.
    if (mySeq !== customersFetchSeq.current) return;
    if (!error) {
      setCustomers(data || []);
      setTotalCount(count || 0);
    }
    setLoading(false);
  }
  async function fetchJobs(cid) {
    var { data, error } = await supabase.from("jobs").select("*").eq("contact_id", cid).order("scheduled_at", { ascending: true });
    if (!error) setCustomerJobs(data);
  }
  // v99: after customer changes, ask the native SyncAdapter to refresh phone
  // contacts. Debounced 5s so bulk imports trigger one sync, not hundreds.
  function requestContactSync() {
    if (!window.AndroidContacts || typeof window.AndroidContacts.requestSync !== "function") return;
    clearTimeout(window.__contactSyncReqTimer);
    window.__contactSyncReqTimer = setTimeout(() => {
      try { window.AndroidContacts.requestSync(); } catch (_) {}
    }, 5000);
  }
  async function addCustomer() {
    if (!String(newCustomer.full_name || "").trim()) { showToast("Please enter the customer's name", "error"); return; } // PATCH audit-3to9 (#4)
    // Session 31: plan customer limit
    var _room = await customerRoomLeft(companyPlan);
    if (_room <= 0) { window.alert(customerLimitMsg(companyPlan)); return; }
    if (await findPhoneOwner(newCustomer.phone)) { showToast(DUP_PHONE_MSG, "error"); return; }
    var { error } = await supabase.from("customers").insert([{ ...newCustomer, area: cleanCity(newCustomer.area), job_status: "lead", company_id: appConfig.companyId, assigned_to: session?.user?.id || null }]) /* PATCH nov01-status: hand added = Lead, imports = no status */;
    if (error) {
      if (isDupPhone(error)) { showToast(DUP_PHONE_MSG, "error"); return; }
      var wasLoggedOut = await handleAuthError(error, "Error saving customer");
      if (wasLoggedOut) return;
      return;
    }
    setShowAddForm(false);
    setNewCustomer({ full_name: "", phone: "", email: "", address: "", area: "", customer_type: "lead", product_interest: "", notes: "" });
    setSearch("");
    setPhoneSearch("");
    setFilterStatus("all");
    setFilterArea("all");
    await fetchCustomers("", "all", "all", true);
    fetchStatusCounts();
    requestContactSync();
    showToast("Customer saved!");
  }
  async function updateCustomer(id, updates) {
    if ("full_name" in updates && !String(updates.full_name || "").trim()) { showToast("Please enter the customer's name", "error"); return; }
    if ("phone" in updates && (await findPhoneOwner(updates.phone, id))) { showToast(DUP_PHONE_MSG, "error"); return; }
    if ("area" in updates) updates = { ...updates, area: cleanCity(updates.area) };
    var { error } = await supabase.from("customers").update(updates).eq("id", id);
    if (error) {
      if (isDupPhone(error)) { showToast(DUP_PHONE_MSG, "error"); return; }
      var wasLoggedOut = await handleAuthError(error, "Error updating customer");
      if (wasLoggedOut) return;
      return;
    }
    fetchCustomers(search, filterStatus, filterArea);
    fetchStatusCounts();
    setSelectedCustomer({ ...selectedCustomer, ...updates });
    setEditMode(false);
    if ("full_name" in updates || "phone" in updates) requestContactSync();
    showToast("Customer updated!");
  }
  async function deleteCustomer(id) {
    // PATCH delete-customer: delete related records first, the customer last.
    var { data: openReminders } = await supabase.from("reminders").select("id").eq("contact_id", id).eq("done", false);
    var _related = ["jobs", "reminders", "follow_up_logs", "measurements", "quotes"];
    for (var _ri = 0; _ri < _related.length; _ri++) {
      var { error: _relErr } = await supabase.from(_related[_ri]).delete().eq("contact_id", id);
      if (_relErr) {
        setConfirmDelete(null);
        await handleAuthError(_relErr, "Error deleting customer. Please try again.");
        return;
      }
    }
    (openReminders || []).forEach((r) => cancelReminderNotification(r.id));
    var { error } = await supabase.from("customers").delete().eq("id", id);
    if (error) {
      setConfirmDelete(null);
      var wasLoggedOut = await handleAuthError(error, "Error deleting customer");
      if (wasLoggedOut) return;
      return;
    }
    requestContactSync();
    setConfirmDelete(null);
    setSelectedCustomer(null);
    fetchCustomers(search, filterStatus, filterArea);
    fetchStatusCounts();
    showToast("Customer deleted");
  }
  async function updateJob(jobId, updates) {
    var { error } = await supabase.from("jobs").update(updates).eq("id", jobId);
    if (error) {
      showToast("Error updating job", "error");
      return;
    }
    setEditingJob(null);
    fetchJobs(selectedCustomer.id);
    fetchUpcomingJobs();
    showToast("Job updated!");
  }
  async function deleteJob(jobId) {
    var { error: _djErr } = await supabase.from("jobs").delete().eq("id", jobId); // PATCH oct16-fixes (#2)
    if (_djErr) { showToast("Error deleting job", "error"); return; }
    setEditingJob(null);
    fetchJobs(selectedCustomer.id);
    fetchUpcomingJobs();
    showToast("Job deleted");
  }
  // Bug 2: notes - controlled input, save only on Add click
  async function addNote(id) {
    if (!newNote.trim()) return;
    var cu = customers.find((c) => c.id === id) || selectedCustomer;
    var ts = new Date().toLocaleString(appConfig.locale, { timeZone: appConfig.timezone });
    var n = "[" + ts + "]\n" + sanitize(newNote) + "\n\n" + (cu?.notes || "");
    var { error: _nErr } = await supabase.from("customers").update({ notes: n }).eq("id", id);
    if (_nErr) { showToast("Error saving note", "error"); return; }
    setSelectedCustomer((prev) => ({ ...prev, notes: n }));
    fetchCustomers(search, filterStatus, filterArea);
    setNewNote("");
    showToast("Note added!");
  }
  async function addQuickNote(cid) {
    var note = quickNote[cid];
    if (!note || !note.trim()) return;
    var cu = customers.find((c) => c.id === cid);
    var ts = new Date().toLocaleString(appConfig.locale, { timeZone: appConfig.timezone });
    var n = "[" + ts + "]\n" + sanitize(note) + "\n\n" + (cu?.notes || "");
    var { error: _qnErr } = await supabase.from("customers").update({ notes: n }).eq("id", cid);
    if (_qnErr) { showToast("Error saving note", "error"); return; }
    setQuickNote({ ...quickNote, [cid]: "" });
    fetchCustomers(search, filterStatus, filterArea);
    showToast("Note added!");
  }
  async function updateLastContacted(cid) {
    await supabase.from("customers").update({ last_contacted: new Date().toISOString() }).eq("id", cid);
  }
  async function exportToCSV() {
    var h = ["Name", "Phone", "Email", "Address", "Area", "Product Interest", "Customer Type", "Job Status", "Last Contacted", "Notes"];
    // v103b: export ALL customers, not just the 50 on screen
    showToast("Preparing export...");
    var { data: allCust, error: expErr } = await fetchAllRows(() => supabase.from("customers").select("*").eq("company_id", appConfig.companyId).order("full_name", { ascending: true }).order("id", { ascending: true }));
    if (expErr || !allCust) { showToast("Export failed, please try again", "error"); return; }
    var rows = allCust.map((c) =>
      [
        c.full_name || "",
        c.phone || "",
        c.email || "",
        c.address || "",
        c.area || "",
        c.product_interest || "",
        c.customer_type || "",
        c.job_status || "",
        c.last_contacted || "",
        unescNote(c.notes).replace(/\n/g, " "),
      ]
        // FIXED: values weren't escaped before wrapping in quotes, so any field
        // containing a literal " (an address, a note, an inch mark in a
        // measurement note, etc.) produced a malformed CSV row. Per the CSV
        // spec, escape embedded quotes by doubling them before wrapping.
        .map((v, _ci) => { var _s = String(v); if (_ci !== 1 && /^[=+\-@\t\r]/.test(_s)) _s = "'" + _s; return '"' + _s.replace(/"/g, '""') + '"'; }) // PATCH oct16-fixes (#4)
        .join(",")
    );
    var csvString = [h.join(","), ...rows].join("\n");
    var fileName = appConfig.csvPrefix + "-" + new Date().toISOString().slice(0, 10) + ".csv";

    if (Capacitor.isNativePlatform()) {
      // Android/iOS: blob-URL downloads fail silently in WebViews.
      // Write to Cache via Filesystem, then hand off to the native Share sheet.
      try {
        var writeResult = await Filesystem.writeFile({
          path: fileName,
          data: csvString,
          directory: Directory.Cache,
          encoding: Encoding.UTF8,
        });
        await Share.share({
          title: fileName,
          url: writeResult.uri,
          dialogTitle: "Save or Share CSV File",
        });
        showToast("File ready to save or share!");
      } catch (err) {
        showToast("Error: " + err.message, "error");
      }
    } else {
      // Web browser: standard blob download
      var a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csvString], { type: "text/csv" }));
      a.download = fileName;
      a.click();
      showToast("Exported!");
    }
  }
  const saveToPhoneInProgress = useRef(false);
  useEffect(() => {
    var h = () => {
      if (!document.hidden) saveToPhoneInProgress.current = false;
    };
    document.addEventListener("visibilitychange", h);
    return () => document.removeEventListener("visibilitychange", h);
  }, []);
  async function saveToPhone(cu) {
    if (saveToPhoneInProgress.current) return;
    saveToPhoneInProgress.current = true;
    try {
      if (window.AndroidContacts) {
        setTimeout(() => {
          saveToPhoneInProgress.current = false;
        }, 3000);
        window.AndroidContacts.saveContact(cu.full_name || "", cu.phone || "", cu.email || "", cu.address || "", cu.product_interest || "");
        showToast("Opening Add Contact...");
      } else {
        var vc =
          "BEGIN:VCARD\nVERSION:3.0\nFN:" +
          (cu.full_name || "") +
          "\nTEL:" +
          (cu.phone || "") +
          "\nEMAIL:" +
          (cu.email || "") +
          "\nADR:;;" +
          (cu.address || "") +
          ";;;;\nNOTE:" +
          (cu.product_interest || "") +
          "\nEND:VCARD";
        var a = document.createElement("a");
        a.href = "data:text/vcard;charset=utf-8," + encodeURIComponent(vc);
        a.download = (cu.full_name || "contact") + ".vcf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast("Contact saved to phone!");
        setTimeout(() => {
          saveToPhoneInProgress.current = false;
        }, 2000);
      }
    } catch (e) {
      showToast("Error saving contact", "error");
      saveToPhoneInProgress.current = false;
    }
  }
  async function uploadPhoto(cid, file) {
    // PATCH oct17-photo: images only, shrink before upload, 10 MB cap if it cannot be shrunk
    if (!file) return;
    var isImg = /^image\//.test(file.type || "") || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name || "");
    if (!isImg) { showToast("Please choose a photo", "error"); return; }
    showToast("Uploading photo...");
    var upload = file;
    var ext = String(file.name || "photo.jpg").split(".").pop().toLowerCase() || "jpg";
    var ctype = file.type || undefined;
    var small = await shrinkImage(file);
    if (small && small.size < file.size) { upload = small; ext = "jpg"; ctype = "image/jpeg"; }
    if (upload === file && file.size > 10 * 1024 * 1024) { showToast("Photo is too large (max 10 MB)", "error"); return; }
    var path = cid + "/" + Date.now() + "." + ext;
    var { error } = await supabase.storage.from("customer-photos").upload(path, upload, ctype ? { contentType: ctype } : undefined);
    if (error) {
      showToast("Error uploading photo", "error");
      return;
    }
    var { data } = supabase.storage.from("customer-photos").getPublicUrl(path);
    var ts = new Date().toLocaleString(appConfig.locale, { timeZone: appConfig.timezone });
    var n = "[" + ts + " - Photo]\n" + data.publicUrl + "\n\n" + (selectedCustomer.notes || "");
    var { error: _phErr } = await supabase.from("customers").update({ notes: n }).eq("id", cid);
    if (_phErr) { showToast("Error saving photo", "error"); return; }
    setSelectedCustomer({ ...selectedCustomer, notes: n });
    fetchCustomers(search, filterStatus, filterArea);
    showToast("Photo uploaded!");
  }
  async function deletePhoto(cid, url) {
    var notes = selectedCustomer.notes || "";
    var lines = notes.split("\n");
    var nl = [];
    var i = 0;
    while (i < lines.length) {
      if (lines[i].includes("- Photo]") && i + 1 < lines.length && lines[i + 1] === url) {
        i += 2;
        if (i < lines.length && lines[i] === "") i++;
      } else {
        nl.push(lines[i]);
        i++;
      }
    }
    var nn = nl.join("\n").trim();
    var { error: _rpErr } = await supabase.from("customers").update({ notes: nn }).eq("id", cid); // PATCH oct16-fixes (#2)
    if (_rpErr) { showToast("Error removing photo", "error"); return; }
    setSelectedCustomer({ ...selectedCustomer, notes: nn });
    fetchCustomers(search, filterStatus, filterArea);
    showToast("Photo removed");
  }
  // PATCH oct18-tax: money with the company currency
  function money(n, decimals) { return fmtMoney(n, companyTax.currency, decimals); }
  // PATCH oct18-notes: edit or delete one note. Reads the latest notes first so a note
  // someone else just added is never lost.
  async function freshNotes(cid) {
    var { data, error } = await supabase.from("customers").select("notes").eq("id", cid).single();
    if (error || !data) return null;
    return data.notes || "";
  }
  async function saveNoteEdit(cid, blk, text) {
    var t = sanitize(text);
    if (!t) { showToast("Note is empty. Use 🗑 to delete it.", "error"); return; }
    var cur = await freshNotes(cid);
    if (cur === null) { showToast("Error saving note", "error"); return; }
    var blocks = parseNotes(cur);
    var idx = findNote(blocks, blk);
    if (idx === -1) { showToast("This note was changed on another device. Please reopen the customer.", "error"); return; }
    var hd = blocks[idx].header;
    if (hd && hd.indexOf("(edited)") === -1) hd = hd.replace(/\]$/, " (edited)]");
    blocks[idx] = { header: hd, body: t };
    var n = joinNotes(blocks);
    var { error } = await supabase.from("customers").update({ notes: n }).eq("id", cid);
    if (error) { showToast("Error saving note", "error"); return; }
    setSelectedCustomer((prev) => (prev && prev.id === cid ? { ...prev, notes: n } : prev));
    setEditingNote(null);
    fetchCustomers(search, filterStatus, filterArea);
    showToast("Note updated!");
  }
  async function deleteNote(cid, blk) {
    if (!window.confirm("Delete this note?")) return;
    var cur = await freshNotes(cid);
    if (cur === null) { showToast("Error deleting note", "error"); return; }
    var blocks = parseNotes(cur);
    var idx = findNote(blocks, blk);
    if (idx === -1) { showToast("This note was changed on another device. Please reopen the customer.", "error"); return; }
    blocks.splice(idx, 1);
    var n = joinNotes(blocks);
    var { error } = await supabase.from("customers").update({ notes: n }).eq("id", cid);
    if (error) { showToast("Error deleting note", "error"); return; }
    setSelectedCustomer((prev) => (prev && prev.id === cid ? { ...prev, notes: n } : prev));
    setEditingNote(null);
    fetchCustomers(search, filterStatus, filterArea);
    showToast("Note deleted");
  }
  // PATCH oct20-minsqft: save minimum sq ft (owner only, checked again in the database)
  async function saveMinSqft() {
    if (minSqftEdit === null) return;
    var v = parseFloat(minSqftEdit);
    if (isNaN(v) || v < 0 || v > 50) { showToast("Please enter a number from 0 to 50", "error"); return; }
    setMinSqftSaving(true);
    var { error } = await supabase.rpc("set_company_min_sqft", { p_company_id: appConfig.companyId, p_min: v });
    setMinSqftSaving(false);
    if (error) {
      showToast(String(error.message || "").indexOf("not_owner") !== -1 ? "Only the account owner can change this" : "Error saving minimum sq ft", "error");
      return;
    }
    setCompanyMinSqft(v);
    setMinSqftEdit(null);
    showToast("Minimum sq ft saved!");
  }
  // PATCH oct18-tax: save Tax & Currency (owner only, checked again in the database)
  async function saveCompanyTax() {
    var te = taxEdit;
    if (!te) return;
    var lines = [];
    for (var i = 0; i < te.lines.length; i++) {
      var l = te.lines[i];
      var nm = String(l.name || "").trim();
      var rt = parseFloat(l.rate);
      if (!nm) { showToast("Please enter a name for each tax line", "error"); return; }
      if (isNaN(rt) || rt < 0 || rt > 100) { showToast("Please enter a rate from 0 to 100 for " + nm, "error"); return; }
      var ln = { name: nm, rate: rt };
      var no = String(l.number || "").trim();
      if (no) ln.number = no;
      lines.push(ln);
    }
    if ((te.country === "CA" || te.country === "US") && !te.region) { showToast("Please choose your " + (te.country === "CA" ? "province" : "state"), "error"); return; }
    setTaxSaving(true);
    var { error } = await supabase.rpc("set_company_tax", { p_company_id: appConfig.companyId, p_country: te.country, p_region: te.region || null, p_lines: lines, p_mode: te.mode, p_currency: te.currency });
    setTaxSaving(false);
    if (error) {
      showToast(String(error.message || "").indexOf("not_owner") !== -1 ? "Only the account owner can change tax settings" : "Error saving tax settings", "error");
      return;
    }
    setCompanyTax(normalizeTax({ tax_country: te.country, tax_region: te.region, tax_lines: lines, tax_mode: te.mode, currency: te.currency }));
    setTaxEdit(null);
    showToast("Tax settings saved!");
  }
  // PATCH oct18-tax: logo saved in the database for the whole team (owner only)
  function syncCompanyLogo(dbLogo, companyId) {
    if (dbLogo) {
      try { localStorage.setItem("businessLogo", dbLogo); } catch (_) {}
      setCompanyLogo(dbLogo);
      return;
    }
    var local = null;
    try { local = localStorage.getItem("businessLogo"); } catch (_) {}
    if (!local || !companyId) return;
    // One time move of an older phone only logo into the database. Staff calls are refused.
    shrinkLogoSrc(local).then(function (small) {
      if (!small) return;
      supabase.rpc("set_company_logo", { p_company_id: companyId, p_logo: small }).then(function (r) {
        if (r && !r.error) {
          try { localStorage.setItem("businessLogo", small); } catch (_) {}
          setCompanyLogo(small);
        }
      });
    });
  }
  async function saveCompanyLogo(file) {
    if (file.size > 5 * 1024 * 1024) { showToast("File too large! Max 5MB", "error"); return; }
    setLogoBusy(true);
    var src = await readFileAsDataUrl(file);
    var small = src ? await shrinkLogoSrc(src) : null;
    if (!small) { setLogoBusy(false); showToast("Could not read this image. Please use PNG or JPG.", "error"); return; }
    var { error } = await supabase.rpc("set_company_logo", { p_company_id: appConfig.companyId, p_logo: small });
    setLogoBusy(false);
    if (error) {
      showToast(String(error.message || "").indexOf("not_owner") !== -1 ? "Only the account owner can change the logo" : "Error saving logo", "error");
      return;
    }
    try { localStorage.setItem("businessLogo", small); } catch (_) {}
    setCompanyLogo(small);
    showToast("Logo saved for your whole team!");
  }
  async function removeCompanyLogo() {
    if (!window.confirm("Remove the logo for your whole team?")) return;
    setLogoBusy(true);
    var { error } = await supabase.rpc("set_company_logo", { p_company_id: appConfig.companyId, p_logo: null });
    setLogoBusy(false);
    if (error) { showToast("Error removing logo", "error"); return; }
    try { localStorage.removeItem("businessLogo"); } catch (_) {}
    setCompanyLogo("");
    showToast("Logo removed!");
  }
  async function fetchMessages() {
    var { data } = await supabase.from("messages").select("*").eq("company_id", appConfig.companyId).order("created_at", { ascending: true }).limit(100);
    if (data) setChatMessages(data);
  }
  async function sendMessage() {
    if (!chatInput.trim() && !chatPhoto) return;
    var se = session?.user?.email || "unknown";
    var sn = se.split("@")[0];
    var msg = chatInput.trim();
    if (chatPhoto) msg = "[PHOTO]:" + chatPhoto + (msg ? String.fromCharCode(10) + msg : "");
    var { error: _cmErr } = await supabase.from("messages").insert([{ sender_email: se, sender_name: sn, message: msg, company_id: appConfig.companyId }]); // PATCH oct16-fixes (#2)
    if (_cmErr) { showToast("Message not sent, please try again", "error"); return; }
    setChatInput("");
    setChatPhoto(null);
  }
  async function setupTeam() {
    if (!session) return;
    var companyId = localStorage.getItem("blindspro_company_id");
    if (!companyId) return;

    // Get team_code from companies table
    var { data: company } = await supabase
      .from("companies")
      .select("team_code, business_name")
      .eq("company_id", companyId)
      .single();

    var code = company?.team_code;

    // If no team_code yet, generate one and save it
    if (!code) {
      code = (company?.business_name || "BIZ").replace(/[^A-Za-z]/g, "").substring(0, 3).toUpperCase() + "-" + Math.floor(1000 + Math.random() * 9000);
      await supabase.from("companies").update({ team_code: code }).eq("company_id", companyId);
    }

    setTeamCode(code);
    localStorage.setItem("teamCode", code);

    // Session 30: the owner team row is created by register_company in the database.

    fetchTeamMembers();
  }
  // Session 30: staff are marked active on sign in, then the team list loads.
  async function afterCompanyResolved() {
    try { await supabase.rpc("activate_my_membership"); } catch (e) {}
    fetchTeamMembers();
  }
  async function fetchTeamMembers() {
    var companyId = localStorage.getItem("blindspro_company_id");
    if (!companyId) return;
    var { data } = await supabase.from("team_members").select("*").eq("company_id", companyId);
    if (data) setTeamMembers(data);
  }
  async function inviteStaff() {
    if (!inviteName.trim() || !inviteEmail.trim()) { showToast("Please enter name and email", "error"); return; }
    setInviteLoading(true);
    try {
      var companyId = appConfig.companyId;
      var companyName = localStorage.getItem("blindspro_business_name") || "";
      var sess = await supabase.auth.getSession();
      var accessToken = sess?.data?.session?.access_token || "";
      var res = await fetch("https://www.blindsprocrm.com/api/invite-staff", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ staffEmail: inviteEmail.trim(), staffName: inviteName.trim(), companyId, companyName, accessToken }),
      });
      var data = await res.json();
      if (data.success) {
        showToast(
          data.reactivated
            ? inviteEmail + " already had an account and was re-added to your team. They can sign in with their existing password."
            : "Invite sent to " + inviteEmail + "!"
        );
        setInviteName("");
        setInviteEmail("");
        setShowInviteForm(false);
        fetchTeamMembers();
      } else {
        showToast(data.error || "Failed to send invite", "error");
      }
    } catch (e) {
      showToast("Error: " + e.message, "error");
    }
    setInviteLoading(false);
  }

  async function removeStaffMember(memberId, memberEmail) {
    if (!window.confirm("Remove " + memberEmail + " from your team? They will lose access immediately.")) return;
    var { error, count } = await supabase.from("team_members").delete({ count: "exact" }).eq("id", memberId);
    if (error) { showToast("Error removing member", "error"); return; }
    if (!count) {
      // RLS blocked the delete silently (0 rows affected, no error) —
      // most likely the caller isn't actually an owner.
      showToast("You don't have permission to remove this member", "error");
      return;
    }
    // FIXED (partial): this used to also run
    //   supabase.from("user_settings").delete().eq("company_id", appConfig.companyId).eq("value", memberEmail)
    // but every user_settings row in this app is keyed by user id or session id,
    // never by email (see "user_id" / "active_session_<uid>" rows above) — so that
    // delete never matched anything and was a no-op. It's removed here rather than
    // left in as false security. Also note: even with the correct user id, deleting
    // the active_session_<uid> row would NOT force a logout — the 5-min session
    // check only signs someone out when it finds a *different* session id in that
    // row, not when the row is missing. Real immediate revocation needs a small
    // server-side endpoint (same pattern as delete-account/invite-staff) that uses
    // the service-role key to look up the removed member's auth user id by email
    // and either delete their user_settings rows with the correct id or call
    // supabase.auth.admin.signOut(userId) directly. Flagging for a follow-up
    // change rather than shipping a fix that only looks like it works.
    showToast("Team member removed");
    fetchTeamMembers();
  }

  async function deleteAccount() {
    setDeleteLoading(true);
    try {
      var sess = await supabase.auth.getSession();
      var accessToken = sess?.data?.session?.access_token || "";
      var companyId = appConfig.companyId;
      var res = await fetch("https://www.blindsprocrm.com/api/delete-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken, companyId }),
      });
      var data = await res.json();
      if (data.success) {
        await supabase.auth.signOut();
        clearLocalKeepPrefs();
        window.location.reload();
      } else {
        showToast(data.error || "Failed to delete account", "error");
      }
    } catch (e) {
      showToast("Error: " + e.message, "error");
    }
    setDeleteLoading(false);
  }

  async function searchReferral(val) {
    setReferralSearch(val);
    if (!val.trim()) {
      setReferralResults([]);
      return;
    }
    var { data } = await supabase
      .from("customers")
      .select("id,full_name,phone,area")
      .eq("company_id", appConfig.companyId)
      .or("full_name.ilike." + orLike(val.trim().replace(/\s+/g, " ")) + ",phone.ilike." + orLike(val.trim().replace(/\s+/g, " "))) // v103
      .limit(6);
    setReferralResults((data || []).filter((cu) => cu.id !== (selectedCustomer && selectedCustomer.id)));
  }
  async function setReferral(referrer) {
    var { error: _rfErr } = await supabase.from("customers").update({ referred_by_id: referrer.id, referred_by_name: referrer.full_name }).eq("id", selectedCustomer.id);
    if (_rfErr) { showToast("Error linking referral", "error"); return; }
    setSelectedCustomer(Object.assign({}, selectedCustomer, { referred_by_id: referrer.id, referred_by_name: referrer.full_name }));
    setReferralSearch("");
    setReferralResults([]);
    showToast("Referral linked!");
  }
  async function clearReferral() {
    var { error: _rcErr } = await supabase.from("customers").update({ referred_by_id: null, referred_by_name: null }).eq("id", selectedCustomer.id);
    if (_rcErr) { showToast("Error removing referral", "error"); return; }
    setSelectedCustomer(Object.assign({}, selectedCustomer, { referred_by_id: null, referred_by_name: null }));
    showToast("Referral removed");
  }
  async function fetchLastContacted(days) {
    setLastContactedLoading(true);
    var c = new Date();
    c.setDate(c.getDate() - (days || lastContactedDays));
    var { data } = await supabase.from("customers").select("*").eq("company_id", appConfig.companyId).gte("last_contacted", c.toISOString()).order("last_contacted", { ascending: false }).limit(100);
    setLastContactedList(data || []);
    setLastContactedLoading(false);
  }
  async function fetchReferrees(rid, rname) {
    var { data } = await supabase.from("customers").select("*").eq("referred_by_id", rid).order("created_at", { ascending: false }); // lint-ignore-company-id — scoped by referred_by_id, which belongs to exactly one company
    setReferreeList(data || []);
    setSelectedReferrer(rname);
  }
  async function fetchTopReferrers() {
    var { data } = await supabase.from("customers").select("referred_by_id,referred_by_name").eq("company_id", appConfig.companyId).not("referred_by_id", "is", null);
    if (!data) return;
    var c = {};
    var n = {};
    data.forEach((cu) => {
      c[cu.referred_by_id] = (c[cu.referred_by_id] || 0) + 1;
      n[cu.referred_by_id] = cu.referred_by_name;
    });
    setTopReferrers(
      Object.entries(c)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map((e) => ({ id: e[0], name: n[e[0]], count: e[1] }))
    );
  }
  async function fetchDashboardData() {
    var nowD = new Date();
    var som = new Date(nowD.getFullYear(), nowD.getMonth(), 1).toISOString();
    var eom = new Date(nowD.getFullYear(), nowD.getMonth() + 1, 0, 23, 59, 59).toISOString();
    var [tR, nR, sR, aR, jR, mR, fR] = await Promise.all([
      supabase.from("customers").select("*", { count: "exact", head: true }).eq("company_id", appConfig.companyId),
      supabase.from("customers").select("*", { count: "exact", head: true }).eq("company_id", appConfig.companyId).eq("bulk_imported", false).gte("created_at", som),
      // v103b: page through all customers (1000 row cap)
      fetchAllRows(() => supabase.from("customers").select("job_status,customer_type").eq("company_id", appConfig.companyId).order("id", { ascending: true })),
      fetchAllRows(() => supabase.from("customers").select("area").eq("company_id", appConfig.companyId).order("id", { ascending: true })),
      supabase.from("jobs").select("scheduled_at,price,customers(full_name)").eq("company_id", appConfig.companyId).gte("scheduled_at", nowD.toISOString()).order("scheduled_at", { ascending: true }).limit(5),
      supabase.from("jobs").select("price").eq("company_id", appConfig.companyId).gte("scheduled_at", som).lte("scheduled_at", eom),
      supabase.from("follow_up_logs").select("action").eq("company_id", appConfig.companyId).gte("created_at", som),
    ]);
    var pipeline = { lead: 0, consulted: 0, quoted: 0, ordered: 0, installing: 0, done: 0 };
    (sR.data || []).forEach((c) => {
      var js = normStatus(c.job_status); if (js && pipeline[js] !== undefined) pipeline[js]++; // PATCH oct21-ui-dash
    });
    var ac = {};
    (aR.data || []).forEach((c) => {
      if (c.area) ac[c.area] = (ac[c.area] || 0) + 1;
    });
    var topAreas = Object.entries(ac)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    var mj = mR.data || [];
    var rev = mj.reduce((s, j) => s + (parseFloat(j.price) || 0), 0);
    var fc = {};
    (fR.data || []).forEach((f) => {
      fc[f.action] = (fc[f.action] || 0) + 1;
    });
    var refData = await supabase.from("customers").select("referred_by_id,referred_by_name").eq("company_id", appConfig.companyId).not("referred_by_id", "is", null);
    var rc = {};
    var rn = {};
    (refData.data || []).forEach((cu) => {
      rc[cu.referred_by_id] = (rc[cu.referred_by_id] || 0) + 1;
      rn[cu.referred_by_id] = cu.referred_by_name;
    });
    var topRef = Object.entries(rc).sort((a, b) => b[1] - a[1])[0];
    setDashboardData({
      totalCustomers: tR.count || 0,
      newLeadsThisMonth: nR.count || 0,
      jobsThisMonth: mj.length,
      revenueThisMonth: rev,
      upcomingJobs: jR.data || [],
      topAreas,
      followCounts: fc,
      pipeline,
      topReferrer: topRef ? rn[topRef[0]] : null,
      topReferrerCount: topRef ? topRef[1] : 0,
    });
  }
  async function fetchCalendarJobs(month) {
    var s = new Date(month.getFullYear(), month.getMonth(), 1).toISOString();
    var e = new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59).toISOString();
    var { data } = await supabase.from("jobs").select("*,customers(full_name)").eq("company_id", appConfig.companyId).gte("scheduled_at", s).lte("scheduled_at", e);
    if (data) setCalendarJobs(data);
  }

  // Phase 4: Accounting
  async function fetchAccountingData(filter) {
    setAcctLoading(true);
    var q = supabase.from("jobs").select("*,customers(full_name,phone,area)").eq("company_id", appConfig.companyId).order("scheduled_at", { ascending: false }).limit(200);
    if (filter && filter !== "all") q = q.eq("payment_status", filter);
    if (filter === "unpaid") q = q.gt("price", 0); // PATCH oct21-ui-dash: $0 jobs are not unpaid
    var { data } = await q;
    setAcctJobs(data || []);
    var { data: allJ } = await fetchAllRows(() => supabase.from("jobs").select("price,payment_status,deposit_amount").eq("company_id", appConfig.companyId).order("id", { ascending: true })); // PATCH audit-3to9 (#3)
    var collected = 0,
      outstanding = 0,
      paidCount = 0,
      unpaidCount = 0;
    (allJ || []).forEach((j) => {
      var p = parseFloat(j.price) || 0;
      var d = parseFloat(j.deposit_amount) || 0;
      if (j.payment_status === "paid") {
        collected += p;
        paidCount++;
      } else if (j.payment_status === "deposit") {
        collected += d;
        outstanding += p - d;
        unpaidCount++;
      } else {
        outstanding += p;
        if (p > 0) unpaidCount++; // PATCH oct21-ui-dash
      }
    });
    setAcctDash({ collected, outstanding, paidCount, unpaidCount });
    setAcctLoading(false);
  }
  async function savePayment(jobId) {
    var updates = {
      payment_status: paymentEdit.payment_status,
      payment_method: paymentEdit.payment_method || null,
      deposit_amount: parseFloat(paymentEdit.deposit_amount) || 0,
      payment_date: paymentEdit.payment_date ? new Date(paymentEdit.payment_date).toISOString() : null,
    };
    var { error } = await supabase.from("jobs").update(updates).eq("id", jobId);
    if (error) {
      showToast("Error saving payment", "error");
      return;
    }
    setEditingPayment(null);
    fetchAccountingData(acctFilter);
    showToast("Payment saved!");
  }

  // Phase 5: Route
  async function fetchRouteJobs() {
    setRouteLoading(true);
    var today = new Date();
    var s = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0).toISOString();
    var e = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();
    var { data } = await supabase.from("jobs").select("*,customers(full_name,phone,address,area)").eq("company_id", appConfig.companyId).gte("scheduled_at", s).lte("scheduled_at", e).order("scheduled_at", { ascending: true });
    setRouteJobs((data || []).filter((j) => !/cancel|complet|done/i.test(String(j.status || ""))));
    setRouteLoading(false);
  }
  function routeStops(jobs) {
    return jobs.filter((j) => j.customers?.address).map((j) => {
      var full = fullAddr(j.customers); // PATCH oct21-ui-more (bug 2)
      return encodeURIComponent(full);
    });
  }
  // Google Maps links allow 10 stops (9 waypoints + destination), so long days are split into parts.
  // Part 1 starts from the phone's current location; each later part starts at the previous part's last stop.
  function buildRouteLegs(jobs) {
    var stops = routeStops(jobs);
    if (!stops.length) return [];
    if (stops.length === 1) return ["https://www.google.com/maps/search/?api=1&query=" + stops[0]];
    var legs = [];
    var PER_LEG = 10;
    for (var i = 0; i < stops.length; i += PER_LEG) {
      var chunk = stops.slice(i, i + PER_LEG);
      var url = "https://www.google.com/maps/dir/?api=1";
      if (i > 0) url += "&origin=" + stops[i - 1];
      url += "&destination=" + chunk[chunk.length - 1];
      if (chunk.length > 1) url += "&waypoints=" + chunk.slice(0, -1).join("%7C");
      url += "&travelmode=driving";
      legs.push(url);
    }
    return legs;
  }
  function buildFullRouteUrl(jobs) {
    var legs = buildRouteLegs(jobs);
    return legs.length ? legs[0] : null;
  }
  function buildRouteWhatsApp(jobs) {
    var today = new Date().toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    var N = "\n";
    var msg = "📅 Today's Jobs: " + today + N + N;
    jobs.forEach((j, i) => {
      var time = new Date(j.scheduled_at).toLocaleString("en-CA", { hour: "2-digit", minute: "2-digit" });
      var name = j.customers?.full_name || "Customer";
      var addr = fullAddr(j.customers || {}) || "No address"; // PATCH oct21-ui-more (bug 2)
      msg += i + 1 + ". " + time + ", " + name + N + "   " + addr + N + N;
    });
    var legs = buildRouteLegs(jobs);
    if (legs.length === 1) msg += "🗺️ Full Route: " + legs[0];
    else legs.forEach((u, k) => { msg += "🗺️ Route part " + (k + 1) + " of " + legs.length + ": " + u + N + N; });
    return msg;
  }

  async function fetchMeasurementHistory(cid) {
    var { data } = await supabase.from("measurements").select("*").eq("contact_id", cid).order("created_at", { ascending: false }).limit(20);
    setMeasurementHistory(data || []);
  }

  // Phase 6: Quote history
  async function fetchCustCounts(cid) { // PATCH oct21-ui-customer
    var [q, m] = await Promise.all([
      supabase.from("quotes").select("id", { count: "exact", head: true }).eq("company_id", appConfig.companyId).eq("contact_id", cid),
      supabase.from("measurements").select("id", { count: "exact", head: true }).eq("contact_id", cid),
    ]);
    setCustCounts({ cid: cid, quotes: q.error ? null : q.count || 0, measurements: m.error ? null : m.count || 0 });
  }
  async function fetchQuoteHistory(cid) {
    var { data } = await supabase.from("quotes").select("*").eq("company_id", appConfig.companyId).eq("contact_id", cid).order("created_at", { ascending: false }).limit(20);
    setQuoteHistory(data || []);
  }
  // PATCH oct18-quotes: delete one quote from Quote History
  async function deleteQuote(q, cid) {
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    var { error, count } = await supabase.from("quotes").delete({ count: "exact" }).eq("id", q.id);
    if (error) { showToast("Error deleting quote", "error"); return; }
    if (!count) { showToast("This quote could not be deleted", "error"); return; }
    if (expandedQuote === q.id) setExpandedQuote(null);
    fetchQuoteHistory(cid);
    fetchCustCounts(cid); // PATCH oct21-ui-customer
    showToast("Quote deleted");
  }

  function Toast() {
    return toast ? (
      <div
        style={{
          position: "fixed",
          bottom: 90,
          left: "50%",
          transform: "translateX(-50%)",
          background: toast.type === "error" ? C.red : C.green,
          color: "white",
          padding: "12px 24px",
          borderRadius: 12,
          fontSize: 14,
          fontWeight: "bold",
          zIndex: 9999,
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          whiteSpace: "normal",
          maxWidth: "90vw",
          width: "max-content",
          boxSizing: "border-box",
          textAlign: "center",
        }}
      >
        <Icon name={toast.type === "error" ? "x" : "check"} size={18} w={2.5} /> {toast.message}
      </div>
    ) : null;
  }
  function DeleteModal() {
    if (!confirmDelete) return null;
    var isAll = confirmDelete === "__ALL__";
    return (
      <div
        style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      >
        <div style={{ background: theme.card, borderRadius: 16, padding: 24, maxWidth: 340, width: "100%" }}>
          <div style={{ fontSize: 38, textAlign: "center", marginBottom: 12 }}>🗑</div>
          <h3 style={{ margin: "0 0 8px", fontSize: 18, textAlign: "center", color: theme.text }}>{isAll ? "Delete ALL Customers?" : "Delete Customer?"}</h3>
          <p style={{ margin: "0 0 24px", fontSize: 14, color: theme.subText, textAlign: "center" }}>{isAll ? "All " + totalCount + " customers, jobs and reminders will be permanently deleted." : "All jobs, reminders, notes, measurements and quotes will be removed."}</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => setConfirmDelete(null)}
              style={{
                flex: 1,
                padding: 12,
                background: theme.sectionBg,
                border: "1px solid " + theme.cardBorder,
                borderRadius: 8,
                fontSize: 15,
                cursor: "pointer",
                fontWeight: "bold",
                color: theme.text,
              }}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (isAll) {
                  setConfirmDelete(null);
                  showToast("Deleting all contacts...");
                  var { data } = await supabase.from("customers").select("id").eq("company_id", appConfig.companyId);
                  if (!data || data.length === 0) { showToast("No contacts to delete"); return; }
                  var ids = data.map(c => c.id);
                  await supabase.from("jobs").delete().in("contact_id", ids);
                  await supabase.from("reminders").delete().in("contact_id", ids);
                  await supabase.from("follow_up_logs").delete().in("contact_id", ids);
                  await supabase.from("customers").delete().eq("company_id", appConfig.companyId);
                  showToast("All contacts deleted!");
                  fetchCustomers();
                  fetchStatusCounts();
                } else {
                  deleteCustomer(confirmDelete);
                }
              }}
              style={{ flex: 1, padding: 12, background: C.red, border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold", color: "white" }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PATCH oct21-ui-dash: one header style (Back, icon, title), logo kept small on the right
  function LogoHeader({ sub, title, icon, showBack, onBack }) {
    var heading = title || businessName || appConfig.companyName;
    var small = title ? (sub || businessName || appConfig.companyName) : sub;
    var logoBox = function (s) {
      return companyLogo ? (
        <img src={companyLogo} alt="Logo" style={{ width: s, height: s, borderRadius: 8, objectFit: "contain", background: "#2F6F6B", flexShrink: 0 }} />
      ) : (
        <div style={{ width: s, height: s, borderRadius: 8, background: "#2F6F6B", display: "flex", alignItems: "center", justifyContent: "center", color: "white", flexShrink: 0 }}><Icon name="window" size={Math.round(s / 2)} /></div>
      );
    };
    return (
      <div style={{ background: theme.card, borderRadius: 14, marginBottom: 12, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12, border: "1px solid " + theme.cardBorder }}>
        {showBack && (
          <button onClick={onBack} aria-label="Back" style={Object.assign({}, UI.btnS, { padding: "0 12px", background: theme.sectionBg, border: "1px solid " + theme.cardBorder, flexShrink: 0 })}>
            <Icon name="arrowLeft" size={18} /> Back
          </button>
        )}
        {!showBack && !title && logoBox(40)}
        {!showBack && title && icon && (
          <span style={{ width: 40, height: 40, borderRadius: 10, background: UI.tint, color: UI.tealT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={icon} size={22} /></span>
        )}
        {showBack && icon && <span style={{ color: UI.tealT }}><Icon name={icon} size={22} /></span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: title ? 20 : 17, fontWeight: 700, color: theme.text, lineHeight: 1.2 }}>{heading}</div>
          {small && !showBack && <div style={{ fontSize: 13, color: theme.mutedText, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{small}</div>}
        </div>
        {title && !showBack && companyLogo && logoBox(32)}
        <button
          onClick={() => setDarkMode(!darkMode)}
          aria-label={darkMode ? "Light mode" : "Dark mode"}
          style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: theme.text, flexShrink: 0 }}
        >
          <Icon name={darkMode ? "sun" : "moon"} size={20} />
        </button>
      </div>
    );
  }

  function BottomNav() {
    var navTeal = darkMode ? "#7CC2BB" : C.teal; // PATCH oct21-ui-home
    var tabs = [
      { s: "list", i: "home", l: "Home" },
      { s: "dashboard", i: "chart", l: "Dashboard" },
      { s: "calendar", i: "calendar", l: "Calendar" },
      { s: "accounting", i: "wallet", l: "Accounting" },
      { s: "settings", i: "settings", l: "Settings" },
    ];
    return (
      <div
        style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: theme.card, borderTop: "1px solid " + theme.cardBorder, display: "flex", zIndex: 100, maxWidth: 600, margin: "0 auto" }}
      >
        {tabs.map((t) => {
          var active = currentScreen === t.s;
          return (
            <button
              key={t.s}
              aria-current={active ? "page" : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setCurrentScreen(t.s);
                if (t.s === "dashboard") fetchDashboardData();
                if (t.s === "calendar") fetchCalendarJobs(calendarMonth);
                if (t.s === "reports") {
                  fetchNotContacted(notContactedDays);
                  fetchTopReferrers();
                }
                if (t.s === "accounting") fetchAccountingData(acctFilter);
              }}
              style={{ flex: 1, padding: "8px 0 6px", background: "transparent", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}
            >
              <span style={{ width: 52, height: 30, borderRadius: 99, background: active ? C.teal : "transparent", color: active ? "white" : theme.mutedText, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name={t.i} size={20} w={active ? 2.5 : 2} /></span>
              <span style={{ fontSize: 11, fontWeight: active ? 700 : 600, color: active ? navTeal : theme.mutedText, marginTop: 2 }}>{t.l}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // ── TRIAL EXPIRED PAYWALL ──
  function TrialExpiredPaywall({ ended, disabled }) {
    var plans = [
      {
        name: "Starter",
        price: "$19",
        yearly: "$190/yr",
        color: "#2F6F6B",
        priceId: "price_1TgbF2Qd2egXy2XpnOXq5bgm",
        priceIdYearly: "price_1TgbF3Qd2egXy2XpMJdPRc2h",
        features: ["Up to 200 customers", "1 user only (owner)", "Up to 5 WhatsApp templates", "All core features"],
      },
      {
        name: "Pro",
        price: "$39",
        yearly: "$390/yr",
        color: "#1E5FA8",
        priceId: "price_1TgbF5Qd2egXy2XpOvimGps0",
        priceIdYearly: "price_1TgbF4Qd2egXy2XpFyFbhc75",
        features: ["Up to 500 customers", "Up to 5 users (incl. owner)", "Up to 10 WhatsApp templates", "Laser measure"],
        popular: true,
      },
      {
        name: "Business",
        price: "$59",
        yearly: "$590/yr",
        color: "#6A3FA0",
        priceId: "price_1TgbF6Qd2egXy2XpyKYtLn5Y",
        priceIdYearly: "price_1TgbF5Qd2egXy2Xp3e9ia7jy",
        features: ["Unlimited customers", "Up to 10 users (incl. owner)", "Unlimited WhatsApp templates", "Laser measure"],
      },
    ];
    // billingInterval state now lives in App (see top of App)
    // Session 32: account disabled by admin. No plans shown.
    // PATCH oct21-ui-dash: paywall in the app's own colours (light and dark)
    var pwWrap = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: theme.bg, zIndex: 99999, overflowY: "auto", fontFamily: "inherit" };
    var pwSignOut = () => supabase.auth.signOut().then(() => { clearLocalKeepPrefs(); window.location.reload(); });
    var pwHelp = <p style={{ color: theme.mutedText, fontSize: 13, marginTop: 20, textAlign: "center" }}>Questions? Email support@blindsprocrm.com</p>;
    var pwBadge = function (icon, color, bg) {
      return <div style={{ width: 64, height: 64, borderRadius: 99, background: bg, color: color, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><Icon name={icon} size={30} /></div>;
    };
    // Session 32: account disabled by admin. No plans shown.
    if (disabled) {
      return (
        <div style={pwWrap}>
          <div style={{ maxWidth: 460, margin: "0 auto", padding: "80px 16px 60px" }}>
            <div style={Object.assign({}, UI.card, { padding: 24, textAlign: "center" })}>
              {pwBadge("lock", UI.danger, darkMode ? "#3A1F1D" : "#FBEAE8")}
              <h1 style={{ color: theme.text, fontSize: 24, fontWeight: 700, margin: "0 0 10px" }}>Account disabled</h1>
              <p style={{ color: theme.subText, fontSize: 15, lineHeight: 1.5, margin: "0 0 20px" }}>This account has been disabled.</p>
              <button onClick={pwSignOut} style={Object.assign({}, UI.btnS, { width: "100%" })}>Sign out</button>
            </div>
            {pwHelp}
          </div>
        </div>
      );
    }
    // PLAY_POLICY_NATIVE_PAYWALL: Google Play Payments policy. The Android app must
    // not show plans, prices, or lead to Stripe. Web keeps the full paywall below.
    if (Capacitor.isNativePlatform()) {
      return (
        <div style={pwWrap}>
          <div style={{ maxWidth: 460, margin: "0 auto", padding: "80px 16px 60px" }}>
            <div style={Object.assign({}, UI.card, { padding: 24, textAlign: "center" })}>
              {pwBadge("clock", UI.tealT, UI.tint)}
              <h1 style={{ color: theme.text, fontSize: 24, fontWeight: 700, margin: "0 0 10px" }}>{ended ? "Your subscription has ended" : "Your trial has ended"}</h1>
              <p style={{ color: theme.subText, fontSize: 15, lineHeight: 1.5, margin: "0 0 20px" }}>Your account does not have an active subscription.</p>
              <button onClick={pwSignOut} style={Object.assign({}, UI.btnS, { width: "100%" })}>Sign out</button>
            </div>
            {pwHelp}
          </div>
        </div>
      );
    }
    var segBtn = function (on) {
      return { flex: 1, minHeight: 40, padding: "0 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 15, fontWeight: 600, fontFamily: "inherit", background: on ? theme.card : "transparent", color: on ? UI.tealT : theme.subText, boxShadow: on ? "0 1px 3px rgba(0,0,0,0.12)" : "none" };
    };
    return (
      <div style={pwWrap}>
        <div style={{ maxWidth: 500, margin: "0 auto", padding: "40px 16px 60px" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            {pwBadge("clock", UI.tealT, UI.tint)}
            <h1 style={{ color: theme.text, fontSize: 24, fontWeight: 700, margin: "0 0 8px" }}>{ended ? "Your subscription has ended" : "Your trial has ended"}</h1>
            <p style={{ color: theme.subText, fontSize: 15, margin: "0 0 20px" }}>Choose a plan to keep using BlindsPro CRM</p>
            <div style={{ display: "inline-flex", background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: 3, gap: 3, minWidth: 260 }}>
              <button onClick={() => setBillingInterval("monthly")} aria-pressed={billingInterval === "monthly"} style={segBtn(billingInterval === "monthly")}>Monthly</button>
              <button onClick={() => setBillingInterval("yearly")} aria-pressed={billingInterval === "yearly"} style={segBtn(billingInterval === "yearly")}>Yearly <span style={{ fontSize: 12, color: UI.green }}>Save 17%</span></button>
            </div>
          </div>
          {plans.map((plan) => (
            <div key={plan.name} style={Object.assign({}, UI.card, { padding: "22px 18px", marginBottom: 16, position: "relative", border: plan.popular ? "2px solid " + UI.tealT : "1px solid " + theme.cardBorder })}>
              {plan.popular && <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: C.teal, color: "white", padding: "3px 14px", borderRadius: 99, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>Most popular</div>}
              <div style={{ fontSize: 18, fontWeight: 700, color: theme.text }}>{plan.name}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: UI.tealT, lineHeight: 1.15 }}>{plan.price}<span style={{ fontSize: 14, fontWeight: 500, color: theme.mutedText }}> /mo CAD</span></div>
              {billingInterval === "yearly" && <div style={{ fontSize: 13, color: theme.mutedText }}>{plan.yearly} billed yearly</div>}
              <div style={{ margin: "14px 0 16px" }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 15, color: theme.text }}>
                    <span style={{ color: UI.tealT }}><Icon name="check" size={16} /></span> {f}
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleCheckout(billingInterval === "yearly" ? plan.priceIdYearly : plan.priceId, plan.name)}
                disabled={checkoutLoading === plan.name}
                style={Object.assign({}, plan.popular ? UI.btnP : UI.btnS, { width: "100%", minHeight: 48, opacity: checkoutLoading === plan.name ? 0.7 : 1 })}
              >
                {checkoutLoading === plan.name ? "Opening Stripe..." : "Choose " + plan.name}
              </button>
            </div>
          ))}
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <button onClick={pwSignOut} style={{ minHeight: 44, background: "transparent", border: "none", color: theme.subText, cursor: "pointer", fontSize: 15, fontFamily: "inherit" }}>Sign out</button>
          </div>
          {pwHelp}
        </div>
      </div>
    );
  }

  // ── ACCOUNTING SCREEN ──
  if (window.location.pathname === "/admin") return <AdminPanel />;
  // PATCH oct21-ui-dash: loading and error screens in the app's own colours
  function splash(msg, extra) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: theme.bg, padding: 24, textAlign: "center", boxSizing: "border-box", fontFamily: "inherit" }}>
        <div style={{ width: 56, height: 56, borderRadius: 14, background: "#2F6F6B", color: "white", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}><Icon name="window" size={28} /></div>
        <div style={{ color: theme.text, fontSize: 22, fontWeight: 700, marginBottom: 6 }}>BlindsPro CRM</div>
        <div style={{ color: theme.mutedText, fontSize: 15, marginBottom: extra ? 20 : 0, maxWidth: 360, lineHeight: 1.5 }}>{msg}</div>
        {extra}
      </div>
    );
  }
  if (authLoading) return splash("Loading...");
  if (!session) return <Login />;
  if (session && companyState === "loading") return splash("Loading...");
  if (session && companyState === "error") return splash("Could not load your account. Please check your connection and try again.", (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 280 }}>
      <button onClick={() => window.location.reload()} style={UI.btnP}>Try again</button>
      <button onClick={() => supabase.auth.signOut().then(() => { clearLocalKeepPrefs(); window.location.reload(); })} style={UI.btnS}>Sign out</button>
    </div>
  ));
  if (session && companyState === "none") return <FinishSetup session={session} onSignOut={() => supabase.auth.signOut().then(() => { clearLocalKeepPrefs(); window.location.reload(); })} />;
  if (session && !trialChecked) return splash("Loading...");
  // Session 32: past_due keeps access while Stripe retries the card.
  var subscriptionEnded = companyStatus === "cancelled" || companyStatus === "inactive" || companyStatus === "disabled";
  if (trialExpired || subscriptionEnded) return <TrialExpiredPaywall ended={subscriptionEnded} disabled={companyStatus === "disabled"} />;
  if (showNameSetup) return <BusinessNameSetup onSave={(name) => { setBusinessName(name); setShowNameSetup(false); }} />;
  if (currentScreen === "csv") return <CSVImport plan={companyPlan} onClose={() => { setCurrentScreen("settings"); fetchCustomers(); fetchAreas(); requestContactSync(); }} />;
  if (currentScreen === "googlesync") return <GoogleSync onClose={() => setCurrentScreen("settings")} onSyncComplete={() => { fetchCustomers(); fetchAreas(); }} />;
  if (showQuote) return <><Toast />{/* PATCH oct18-qedit: messages on the quote screen */}<QuoteTemplate key={editingQuote ? editingQuote.id : "new"} editQuote={editingQuote} customer={quoteCustomer} onClose={() => { setShowQuote(false); setQuoteCustomer(null); setEditingQuote(null); if (selectedCustomer) fetchQuoteHistory(selectedCustomer.id); /* PATCH oct18-quotes */ }} onSaved={() => { setShowQuote(false); setQuoteCustomer(null); }} theme={theme} C={C} showToast={showToast} taxSettings={companyTax} /></>;
  if (showMeasurements) return <Measurements
    onClose={() => {
      setShowMeasurements(false);
      setEditingMeasurement(null);
      if (selectedCustomer) fetchMeasurementHistory(selectedCustomer.id);
    }}
    theme={theme} C={C} showToast={showToast}
    editingRecord={editingMeasurement}
    plan={companyPlan}
    minSqft={companyMinSqft}
  />;

  if (currentScreen === "help") return <Help theme={theme} C={C} onClose={() => setCurrentScreen("settings")} />;

  if (currentScreen === "accounting") {
    return (
      <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: 80 }}>
        <Toast />
        <LogoHeader title="Accounting" icon="wallet" />
        {/* PATCH oct21-ui-dash: white tiles with the number in colour */}
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <KpiCard theme={theme} label="Collected" value={money(acctDash.collected, 0)} color={UI.green} />
          <KpiCard theme={theme} label="Outstanding" value={money(acctDash.outstanding, 0)} color={acctDash.outstanding > 0 ? UI.danger : theme.text} />
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <KpiCard theme={theme} label="Paid jobs" value={acctDash.paidCount} color={UI.tealT} />
          <KpiCard theme={theme} label="Unpaid or deposit" value={acctDash.unpaidCount} color={darkMode ? "#E9A23B" : "#C2700A"} />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {["all", "unpaid", "deposit", "paid"].map((f) => {
            var lbl = { all: "All", unpaid: "Unpaid", deposit: "Deposit", paid: "Paid" };
            var active = acctFilter === f;
            return (
              <button
                key={f}
                aria-pressed={active}
                onClick={() => {
                  setAcctFilter(f);
                  fetchAccountingData(f);
                }}
                style={{ flex: 1, minHeight: 40, padding: "0 12px", borderRadius: 99, border: "1px solid " + (active ? C.teal : theme.inputBorder), background: active ? C.teal : theme.card, color: active ? "white" : theme.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              >
                {f !== "all" && <span style={{ width: 8, height: 8, borderRadius: 99, background: active ? "white" : PS_COLOR[f] }}></span>}
                {lbl[f]}
              </button>
            );
          })}
        </div>
        {acctLoading && <div style={{ textAlign: "center", color: theme.subText, padding: 20 }}>Loading...</div>}
        {!acctLoading && acctJobs.length === 0 && <div style={{ textAlign: "center", color: theme.subText, padding: 20 }}>No jobs found.</div>}
        {!acctLoading &&
          acctJobs.map((job) => {
            var isEditing = editingPayment === job.id;
            var pStatus = job.payment_status || "unpaid";
            var price = parseFloat(job.price) || 0;
            var deposit = parseFloat(job.deposit_amount) || 0;
            var balance = pStatus === "deposit" ? price - deposit : pStatus === "paid" ? 0 : price;
            var dt = job.scheduled_at ? fmtDateTime(new Date(job.scheduled_at)) : "";
            var boxS = { flex: 1, background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: "8px 10px", textAlign: "center" };
            return (
              <div key={job.id} style={UI.card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                  <div onClick={async () => { if(!job.contact_id) return; var{data}=await supabase.from('customers').select('*').eq('id',job.contact_id).single(); if(data){setSelectedCustomer(data);fetchJobs(data.id);setCurrentScreen('list');} }} style={{ cursor: job.contact_id ? "pointer" : "default", minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: theme.text, fontSize: 16 }}>{job.customers?.full_name || "Unknown"}</div>
                    <div style={{ fontSize: 13, color: theme.mutedText, marginTop: 2 }}>{[job.customers?.area, dt].filter(Boolean).join(" · ")}</div>
                  </div>
                  {price > 0 ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: theme.text, border: "1px solid " + theme.cardBorder, background: theme.card, padding: "3px 10px", borderRadius: 99, flexShrink: 0 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 99, background: PS_COLOR[pStatus] || theme.mutedText }}></span>
                      {(PS_LABEL[pStatus] || pStatus).replace(" ✓", "")}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 600, color: theme.subText, background: theme.sectionBg, border: "1px solid " + theme.cardBorder, padding: "3px 10px", borderRadius: 99, flexShrink: 0 }}>No price</span>
                  )}
                </div>
                {price > 0 && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <div style={boxS}>
                      <div style={{ fontSize: 12, color: theme.mutedText }}>Job total</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>{money(price)}</div>
                    </div>
                    {pStatus === "deposit" && (
                      <div style={boxS}>
                        <div style={{ fontSize: 12, color: theme.mutedText }}>Deposit</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>{money(deposit)}</div>
                      </div>
                    )}
                    <div style={boxS}>
                      <div style={{ fontSize: 12, color: theme.mutedText }}>Balance</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: balance > 0 ? UI.danger : UI.green }}>{money(balance)}</div>
                    </div>
                  </div>
                )}
                {job.payment_method && (
                  <div style={{ fontSize: 13, color: theme.mutedText, marginBottom: 10 }}>
                    Paid by {job.payment_method}
                    {job.payment_date ? " · " + fmtDate(/^\d{4}-\d{2}-\d{2}$/.test(String(job.payment_date)) ? new Date(job.payment_date + "T00:00:00") : job.payment_date) : ""}
                  </div>
                )}
                {isEditing ? (
                  <div style={{ background: theme.sectionBg, borderRadius: 10, padding: 12, border: "1px solid " + C.amber }}>
                    <div style={{ fontSize: 12, fontWeight: "bold", color: C.amber, marginBottom: 8 }}>Update Payment</div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: theme.subText, marginBottom: 4 }}>Status</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {["unpaid", "deposit", "paid"].map((s) => {
                          var a = paymentEdit.payment_status === s;
                          return (
                            <button
                              key={s}
                              onClick={() => setPaymentEdit((p) => ({ ...p, payment_status: s }))}
                              style={{
                                flex: 1,
                                padding: "7px 4px",
                                borderRadius: 7,
                                border: "1.5px solid " + (a ? PS_COLOR[s] : theme.cardBorder),
                                background: a ? PS_COLOR[s] : "transparent",
                                color: a ? "white" : theme.subText,
                                fontSize: 12,
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              {PS_LABEL[s]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: theme.subText, marginBottom: 4 }}>Method</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {["Cash", "Cheque", "E-Transfer", "Card"].map((m) => {
                          var a = paymentEdit.payment_method === m;
                          return (
                            <button
                              key={m}
                              onClick={() => setPaymentEdit((p) => ({ ...p, payment_method: m }))}
                              style={{
                                padding: "6px 10px",
                                borderRadius: 7,
                                border: "1.5px solid " + (a ? C.teal : theme.cardBorder),
                                background: a ? C.teal : "transparent",
                                color: a ? "white" : theme.subText,
                                fontSize: 12,
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              {m}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {paymentEdit.payment_status === "deposit" && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: theme.subText, marginBottom: 4 }}>Deposit Amount</div>
                        <input
                          type="number"
                          min="0"
                          value={paymentEdit.deposit_amount}
                          onChange={(e) => setPaymentEdit((p) => ({ ...p, deposit_amount: e.target.value }))}
                          placeholder="0.00"
                          style={{ ...IS, marginBottom: 0 }}
                        />
                      </div>
                    )}
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, color: theme.subText, marginBottom: 4 }}>Payment Date</div>
                      <input type="date" value={paymentEdit.payment_date} onChange={(e) => setPaymentEdit((p) => ({ ...p, payment_date: e.target.value }))} style={{ ...IS, marginBottom: 0 }} />
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => savePayment(job.id)}
                        style={{ flex: 1, padding: 10, background: C.green, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: "bold" }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingPayment(null)}
                        style={{
                          flex: 1,
                          padding: 10,
                          background: "transparent",
                          color: theme.subText,
                          border: "1px solid " + theme.cardBorder,
                          borderRadius: 8,
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: "bold",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingPayment(job.id);
                      setPaymentEdit({
                        payment_status: job.payment_status || "unpaid",
                        payment_method: job.payment_method || "",
                        deposit_amount: job.deposit_amount || "",
                        payment_date: job.payment_date ? new Date(job.payment_date).toISOString().slice(0, 10) : "",
                      });
                    }}
                    style={Object.assign({}, UI.btnS, { width: "100%", color: UI.tealT })}
                  >
                    <Icon name="card" size={18} /> Update payment
                  </button>
                )}
              </div>
            );
          })}
        <BottomNav />
      </div>
    );
  }

  // ── ROUTE PLANNER SCREEN ──
  if (currentScreen === "route") {
    // PATCH oct21-ui-more: new look, addresses without double commas
    var routeLegs = buildRouteLegs(routeJobs);
    var routeStopCount = routeStops(routeJobs).length;
    var routeUrl = routeLegs.length === 1 ? routeLegs[0] : null;
    return (
      <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: 40 }}>
        <Toast />
        <LogoHeader title="Route today" icon="map" showBack={true} onBack={() => setCurrentScreen("list")} />
        <div style={UI.card}>
          <div style={{ fontSize: 22, fontWeight: 700, color: theme.text }}>Route today</div>
          <div style={UI.hint}>{fmtDate(new Date())} · {routeJobs.length} job{routeJobs.length === 1 ? "" : "s"}</div>
        </div>
        {routeLoading && <div style={{ textAlign: "center", color: theme.subText, padding: 20 }}>Loading today's jobs...</div>}
        {!routeLoading && routeJobs.length === 0 && (
          <div style={{ ...UI.card, textAlign: "center", padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "center", color: theme.mutedText, marginBottom: 8 }}><Icon name="map" size={28} /></div>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>No jobs today</div>
          </div>
        )}
        {!routeLoading &&
          routeJobs.map((job, i) => {
            var name = job.customers?.full_name || "Customer";
            var full = fullAddr(job.customers || {});
            var mapsUrl = full ? "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(full) : null;
            return (
              <div key={job.id} style={{ ...UI.card, padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 99, background: C.teal, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{name}</div>
                    <div style={UI.hint}>{fmtTime(job.scheduled_at)}</div>
                  </div>
                  {job.price > 0 && <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{money(job.price)}</div>}
                </div>
                {full && <div style={{ fontSize: 15, color: theme.subText, marginTop: 10 }}>{full}</div>}
                <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
                  {mapsUrl ? (
                    <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ ...UI.btnS, flex: 1 }}><Icon name="nav" size={18} /> Open in Maps</a>
                  ) : (
                    <div style={{ flex: 1, ...UI.hint }}>No address</div>
                  )}
                  {job.customers?.phone && (
                    <>
                      <button aria-label={"Call " + name} onClick={() => makeCall(job.customers.phone)} style={UI.round(C.teal)}><Icon name="phone" size={18} /></button>
                      <a aria-label={"WhatsApp " + name} href={"https://wa.me/" + waNum(job.customers.phone)} target="_blank" rel="noreferrer" style={UI.round("#1E7A45")}><Icon name="whatsapp" size={18} /></a>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        {!routeLoading && routeLegs.length > 1 && (
          <div style={UI.card}>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 2 }}>Route split into {routeLegs.length} parts</div>
            <div style={{ ...UI.hint, marginBottom: 10 }}>Google Maps allows 10 stops per route. Open each part in order.</div>
            {routeLegs.map((u, k) => (
              <a key={k} href={u} target="_blank" rel="noopener noreferrer" style={{ ...UI.btnS, width: "100%", marginBottom: 8 }}>
                Part {k + 1}: stops {k * 10 + 1} to {Math.min((k + 1) * 10, routeStopCount)}
              </a>
            ))}
          </div>
        )}
        {routeJobs.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            {routeUrl && (
              <a href={routeUrl} target="_blank" rel="noreferrer" style={{ ...UI.btnP, flex: 1 }}><Icon name="map" size={18} /> Full route</a>
            )}
            <button
              onClick={() => {
                var msg = buildRouteWhatsApp(routeJobs);
                window.open("https://wa.me/?text=" + encodeURIComponent(msg), "_blank");
              }}
              style={{ ...UI.btnS, flex: 1 }}
            >
              <span style={{ color: UI.wa }}><Icon name="whatsapp" size={18} /></span> Share to staff
            </button>
          </div>
        )}
      </div>
    );
  }

  if (currentScreen === "phoneimport") {
    var pAccounts = ['all', ...Array.from(new Set(phoneImportContacts.map(c => c.account_name || 'Unknown')))];
    // v103: trimmed import search (a lone space no longer un-hides reviewed contacts)
    var pq = phoneImportSearch.trim().replace(/\s+/g, " ").toLowerCase();
    var pFiltered = phoneImportContacts.filter(c => {
      var matchAccount = phoneImportAccount === 'all' || c.account_name === phoneImportAccount;
      var matchSearch = !pq || (c.full_name && c.full_name.toLowerCase().replace(/\s+/g, " ").includes(pq));
      var wasSeen = phoneImportSeenSet.has(normalizePhone(c.phone));
      var showDespiteSeen = !wasSeen || !!pq;
      return matchAccount && matchSearch && showDespiteSeen;
    });
    return (
      <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: 100 }}>
        <Toast />
        <LogoHeader title="Import from phone" icon="smartphone" showBack={true} onBack={() => { setCurrentScreen("list"); setPhoneImportContacts([]); setPhoneImportSelected([]); }} />
        {/* PATCH oct21-ui-dash: Import from Phone in the new style */}
        {phoneImportLoading && <div style={{ textAlign: "center", color: theme.subText, padding: 40, fontSize: 15 }}>Reading phone contacts...</div>}
        {!phoneImportLoading && phoneImportContacts.length === 0 && (
          <div style={Object.assign({}, UI.card, { textAlign: "center", padding: 32 })}>
            <div style={{ width: 56, height: 56, borderRadius: 99, background: UI.tint, color: UI.tealT, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><Icon name="smartphone" size={26} /></div>
            <div style={{ fontSize: 15, color: theme.subText }}>No phone contacts found</div>
          </div>
        )}
        {!phoneImportLoading && phoneImportContacts.length > 0 && (
          <div>
            <div style={UI.card}>
              <label style={UI.lbl}>Filter by source</label>
              <select value={phoneImportAccount} onChange={(e) => setPhoneImportAccount(e.target.value)} style={UI.inp}>
                {pAccounts.map(a => <option key={a} value={a}>{a === 'all' ? 'All sources' : a}</option>)}
              </select>
              <label style={UI.lbl}>Search</label>
              <input autoComplete="bp-off" placeholder="Search by name" value={phoneImportSearch} onChange={(e) => setPhoneImportSearch(e.target.value)} style={Object.assign({}, UI.inp, { marginBottom: 0 })} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 14, color: theme.subText }}>{pFiltered.length} contacts · {phoneImportSelected.length} selected</span>
              <button onClick={() => {
                var allPhones = pFiltered.map(c => c.phone);
                if (phoneImportSelected.length === allPhones.length) setPhoneImportSelected([]);
                else setPhoneImportSelected(allPhones);
              }} style={Object.assign({}, UI.btnS, { minHeight: 40, padding: "0 14px", fontSize: 14, color: UI.tealT })}>
                {phoneImportSelected.length === pFiltered.length ? "Deselect all" : "Select all"}
              </button>
            </div>
            <div style={Object.assign({}, UI.card, { padding: 0, overflow: "hidden", marginBottom: 90 })}>
              {pFiltered.map((ct, i) => {
                var isSel = phoneImportSelected.includes(ct.phone);
                return (
                  <div key={i} role="checkbox" aria-checked={isSel} onClick={() => {
                    if (isSel) setPhoneImportSelected(phoneImportSelected.filter(p => p !== ct.phone));
                    else setPhoneImportSelected([...phoneImportSelected, ct.phone]);
                  }} style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 56, padding: "8px 14px", borderTop: i ? "1px solid " + theme.cardBorder : "none", cursor: "pointer", background: isSel ? UI.tint : theme.card }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, border: "2px solid " + (isSel ? C.teal : theme.inputBorder), background: isSel ? C.teal : "transparent", color: "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {isSel && <Icon name="check" size={16} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{ct.full_name}</div>
                      <div style={{ fontSize: 13, color: theme.mutedText }}>{[ct.phone, ct.account_name || "Unknown"].filter(Boolean).join(" · ")}</div>
                      {ct.address && <div style={{ fontSize: 13, color: theme.mutedText }}>{ct.address}</div>}
                      {ct.email && <div style={{ fontSize: 13, color: theme.mutedText }}>{ct.email}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: theme.card, borderTop: "1px solid " + theme.cardBorder, padding: 14, maxWidth: 600, margin: "0 auto" }}>
              <button disabled={!!phoneImportProgress} onClick={async () => {
                if (phoneImportSelected.length === 0) { showToast("Select at least one contact", "error"); return; }
                var companyId = appConfig.companyId;
                // v103b: page through all customers (1000 row cap caused missed duplicates)
                var { data: existing, error: existErr } = await fetchAllRows(() => supabase.from("customers").select("phone").eq("company_id", companyId).order("id", { ascending: true }));
                if (existErr) { showToast("Could not check existing customers, please try again", "error"); return; }
                var existingPhones = new Set((existing || []).map(c => c.phone ? c.phone.replace(/\D/g, "").slice(-10) : ""));
                var toImport = phoneImportContacts.filter(c => phoneImportSelected.includes(c.phone));
                // Session 31: plan customer limit
                var _room = await customerRoomLeft(companyPlan);
                if (_room <= 0) { window.alert(customerLimitMsg(companyPlan)); return; }
                var _limitHit = false;
                var inserted = 0;
                var skipped = 0;
                var failed = 0; // PATCH audit-3to9 (#5)
                var total = toImport.length;
                setPhoneImportProgress({ done: 0, total: total });
                for (var ct of toImport) {
                  var normalizedPhone = ct.phone.replace(/\D/g, "").slice(-10);
                  if (existingPhones.has(normalizedPhone)) { skipped++; }
                  else if (inserted >= _room) { _limitHit = true; break; }
                  else {
                    var phoneImportNotes = [ct.company ? "Company: " + ct.company : "", ct.notes || ""].filter(Boolean).join("\n");
                    var { error } = await supabase.from("customers").insert([{ full_name: String(ct.full_name || "").trim() || ct.phone, phone: ct.phone, email: ct.email || "", address: ct.address || "", notes: phoneImportNotes, company_id: companyId, customer_type: "lead", bulk_imported: true, assigned_to: session?.user?.id || null }]);
                    if (!error) { inserted++; existingPhones.add(normalizedPhone); }
                    else failed++;
                  }
                  setPhoneImportProgress({ done: inserted + skipped + failed, total: total });
                }
                setPhoneImportProgress(null);
                if (_limitHit) window.alert("Imported " + inserted + " contacts. The rest were not imported.\n\n" + customerLimitMsg(companyPlan));
                if (inserted > 0) requestContactSync();
                if (failed > 0) window.alert(inserted + " imported, " + skipped + " skipped (duplicates), " + failed + " could not be saved.\n\nPlease try importing those again.");
                else showToast(inserted + " imported successfully, " + skipped + " skipped (duplicates)");
                setCurrentScreen("list");
                setPhoneImportContacts([]);
                setPhoneImportSelected([]);
                fetchCustomers();
                fetchStatusCounts();
              }} style={Object.assign({}, UI.btnP, { width: "100%", minHeight: 50, fontSize: 16, background: phoneImportProgress ? theme.mutedText : C.teal, border: "none", cursor: phoneImportProgress ? "not-allowed" : "pointer" })}>
                {phoneImportProgress ? "Importing " + phoneImportProgress.done + " / " + phoneImportProgress.total + "..." : "Import " + phoneImportSelected.length + " Selected Contact" + (phoneImportSelected.length === 1 ? "" : "s")}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (currentScreen === "chat") {
    // PATCH oct21-ui-more: date dividers, new time format, send icon
    var chatLastDay = null;
    return (
      <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, height: "100vh", display: "flex", flexDirection: "column", boxSizing: "border-box", overflow: "hidden" }}>
        <Toast />
        <LogoHeader title="Team chat" icon="chat" showBack={true} onBack={() => setCurrentScreen("list")} />
        <div style={{ flex: 1, minHeight: 0, background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 14, padding: 12, marginBottom: 12, overflowY: "auto" }}>
          {chatMessages.length === 0 && <div style={{ textAlign: "center", color: theme.subText, fontSize: 15, padding: 20 }}>No messages yet. Say hello!</div>}
          {chatMessages.map((msg) => {
            var isMe = msg.sender_email === session?.user?.email;
            var day = localDateStr(msg.created_at);
            var showDay = day !== chatLastDay;
            chatLastDay = day;
            return (
              <div key={msg.id}>
                {showDay && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 12px" }}>
                    <span style={{ flex: 1, height: 1, background: theme.cardBorder }}></span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: theme.mutedText }}>{fmtDate(msg.created_at)}</span>
                    <span style={{ flex: 1, height: 1, background: theme.cardBorder }}></span>
                  </div>
                )}
                <div style={{ marginBottom: 12, display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start" }}>
                  <div style={{ fontSize: 13, color: theme.mutedText, marginBottom: 3 }}>
                    {isMe ? "You" : msg.sender_name} · {fmtTime(msg.created_at)}
                  </div>
                  <div
                    style={{
                      maxWidth: "80%",
                      padding: "10px 14px",
                      borderRadius: isMe ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                      background: isMe ? C.teal : theme.sectionBg,
                      border: isMe ? "none" : "1px solid " + theme.cardBorder,
                      color: isMe ? "white" : theme.text,
                      fontSize: 15,
                      lineHeight: 1.45,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {/* PATCH audit-3to9 (#9): text only chat */}
                    {String(msg.message || "").startsWith("[PHOTO]:")
                      ? "Photo (no longer supported)"
                      : String(msg.message || "")}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={chatEndRef} />
          {(() => {
            setTimeout(() => {
              if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
            }, 100);
            return null;
          })()}
        </div>
        <div style={{ display: "flex", gap: 8, background: theme.card, padding: 10, borderRadius: 14, border: "1px solid " + theme.cardBorder }}>
          <input
            aria-label="Message"
            placeholder="Type a message..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendMessage();
            }}
            style={{ flex: 1, minWidth: 0, minHeight: 44, padding: "0 16px", borderRadius: 22, border: "1px solid " + theme.inputBorder, fontSize: 16, background: theme.inputBg, color: theme.text, outline: "none", fontFamily: "inherit" }}
          />
          <button aria-label="Send" onClick={sendMessage} style={UI.round(C.teal)}>
            <Icon name="send" size={18} />
          </button>
        </div>
      </div>
    );
  }

  if (currentScreen === "dashboard") {
    var dData = dashboardData;
    return (
      <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: 80 }}>
        <Toast />
        <LogoHeader title="Dashboard" icon="chart" sub={new Date().toLocaleDateString("en-CA", { month: "long", year: "numeric" })} />
        {/* PATCH oct21-ui-dash: white tiles, pipeline with dots, bug 1 fixed */}
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <KpiCard theme={theme} label="Total customers" value={dData.totalCustomers || 0} color={UI.tealT} />
          <KpiCard theme={theme} label="New leads this month" value={dData.newLeadsThisMonth || 0} color={darkMode ? "#6EA8E8" : SC.lead} />
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <KpiCard theme={theme} label="Jobs this month" value={dData.jobsThisMonth || 0} color={darkMode ? "#E9A23B" : SC.consulted} />
          <KpiCard theme={theme} label="Revenue this month" value={money(dData.revenueThisMonth || 0, 0)} color={UI.green} />
        </div>
        <div style={UI.card}>
          <h3 style={Object.assign({}, UI.title, { marginBottom: 4 })}>Pipeline</h3>
          {(function () {
            var pipeTotal = SL.reduce((a, s) => a + ((dData.pipeline && dData.pipeline[s]) || 0), 0);
            return (
              <>
                <div style={Object.assign({}, UI.hint, { marginBottom: 12 })}>{pipeTotal} customer{pipeTotal === 1 ? "" : "s"} with a job status</div>
                {SL.map((s) => {
                  var count = (dData.pipeline && dData.pipeline[s]) || 0;
                  var pct = pipeTotal ? Math.round((count / pipeTotal) * 100) : 0;
                  var pctL = count > 0 && pct === 0 ? "<1" : pct === 100 && count < pipeTotal ? ">99" : pct;
                  return (
                    <div key={s} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 15, color: theme.text, fontWeight: 600 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 99, background: SC[s] }}></span>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </span>
                        <span style={{ fontSize: 14, color: theme.subText }}>{count} ({pctL}%)</span>
                      </div>
                      <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 99, height: 8, overflow: "hidden" }}>
                        <div style={{ width: pct + "%", height: "100%", background: SC[s], borderRadius: 99 }}></div>
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>
        {dData.upcomingJobs && dData.upcomingJobs.length > 0 && (
          <div style={UI.card}>
            <h3 style={Object.assign({}, UI.title, { marginBottom: 6 })}>Next jobs</h3>
            {dData.upcomingJobs.map((job, ji) => (
              <div key={job.id || ji} style={{ padding: "10px 0", borderTop: ji ? "1px solid " + theme.cardBorder : "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: theme.text, fontSize: 15 }}>{job.customers?.full_name || "Job"}</div>
                  <div style={{ fontSize: 13, color: theme.mutedText, marginTop: 2 }}>{fmtDateTime(new Date(job.scheduled_at))}</div>
                </div>
                {job.price > 0 ? (
                  <span style={{ fontSize: 15, fontWeight: 600, color: theme.text, flexShrink: 0 }}>{money(job.price)}</span>
                ) : (
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.subText, background: theme.sectionBg, border: "1px solid " + theme.cardBorder, padding: "2px 8px", borderRadius: 99, flexShrink: 0 }}>No price</span>
                )}
              </div>
            ))}
          </div>
        )}
        <BottomNav />
      </div>
    );
  }

  if (currentScreen === "calendar") {
    var calYr = calendarMonth.getFullYear();
    var calMo = calendarMonth.getMonth();
    var calFd = new Date(calYr, calMo, 1).getDay();
    var calDim = new Date(calYr, calMo + 1, 0).getDate();
    var calMn = calendarMonth.toLocaleDateString("en-CA", { month: "long", year: "numeric" });
    var calJbd = {};
    calendarJobs.forEach((j) => {
      var day = new Date(j.scheduled_at).getDate();
      if (!calJbd[day]) calJbd[day] = [];
      calJbd[day].push(j);
    });
    var calDays = [];
    for (var ci = 0; ci < calFd; ci++) calDays.push(null);
    for (var cd = 1; cd <= calDim; cd++) calDays.push(cd);
    var calToday = new Date();
    return (
      <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: 80 }}>
        <Toast />
        <LogoHeader title="Calendar" icon="calendar" />
        {/* PATCH oct21-ui-dash: month switcher in the new style, list sorted by date (bug 3) */}
        <div style={Object.assign({}, UI.card, { padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 })}>
          <button
            aria-label="Previous month"
            onClick={() => {
              var p = new Date(calYr, calMo - 1, 1);
              setCalendarMonth(p);
              fetchCalendarJobs(p);
            }}
            style={UI.iconBtn(theme.text)}
          >
            <Icon name="chevronLeft" size={22} w={2.5} />
          </button>
          <div style={{ fontSize: 18, fontWeight: 700, color: theme.text, textAlign: "center" }}>{calMn}</div>
          <button
            aria-label="Next month"
            onClick={() => {
              var nx = new Date(calYr, calMo + 1, 1);
              setCalendarMonth(nx);
              fetchCalendarJobs(nx);
            }}
            style={UI.iconBtn(theme.text)}
          >
            <Icon name="chevronRight" size={22} w={2.5} />
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3, marginBottom: 4 }}>
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <div key={i} style={{ textAlign: "center", fontSize: 12, fontWeight: 600, color: theme.mutedText, padding: "3px 0" }}>
              {d}
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3, marginBottom: 14 }}>
          {calDays.map((day, i) => {
            if (!day) return <div key={"e" + i}></div>;
            var jobs = calJbd[day] || [];
            var isTd = day === calToday.getDate() && calMo === calToday.getMonth() && calYr === calToday.getFullYear();
            return (
              <div key={day} style={{ background: isTd ? C.teal : theme.card, border: "1px solid " + (isTd ? C.teal : theme.cardBorder), borderRadius: 8, padding: "5px 3px", minHeight: 54, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: isTd ? "white" : theme.text, textAlign: "center", marginBottom: 2 }}>{day}</div>
                {jobs.slice(0, 2).map((j, ji) => (
                  <div
                    key={ji}
                    style={{ background: isTd ? "rgba(255,255,255,0.22)" : UI.tint, color: isTd ? "white" : UI.tealT, borderRadius: 4, padding: "1px 3px", fontSize: 10, fontWeight: 600, marginBottom: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {j.customers?.full_name || "Job"}
                  </div>
                ))}
                {jobs.length > 2 && <div style={{ fontSize: 10, fontWeight: 600, color: isTd ? "white" : UI.tealT, textAlign: "center" }}>+{jobs.length - 2}</div>}
              </div>
            );
          })}
        </div>
        <div style={UI.card}>
          <h3 style={Object.assign({}, UI.title, { marginBottom: 6 })}>Jobs this month ({calendarJobs.length})</h3>
          {calendarJobs.length === 0 && <p style={{ color: theme.mutedText, fontSize: 15, margin: "6px 0 0" }}>No jobs this month</p>}
          {calendarJobs.slice().sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)).map((job, ji) => (
            <div key={job.id} style={{ padding: "10px 0", borderTop: ji ? "1px solid " + theme.cardBorder : "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: theme.text, fontSize: 15 }}>{job.customers?.full_name || "Job"}</div>
                <div style={{ fontSize: 13, color: theme.mutedText, marginTop: 2 }}>{fmtDateTime(new Date(job.scheduled_at))}</div>
              </div>
              {job.price > 0 ? (
                <span style={{ fontSize: 15, fontWeight: 600, color: theme.text, flexShrink: 0 }}>{money(job.price)}</span>
              ) : (
                <span style={{ fontSize: 12, fontWeight: 600, color: theme.subText, background: theme.sectionBg, border: "1px solid " + theme.cardBorder, padding: "2px 8px", borderRadius: 99, flexShrink: 0 }}>No price</span>
              )}
            </div>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }

  if (currentScreen === "reports") {
    return (
      <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: 80 }}>
        <Toast />
        <LogoHeader title="Reports" icon="chart" />

        {/* Route Planner quick-link */}
        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 12, marginBottom: 12, overflow: "hidden" }}>
          <button
            onClick={() => {
              setCurrentScreen("route");
              fetchRouteJobs();
            }}
            style={{ width: "100%", padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>🗺️</span>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold", color: C.blue, fontSize: 15 }}>Route Planner</div>
                <div style={{ fontSize: 11, color: theme.subText }}>Today's jobs · open Maps · share to staff</div>
              </div>
            </div>
            <span style={{ fontSize: 18, color: theme.subText }}>›</span>
          </button>
        </div>

        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 12, marginBottom: 12, overflow: "hidden" }}>
          <button
            onClick={() => {
              setShowLastContacted(!showLastContacted);
              if (!showLastContacted) fetchLastContacted(lastContactedDays);
            }}
            style={{ width: "100%", padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold", color: C.green, fontSize: 15 }}>Last Contacted Report</div>
                <div style={{ fontSize: 11, color: theme.subText }}>Tap to run · customers you contacted recently</div>
              </div>
            </div>
            <span style={{ fontSize: 18, color: theme.subText }}>{showLastContacted ? "▲" : "▼"}</span>
          </button>
          {showLastContacted && (
            <div style={{ padding: "0 16px 16px" }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                {[7, 14, 30].map((d) => (
                  <button
                    key={d}
                    onClick={() => {
                      setLastContactedDays(d);
                      fetchLastContacted(d);
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 0",
                      background: lastContactedDays === d ? C.green : "transparent",
                      color: lastContactedDays === d ? "white" : C.green,
                      border: "1px solid " + C.green,
                      borderRadius: 8,
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: "bold",
                    }}
                  >
                    Last {d}d
                  </button>
                ))}
              </div>
              {lastContactedLoading && <div style={{ fontSize: 13, color: theme.subText, textAlign: "center", padding: 12 }}>Loading...</div>}
              {!lastContactedLoading && lastContactedList.length === 0 && (
                <div style={{ fontSize: 13, color: theme.subText, textAlign: "center", padding: 12 }}>No customers contacted in last {lastContactedDays} days.</div>
              )}
              {!lastContactedLoading &&
                lastContactedList.map((cu) => {
                  var ds = cu.last_contacted ? Math.floor((Date.now() - new Date(cu.last_contacted)) / 86400000) : null;
                  var timeStr = cu.last_contacted
                    ? new Date(cu.last_contacted).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                    : null;
                  return (
                    <div
                      key={cu.id}
                      onClick={() => {
                        setSelectedCustomer(cu);
                        fetchJobs(cu.id);
                      }}
                      style={{ padding: "10px 0", borderBottom: "1px solid " + theme.cardBorder, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "600", color: theme.text, fontSize: 14 }}>{cu.full_name}</div>
                        <div style={{ fontSize: 11, color: theme.subText }}>
                          {cu.area} · {cu.job_status || "no status"}
                        </div>
                        {timeStr && (
                          <div style={{ fontSize: 11, color: ds === 0 ? C.green : ds <= 3 ? C.amber : theme.mutedText, fontWeight: "bold" }}>
                            {ds === 0 ? "Today" : ds === 1 ? "Yesterday" : ds + " days ago"} · {timeStr}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            makeCall(cu.phone);
                            updateLastContacted(cu.id);
                          }}
                          style={{ padding: "7px 12px", background: C.blue, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: "bold" }}
                        >
                          📞
                        </button>
                        <a
                          href={"https://wa.me/" + waNum(cu.phone)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateLastContacted(cu.id);
                          }}
                          style={{ padding: "7px 12px", background: C.green, color: "white", borderRadius: 6, textDecoration: "none", fontSize: 13, fontWeight: "bold" }}
                        >
                          💬
                        </a>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 12, marginBottom: 12, overflow: "hidden" }}>
          <button
            onClick={() => setShowNotContacted(!showNotContacted)}
            style={{ width: "100%", padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold", color: C.orange, fontSize: 15 }}>Not Contacted Report</div>
                <div style={{ fontSize: 11, color: theme.subText }}>Tap to run · excludes Done customers</div>
              </div>
            </div>
            <span style={{ fontSize: 18, color: theme.subText }}>{showNotContacted ? "▲" : "▼"}</span>
          </button>
          {showNotContacted && (
            <div style={{ padding: "0 16px 16px" }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                {[7, 14, 30].map((d) => (
                  <button
                    key={d}
                    onClick={() => {
                      setNotContactedDays(d);
                      fetchNotContacted(d);
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 0",
                      background: notContactedDays === d ? C.orange : "transparent",
                      color: notContactedDays === d ? "white" : C.orange,
                      border: "1px solid " + C.orange,
                      borderRadius: 8,
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: "bold",
                    }}
                  >
                    Last {d}d
                  </button>
                ))}
              </div>
              {notContactedLoading && <div style={{ fontSize: 13, color: theme.subText, textAlign: "center", padding: 12 }}>Loading...</div>}
              {!notContactedLoading && notContactedList.length === 0 && (
                <div style={{ fontSize: 13, color: C.green, fontWeight: "bold", textAlign: "center", padding: 12 }}>✅ All customers contacted within {notContactedDays} days!</div>
              )}
              {!notContactedLoading &&
                notContactedList.map((cu) => {
                  var ds = cu.last_contacted ? Math.floor((Date.now() - new Date(cu.last_contacted)) / 86400000) : 999;
                  return (
                    <div key={cu.id} style={{ padding: "10px 0", borderBottom: "1px solid " + theme.cardBorder, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div
                        onClick={() => {
                          setSelectedCustomer(cu);
                          fetchJobs(cu.id);
                        }}
                        style={{ flex: 1, cursor: "pointer" }}
                      >
                        <div style={{ fontWeight: "600", color: theme.text, fontSize: 14 }}>{cu.full_name}</div>
                        <div style={{ fontSize: 11, color: theme.subText }}>
                          {cu.area} · {cu.job_status || "no status"}
                        </div>
                        <div style={{ fontSize: 11, color: ds > 30 ? C.red : C.amber, fontWeight: "bold" }}>{cu.last_contacted ? ds + " days ago" : "Never contacted"}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => {
                            makeCall(cu.phone);
                            updateLastContacted(cu.id);
                          }}
                          style={{ padding: "7px 12px", background: C.blue, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: "bold" }}
                        >
                          📞
                        </button>
                        <a
                          href={"https://wa.me/" + waNum(cu.phone)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => updateLastContacted(cu.id)}
                          style={{ padding: "7px 12px", background: C.green, color: "white", borderRadius: 6, textDecoration: "none", fontSize: 13, fontWeight: "bold" }}
                        >
                          💬
                        </a>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 12, marginBottom: 12, overflow: "hidden" }}>
          <button
            onClick={() => setShowMap(!showMap)}
            style={{ width: "100%", padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>🗺️</span>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold", color: C.blue, fontSize: 15 }}>Customer Map</div>
                <div style={{ fontSize: 11, color: theme.subText }}>Tap to view customer locations</div>
              </div>
            </div>
            <span style={{ fontSize: 18, color: theme.subText }}>{showMap ? "▲" : "▼"}</span>
          </button>
          {showMap && (
            <div style={{ padding: "0 16px 16px" }}>
              <iframe
                title="Customer Map"
                width="100%"
                height="260"
                style={{ border: "none", display: "block", borderRadius: 8, marginBottom: 10 }}
                src={(function () { var counts = {}; customers.forEach(function (cu) { var a = (cu.area || "").trim(); if (a) counts[a] = (counts[a] || 0) + 1; }); var top = Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; })[0]; return top ? "https://www.google.com/maps/embed/v1/search?key=AIzaSyD-9tSrke72FloRkJ8G2YPwhqFzV1j7Xoc&q=" + encodeURIComponent(top) : "https://www.google.com/maps/embed/v1/view?key=AIzaSyD-9tSrke72FloRkJ8G2YPwhqFzV1j7Xoc&center=45,-95&zoom=3"; })()}
                allowFullScreen
              />
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {customers
                  .filter((cu) => cu.address)
                  .slice(0, 40)
                  .map((cu) => (
                    <a
                      key={cu.id}
                      href={"https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(((cu.address || "") + " " + (cu.area || "")).trim())}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "7px 0",
                        borderBottom: "1px solid " + theme.cardBorder,
                        textDecoration: "none",
                        color: theme.text,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: "600", color: theme.text }}>{cu.full_name}</div>
                        <div style={{ fontSize: 11, color: theme.subText }}>
                          {cu.address}, {cu.area}
                        </div>
                      </div>
                      <span style={{ color: C.blue, fontSize: 20 }}>📍</span>
                    </a>
                  ))}
                {customers.filter((cu) => cu.address).length === 0 && <div style={{ fontSize: 13, color: theme.subText }}>No customers with addresses yet.</div>}
              </div>
            </div>
          )}
        </div>

        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 12, marginBottom: 12, overflow: "hidden" }}>
          <button
            onClick={() => setSelectedReferrer(selectedReferrer ? null : "")}
            style={{ width: "100%", padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>🤝</span>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold", color: C.purple, fontSize: 15 }}>Top Referrers</div>
                <div style={{ fontSize: 11, color: theme.subText }}>{topReferrers.length > 0 ? topReferrers.length + " referrers found" : "Tap to view"}</div>
              </div>
            </div>
            <span style={{ fontSize: 18, color: theme.subText }}>{selectedReferrer !== null ? "▲" : "▼"}</span>
          </button>
          {selectedReferrer !== null && (
            <div style={{ padding: "0 16px 16px" }}>
              {topReferrers.length === 0 && <div style={{ fontSize: 13, color: theme.subText, textAlign: "center", padding: 12 }}>No referrals recorded yet.</div>}
              {topReferrers.map(function (r, i) {
                var isSelected = selectedReferrer === r.name;
                return (
                  <div key={r.id}>
                    <div
                      onClick={() => {
                        if (isSelected) {
                          setSelectedReferrer("");
                          setReferreeList([]);
                        } else fetchReferrees(r.id, r.name);
                      }}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid " + theme.cardBorder, cursor: "pointer" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 20 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : "#" + (i + 1)}</span>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: "600", color: isSelected ? C.purple : theme.text }}>{r.name}</div>
                          <div style={{ fontSize: 11, color: theme.subText }}>
                            {r.count} referral{r.count === 1 ? "" : "s"}
                          </div>
                        </div>
                      </div>
                      <span style={{ background: C.purple, color: "white", padding: "3px 12px", borderRadius: 20, fontSize: 13, fontWeight: "bold" }}>{r.count}</span>
                    </div>
                    {isSelected && referreeList.length > 0 && (
                      <div style={{ background: theme.sectionBg, borderRadius: 8, padding: "8px 12px", marginBottom: 8 }}>
                        {referreeList.map(function (ref) {
                          return (
                            <div
                              key={ref.id}
                              onClick={() => {
                                setSelectedCustomer(ref);
                                fetchJobs(ref.id);
                              }}
                              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + theme.cardBorder, cursor: "pointer" }}
                            >
                              <div>
                                <div style={{ fontSize: 13, fontWeight: "600", color: theme.text }}>{ref.full_name}</div>
                                <div style={{ fontSize: 11, color: theme.subText }}>
                                  {ref.area} · {ref.job_status || "lead"}
                                </div>
                              </div>
                              <span style={{ color: C.purple, fontSize: 16 }}>→</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <BottomNav />
      </div>
    );
  }

  if (currentScreen === "settings") {
    // PATCH oct21-ui-settings: grouped, collapsible rows, one open at a time
    var myTeamRole = (teamMembers.find(function(m) {
      return m.email && session && session.user && session.user.email &&
        m.email.toLowerCase() === session.user.email.toLowerCase();
    }) || {}).role;
    var isOwner = myTeamRole === "owner";
    var sTeal = darkMode ? "#7CC2BB" : C.teal;
    var sTint = darkMode ? "#1C2E2C" : "#E6F0EF";
    var sDanger = darkMode ? "#F0776B" : "#B42318";
    var sWarn = darkMode ? "#E39A45" : "#B45309";
    var btnP = { minHeight: 44, padding: "0 16px", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: C.teal, color: "white", border: "1px solid " + C.teal, boxSizing: "border-box" };
    var btnS = Object.assign({}, btnP, { background: theme.card, color: theme.text, border: "1px solid " + theme.inputBorder });
    var btnD = { background: "transparent", border: "none", color: sDanger, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: "10px 0", minHeight: 44 };
    var lbl = { fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 6 };
    var hint = { fontSize: 13, color: theme.mutedText, marginBottom: 12, lineHeight: 1.45 };
    var inp = Object.assign({}, IS, { minHeight: 44, borderRadius: 10, fontSize: 15, marginBottom: 12, padding: "10px 12px" });
    var planName = companyPlan ? planLabel(companyPlan) + " Plan" : "Free Trial";
    var userLimits = { starter: 1, pro: 5, business: 10 };
    var maxUsers = userLimits[companyPlan] || 1;
    var canInvite = teamMembers.length < maxUsers;
    var taxSummary = (companyTax.region ? companyTax.region + " · " : "") +
      (companyTax.lines && companyTax.lines.length ? companyTax.lines.map(function (l) { return l.name + " " + l.rate + "%"; }).join(" + ") : "No tax") +
      " · " + companyTax.currency;
    var minSummary = Number(companyMinSqft) > 0 ? "Min " + companyMinSqft + " sq ft per window" : "No minimum";
    function rowHead(icon, title, summary, right, onClick, open) {
      var inner = (
        <>
          <span style={{ width: 36, height: 36, borderRadius: 10, background: sTint, color: sTeal, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={icon} size={20} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 15, fontWeight: 600, color: theme.text }}>{title}</span>
            {summary && <span style={{ display: "block", fontSize: 13, color: theme.mutedText, marginTop: 2 }}>{summary}</span>}
          </span>
          {right}
        </>
      );
      var st = { width: "100%", minHeight: 64, padding: "10px 14px", background: "transparent", border: "none", display: "flex", alignItems: "center", gap: 12, textAlign: "left", fontFamily: "inherit", boxSizing: "border-box" };
      if (!onClick) return <div style={st}>{inner}</div>;
      return <button onClick={onClick} aria-expanded={open} style={Object.assign({}, st, { cursor: "pointer" })}>{inner}</button>;
    }
    function sRow(key, icon, title, summary, body) {
      var open = settingsOpen === key;
      return (
        <div key={key}>
          {rowHead(icon, title, summary, <span style={{ color: theme.mutedText }}><Icon name={open ? "chevronUp" : "chevronDown"} size={20} /></span>, () => setSettingsOpen(open ? null : key), open)}
          {open && <div style={{ padding: "4px 14px 16px" }}>{body}</div>}
        </div>
      );
    }
    function sGroup(title, rows) {
      rows = rows.filter(Boolean);
      if (!rows.length) return null;
      return (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.mutedText, margin: "0 4px 8px" }}>{title}</div>
          <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 14, overflow: "hidden" }}>
            {rows.map((r, i) => <div key={i} style={{ borderTop: i ? "1px solid " + theme.cardBorder : "none" }}>{r}</div>)}
          </div>
        </div>
      );
    }
    function rolePill(role) {
      return (
        <span style={{ fontSize: 12, fontWeight: 600, color: theme.subText, background: theme.sectionBg, border: "1px solid " + theme.cardBorder, padding: "2px 10px", borderRadius: 99 }}>
          {role === "owner" ? "Owner" : role === "manager" ? "Manager" : "Staff"}
        </span>
      );
    }

    var logoBody = (
      <div>
        {companyLogo && (
          <img src={companyLogo} alt="Logo" style={{ width: 72, height: 72, borderRadius: 12, objectFit: "contain", border: "1px solid " + theme.cardBorder, display: "block", marginBottom: 12 }} />
        )}
        {isOwner ? (
          <>
            <label style={Object.assign({}, btnS, { display: "inline-flex", alignItems: "center", gap: 8, cursor: logoBusy ? "wait" : "pointer" })}>
              <Icon name="image" size={18} />
              {logoBusy ? "Saving..." : companyLogo ? "Change logo" : "Upload logo"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: "none" }}
                disabled={logoBusy}
                onChange={(e) => {
                  var f = e.target.files[0];
                  e.target.value = "";
                  if (f) saveCompanyLogo(f);
                }}
              />
            </label>
            <div style={Object.assign({}, hint, { marginTop: 10, marginBottom: 0 })}>Square PNG or JPG, 200 × 200 px or larger, max 5 MB. A transparent background looks best. Shown on all your team's devices.</div>
            {companyLogo && (
              <button onClick={removeCompanyLogo} disabled={logoBusy} style={btnD}>Remove logo</button>
            )}
          </>
        ) : (
          <div style={Object.assign({}, hint, { marginBottom: 0 })}>{companyLogo ? "Only the account owner can change the logo." : "No logo yet. The account owner can add one in Settings."}</div>
        )}
      </div>
    );

    var taxBody = (() => {
      var te = taxEdit || companyTax;
      var regions = regionsFor(te.country);
      var upd = (ch) => setTaxEdit(Object.assign({}, te, ch));
      var updLine = (i, ch) => upd({ lines: te.lines.map((l, k) => (k === i ? Object.assign({}, l, ch) : l)) });
      return (
        <div>
          <div style={hint}>Used on new quotes. Quotes already saved keep the tax they were made with.</div>
          <div style={lbl}>Country</div>
          <select
            value={te.country}
            onChange={(e) => {
              var co = e.target.value;
              var p = presetFor(co, null);
              upd({ country: co, region: null, lines: p.lines, currency: co === "OT" ? te.currency : p.currency });
            }}
            style={inp}
          >
            {COUNTRIES.map((co) => (
              <option key={co.code} value={co.code}>{co.name}</option>
            ))}
          </select>
          {regions.length > 0 && (
            <>
              <div style={lbl}>{te.country === "CA" ? "Province or territory" : "State"}</div>
              <select
                value={te.region || ""}
                onChange={(e) => {
                  var rg = e.target.value || null;
                  upd({ region: rg, lines: presetFor(te.country, rg).lines });
                }}
                style={inp}
              >
                <option value="">Choose...</option>
                {regions.map((rg) => (
                  <option key={rg.code} value={rg.code}>{rg.name}</option>
                ))}
              </select>
            </>
          )}
          <div style={lbl}>Tax lines (you can change any name or rate)</div>
          {te.lines.map((l, i) => (
            <div key={i} style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: 10, marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={lbl}>Name</div>
                  <input placeholder="e.g. GST" value={l.name} onChange={(e) => updLine(i, { name: e.target.value })} style={Object.assign({}, inp, { marginBottom: 8 })} />
                </div>
                <div style={{ width: 96 }}>
                  <div style={lbl}>Rate %</div>
                  <input type="number" min="0" max="100" step="0.001" value={l.rate} onChange={(e) => updLine(i, { rate: e.target.value })} style={Object.assign({}, inp, { marginBottom: 8 })} />
                </div>
                <button
                  aria-label="Remove this tax line"
                  title="Remove this tax line"
                  onClick={() => upd({ lines: te.lines.filter((_, k) => k !== i) })}
                  style={{ alignSelf: "flex-end", marginBottom: 8, width: 44, height: 44, flexShrink: 0, background: "transparent", color: sDanger, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <Icon name="x" size={18} />
                </button>
              </div>
              <div style={lbl}>Tax number shown on quotes (optional)</div>
              <input value={l.number || ""} onChange={(e) => updLine(i, { number: e.target.value })} style={Object.assign({}, inp, { marginBottom: 0 })} />
            </div>
          ))}
          {te.lines.length === 0 && <div style={hint}>No tax lines. Quotes will show no tax.</div>}
          {te.lines.length < 3 && (
            <button onClick={() => upd({ lines: te.lines.concat([{ name: "", rate: "" }]) })} style={Object.assign({}, btnS, { width: "100%", marginBottom: 12 })}>
              + Add tax line
            </button>
          )}
          {te.country === "US" && <div style={hint}>US sales tax depends on your state and city. Enter your combined rate.</div>}
          <div style={lbl}>Prices on quotes</div>
          <div style={{ display: "flex", padding: 3, background: theme.sectionBg, border: "1px solid " + theme.inputBorder, borderRadius: 10, marginBottom: 12 }}>
            {[["added", "Added on top"], ["included", "Included in price"]].map(([m, t]) => (
              <button
                key={m}
                onClick={() => upd({ mode: m })}
                aria-pressed={te.mode === m}
                style={{ flex: 1, minHeight: 38, borderRadius: 8, border: "none", background: te.mode === m ? C.teal : "transparent", color: te.mode === m ? "white" : theme.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
              >
                {t}
              </button>
            ))}
          </div>
          <div style={lbl}>Currency</div>
          <select value={te.currency} onChange={(e) => upd({ currency: e.target.value })} style={inp}>
            {CURRENCIES.map((cu) => (
              <option key={cu} value={cu}>{cu}</option>
            ))}
          </select>
          {taxEdit && (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveCompanyTax} disabled={taxSaving} style={Object.assign({}, btnP, { flex: 1, opacity: taxSaving ? 0.6 : 1 })}>
                {taxSaving ? "Saving..." : "Save tax settings"}
              </button>
              <button onClick={() => setTaxEdit(null)} style={Object.assign({}, btnS, { flex: 1 })}>Cancel</button>
            </div>
          )}
        </div>
      );
    })();

    var minBody = (
      <div>
        <div style={hint}>Minimum sq ft charged per window. Use 0 for no minimum. Measurements already saved keep the minimum they were made with.</div>
        <div style={lbl}>Minimum sq ft per window</div>
        <input type="number" min="0" max="50" step="0.5" value={minSqftEdit !== null ? minSqftEdit : String(companyMinSqft)} onChange={(e) => setMinSqftEdit(e.target.value)} style={inp} />
        {minSqftEdit !== null && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={saveMinSqft} disabled={minSqftSaving} style={Object.assign({}, btnP, { flex: 1, opacity: minSqftSaving ? 0.6 : 1 })}>
              {minSqftSaving ? "Saving..." : "Save minimum"}
            </button>
            <button onClick={() => setMinSqftEdit(null)} style={Object.assign({}, btnS, { flex: 1 })}>Cancel</button>
          </div>
        )}
      </div>
    );

    var teamBody = (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: theme.subText, marginBottom: 6 }}>
          <span>{planName}</span>
          <span style={{ fontWeight: 600, color: theme.text }}>{teamMembers.length} of {maxUsers} users</span>
        </div>
        <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 99, height: 8, overflow: "hidden", marginBottom: 14 }}>
          <div style={{ width: Math.min((teamMembers.length / maxUsers) * 100, 100) + "%", height: "100%", background: C.teal, borderRadius: 99 }} />
        </div>
        {teamMembers.map((m) => (
          <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "10px 0", borderTop: "1px solid " + theme.cardBorder }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{m.name}</div>
              <div style={{ fontSize: 13, color: theme.mutedText, wordBreak: "break-all" }}>{m.email}</div>
              {m.status === "invited" && <div style={{ fontSize: 13, color: sWarn, fontWeight: 600 }}>Invite pending</div>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              {rolePill(m.role)}
              {m.role !== "owner" && (
                <button onClick={() => removeStaffMember(m.id, m.email)} style={Object.assign({}, btnD, { padding: "0 4px" })}>Remove</button>
              )}
            </div>
          </div>
        ))}
        {showInviteForm ? (
          <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 14, marginTop: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 10 }}>Invite a staff member</div>
            <div style={lbl}>Full name</div>
            <input value={inviteName} onChange={(e) => setInviteName(e.target.value)} style={inp} />
            <div style={lbl}>Email</div>
            <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} style={inp} />
            <div style={hint}>They get an email to set their password and join your team.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={inviteStaff} disabled={inviteLoading} style={Object.assign({}, btnP, { flex: 1, opacity: inviteLoading ? 0.6 : 1 })}>
                {inviteLoading ? "Sending..." : "Send invite"}
              </button>
              <button onClick={() => { setShowInviteForm(false); setInviteName(""); setInviteEmail(""); }} style={Object.assign({}, btnS, { flex: 1 })}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            {canInvite ? (
              <button onClick={() => setShowInviteForm(true)} style={Object.assign({}, btnP, { width: "100%" })}>+ Add staff member</button>
            ) : (
              <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 2 }}>Team is full ({teamMembers.length} of {maxUsers})</div>
                <div style={{ fontSize: 13, color: theme.mutedText }}>{Capacitor.isNativePlatform() ? "Staff limit reached for your current plan." : "Upgrade your plan to add more staff members."}</div>
              </div>
            )}
            <button onClick={() => { fetchTeamMembers(); showToast("Members refreshed!"); }} style={Object.assign({}, btnS, { width: "100%", marginTop: 8 })}>
              Refresh members
            </button>
          </div>
        )}
      </div>
    );

    var subBody = (
      <div>
        <div style={hint}>Change plan, update card, see invoices or cancel.</div>
        <button onClick={openBillingPortal} disabled={portalLoading} style={Object.assign({}, btnP, { width: "100%", opacity: portalLoading ? 0.6 : 1 })}>
          {portalLoading ? "Opening..." : "Manage subscription"}
        </button>
      </div>
    );

    var infoBody = (
      <div style={{ fontSize: 15, color: theme.text, lineHeight: 1.6 }}>
        {session?.user?.email && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ wordBreak: "break-all" }}>Signed in as <b>{session.user.email}</b></span>
            {myTeamRole && rolePill(myTeamRole)}
          </div>
        )}
        <div style={{ color: theme.subText }}>{businessName || appConfig.companyName}</div>
        <div style={{ color: theme.subText }}>{companyCustomerCount} customers · {planName}</div>
        {companyStatus === "past_due" && !Capacitor.isNativePlatform() && ( /* Session 32 */
          <div style={{ fontWeight: 600, color: sWarn, marginTop: 4 }}>Last payment failed. Update your card in Manage subscription.</div>
        )}
        {trialDaysLeft !== null && (
          <div style={{ fontWeight: 600, color: trialDaysLeft <= 3 ? sWarn : theme.text, marginTop: 4 }}>
            {trialDaysLeft > 0 ? trialDaysLeft + " day" + (trialDaysLeft !== 1 ? "s" : "") + " left in trial" : "Trial expired"}
          </div>
        )}
        {/* PATCH app-version */}
        <div style={{ fontSize: 13, color: theme.mutedText, marginTop: 6 }}>
          Version {APP_VERSION} ({APP_BUILD}) · {APP_BUILD_DATE}
        </div>
      </div>
    );

    var darkSwitch = (
      <button
        role="switch"
        aria-checked={darkMode}
        aria-label="Dark mode"
        onClick={() => setDarkMode(!darkMode)}
        style={{ width: 52, height: 32, borderRadius: 99, border: "none", padding: 0, position: "relative", cursor: "pointer", flexShrink: 0, background: darkMode ? C.teal : theme.inputBorder }}
      >
        <span style={{ position: "absolute", top: 4, left: darkMode ? 24 : 4, width: 24, height: 24, borderRadius: 99, background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.25)", transition: "left 0.15s" }} />
      </button>
    );

    return (
      <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: 90 }}>
        <Toast />
        <LogoHeader title="Settings" icon="settings" />
        {VOIP_ENABLED && ( /* v103b: hidden until VoIP is enabled, code kept */
        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <h3 style={{ margin: "0 0 4px", color: C.purple, fontSize: 15 }}>📞 VoIP Integration (OpenPhone)</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 11, height: 11, borderRadius: 99, background: VOIP_ENABLED ? C.green : "#888" }}></div>
            <span style={{ fontSize: 13, color: theme.subText }}>{VOIP_ENABLED ? "✅ VoIP Active" : "⏸ Using regular calls (VoIP ready)"}</span>
          </div>
          <div style={{ background: darkMode ? "#1A1A2A" : "#f5f0fc", borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 12, color: C.purple, fontWeight: "bold", fontFamily: "monospace" }}>var VOIP_ENABLED = false; ← change to true</div>
          </div>
        </div>
        )}
        {sGroup("Company", [
          sRow("logo", "image", "Business Logo", companyLogo ? "Logo added" : "No logo", logoBody),
          isOwner && sRow("tax", "receipt", "Tax & Currency", taxSummary, taxBody), /* PATCH oct18-tax: owner only */
          isOwner && sRow("measure", "ruler", "Measurements", minSummary, minBody), /* PATCH oct20-minsqft: owner only */
        ])}
        {sGroup("Team & billing", [
          isOwner && sRow("team", "users", "Team", teamMembers.length + " of " + maxUsers + " users", teamBody), /* Session 30: owner only */
          isOwner && !Capacitor.isNativePlatform() && sRow("sub", "card", "Subscription", planName, subBody), /* Session 32: owner + web only */
        ])}
        {sGroup("App", [
          rowHead(darkMode ? "sun" : "moon", "Dark mode", darkMode ? "On" : "Off", darkSwitch),
          rowHead("help", "Help Center", "How to use every feature", <span style={{ color: theme.mutedText }}><Icon name="chevronRight" size={20} /></span>, () => { setCurrentScreen("help"); window.scrollTo(0, 0); }),
          sRow("info", "info", "App Info", "Version " + APP_VERSION + " (" + APP_BUILD + ")", infoBody),
        ])}
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.mutedText, margin: "0 4px 8px" }}>Account</div>
        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 14, padding: 14, marginBottom: 20 }}>
          <button
            onClick={() => supabase.auth.signOut().then(() => { clearLocalKeepPrefs(); window.location.reload(); })}
            style={Object.assign({}, btnS, { width: "100%" })}
          >
            Sign out
          </button>
          {!showDeleteAccount ? (
            <button onClick={() => setShowDeleteAccount(true)} style={Object.assign({}, btnD, { width: "100%", marginTop: 6 })}>
              Delete my account
            </button>
          ) : (
            <div style={{ marginTop: 12, background: theme.sectionBg, border: "1.5px solid " + sDanger, borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: sDanger, marginBottom: 6 }}>Delete account permanently</div>
              <div style={Object.assign({}, hint, { color: theme.subText })}>
                {isOwner ? "This will cancel your subscription and permanently delete your company, all customers, jobs, measurements, quotes and team members. " : "This will permanently delete your login. Your company's customers and data are not affected. "}<strong>This cannot be undone.</strong>
              </div>
              <div style={lbl}>Type DELETE to confirm</div>
              <input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                style={Object.assign({}, inp, { border: "1.5px solid " + sDanger })}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={deleteAccount}
                  disabled={deleteConfirmText !== "DELETE" || deleteLoading}
                  style={Object.assign({}, btnS, { flex: 1, color: sDanger, border: "1.5px solid " + sDanger, opacity: deleteConfirmText === "DELETE" ? 1 : 0.5, cursor: deleteConfirmText === "DELETE" ? "pointer" : "not-allowed" })}
                >
                  {deleteLoading ? "Deleting..." : "Permanently delete"}
                </button>
                <button onClick={() => { setShowDeleteAccount(false); setDeleteConfirmText(""); }} style={Object.assign({}, btnS, { flex: 1 })}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        <BottomNav />
      </div>
    );
  }

  if (selectedCustomer) {
    var c = selectedCustomer;
    // PATCH oct21-ui-customer: new layout, same saving logic
    var cTeal = darkMode ? "#7CC2BB" : C.teal;
    var cTint = darkMode ? "#1C2E2C" : "#E6F0EF";
    var cDanger = darkMode ? "#F0776B" : "#B42318";
    var waC = darkMode ? "#4CC38A" : "#1E7A45";
    var waT = darkMode ? "#16301F" : "#E5F4EA";
    var dirBlue = darkMode ? "#6FA3DE" : "#2563A8";
    var btnP = { minHeight: 44, padding: "0 16px", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: C.teal, color: "white", border: "1px solid " + C.teal, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none" };
    var btnS = Object.assign({}, btnP, { background: theme.card, color: theme.text, border: "1px solid " + theme.inputBorder });
    var btnD = { background: "transparent", border: "none", color: cDanger, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: "10px 0", minHeight: 44 };
    var lbl = { fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 6 };
    var hint = { fontSize: 13, color: theme.mutedText };
    var inp = Object.assign({}, IS, { minHeight: 44, borderRadius: 10, fontSize: 15, marginBottom: 12, padding: "10px 12px" });
    var card = { background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 14, padding: 16, marginBottom: 12 };
    var h3 = { margin: "0 0 12px", fontSize: 15, fontWeight: 600, color: theme.text };
    function iconBtn(color) { return UI.iconBtn(color); } /* PATCH oct21-ui-bold */
    function statusPill(s) {
      s = normStatus(s); // PATCH oct21-ui-dash
      if (!s) return null;
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: theme.card, border: "1px solid " + theme.cardBorder, color: theme.text, padding: "4px 10px", borderRadius: 99, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: SC[s] || theme.mutedText }}></span>
          {s.charAt(0).toUpperCase() + s.slice(1)}
        </span>
      );
    }
    function listRow(icon, label, right, onClick, open, isWa) {
      return (
        <button
          onClick={onClick}
          aria-expanded={open}
          style={{ width: "100%", minHeight: 56, padding: "8px 14px", background: "transparent", border: "none", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
        >
          <span style={{ width: 36, height: 36, borderRadius: 10, background: isWa ? waT : cTint, color: isWa ? waC : cTeal, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={icon} size={20} /></span>
          <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: theme.text }}>{label}</span>
          {right != null && right !== "" && <span style={{ fontSize: 13, color: theme.mutedText, fontWeight: 600 }}>{right}</span>}
          <span style={{ color: theme.mutedText }}><Icon name={open === undefined ? "chevronRight" : open ? "chevronUp" : "chevronDown"} size={20} /></span>
        </button>
      );
    }
    function roundAct(icon, label, bg, onClick, href, disabled) {
      var circle = (
        <span style={{ width: 52, height: 52, borderRadius: 99, background: disabled ? theme.sectionBg : bg, color: disabled ? theme.mutedText : "white", display: "flex", alignItems: "center", justifyContent: "center", border: disabled ? "1px solid " + theme.cardBorder : "none" }}>
          <Icon name={icon} size={22} />
        </span>
      );
      var st = { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, background: "transparent", border: "none", cursor: disabled ? "default" : "pointer", textDecoration: "none", fontFamily: "inherit", padding: 0 };
      var text = <span style={{ fontSize: 13, fontWeight: 600, color: disabled ? theme.mutedText : theme.text }}>{label}</span>;
      if (href && !disabled) return <a href={href} target="_blank" rel="noreferrer" onClick={onClick} style={st}>{circle}{text}</a>;
      return <button onClick={disabled ? undefined : onClick} disabled={disabled} style={st}>{circle}{text}</button>;
    }
    // Note dates are saved like [2026-10-18, 11:18:00 a.m.]. Show them in the new format.
    function noteHead(h) {
      var t = String(h || "").slice(1, -1);
      var m = t.match(/^(\d{4})-(\d{2})-(\d{2}),?\s+(\d{1,2}):(\d{2})(?::\d{2})?\s*([ap])\.?\s*m\.?(.*)$/i);
      if (!m) return t;
      var hr = parseInt(m[4], 10) % 12;
      if (m[6].toLowerCase() === "p") hr += 12;
      var d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10), hr, parseInt(m[5], 10));
      var rest = m[7].replace(/^\s*-\s*/, " · ").replace(/^\s+/, " ");
      return fmtDateTime(d) + rest;
    }
    var FU_ICON = { Called: "phone", Texted: "chat", "Sent Quote": "file", "Installation Done": "check", "No Answer": "phoneOff", "Left Voicemail": "voicemail" };
    var closeCustomer = () => {
      setLastFU(null);
      setSelectedCustomer(null);
      setEditMode(false);
      setCustomerJobs([]);
      setShowFollowUpHistory(false);
      setShowWhatsAppTemplates(false);
      setEditingJob(null);
      setShowQuoteHistory(false);
      setShowMeasurementHistory(false);
      setMeasurementHistory([]);
    };
    var cnt = custCounts.cid === c.id ? custCounts : { quotes: null, measurements: null };
    var subLine = [c.area, fp(c.phone)].filter(Boolean).join(" · ");
    return (
      <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: editMode && detailsDirty ? 100 : 24 }}>
        <Toast />
        <DeleteModal />
        <LogoHeader title="Customer" icon="users" showBack={true} onBack={closeCustomer} />

        {/* Top card */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.text, lineHeight: 1.25 }}>{c.full_name}</h2>
              {subLine && <div style={{ fontSize: 15, color: theme.subText, marginTop: 4 }}>{subLine}</div>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
              {statusPill(c.job_status)}
              {c.customer_type === "existing" && (
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.subText, background: theme.sectionBg, border: "1px solid " + theme.cardBorder, padding: "4px 10px", borderRadius: 99 }}>Existing</span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {roundAct("phone", "Call", C.teal, () => { makeCall(c.phone); updateLastContacted(c.id); }, null, !c.phone)}
            {roundAct("whatsapp", "WhatsApp", "#1E7A45", () => updateLastContacted(c.id), "https://wa.me/" + waNum(c.phone), !c.phone)}
            {roundAct("nav", "Directions", "#2563A8", null, c.address ? "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(fullAddr(c)) : null, !c.address)}
          </div>
          {(c.address || c.email) && (
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid " + theme.cardBorder }}>
              {c.address && (
                <a
                  href={"https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(fullAddr(c))}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 36, fontSize: 15, color: dirBlue, textDecoration: "none" }}
                >
                  <span style={{ color: theme.mutedText }}><Icon name="nav" size={16} /></span>
                  <span style={{ minWidth: 0, wordBreak: "break-word" }}>{fullAddr(c)}</span>
                </a>
              )}
              {c.email && (
                <a
                  href={"mailto:" + String(c.email).trim()}
                  style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 36, fontSize: 15, color: dirBlue, textDecoration: "none" }}
                >
                  <span style={{ color: theme.mutedText }}><Icon name="mail" size={16} /></span>
                  <span style={{ minWidth: 0, wordBreak: "break-all" }}>{String(c.email).trim()}</span>
                </a>
              )}
            </div>
          )}
        </div>

        {/* Actions list */}
        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 14, overflow: "hidden", marginBottom: 12 }}>
          {listRow("file", "Create quote", null, () => { setQuoteCustomer(c); setEditingQuote(null); setShowQuote(true); })}
          <div style={{ borderTop: "1px solid " + theme.cardBorder }}>
            {listRow("clock", "Quote history", cnt.quotes, () => { setShowQuoteHistory(!showQuoteHistory); if (!showQuoteHistory) fetchQuoteHistory(c.id); }, showQuoteHistory)}
            {showQuoteHistory && (
              <div style={{ padding: "0 14px 12px" }}>
                {quoteHistory.length === 0 && <div style={Object.assign({}, hint, { textAlign: "center", padding: 8 })}>No quotes yet.</div>}
                {quoteHistory.map((q) => {
                  var qDate = new Date(q.created_at).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
                  var total = q.total || 0;
                  return (
                    <div key={q.id} style={{ padding: "12px 0", borderTop: "1px solid " + theme.cardBorder }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{fmtMoney(total, q.currency || "CAD")}</div>
                          <div style={hint}>{fmtDate(q.created_at)}{q.edited_at && <span> · Edited {fmtDate(q.edited_at)}</span>}</div>
                          {q.notes && <div style={Object.assign({}, hint, { marginTop: 2 })}>{q.notes}</div>}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>{/* PATCH oct18-quotes */}
                          <a
                            aria-label="Send on WhatsApp"
                            title="Send on WhatsApp"
                            href={"https://wa.me/" + waNum(c.phone) + "?text=" + encodeURIComponent(q.whatsapp_message || ("Quote from " + appConfig.companyFullName + "\nDate: " + qDate + "\nTotal: " + fmtMoney(total, q.currency || "CAD")))}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => updateLastContacted(c.id)}
                            style={iconBtn(waC)}
                          >
                            <Icon name="whatsapp" size={20} w={2.5} />
                          </a>
                          <button aria-label="Edit quote" title="Edit quote" onClick={() => { setEditingQuote(q); setQuoteCustomer(c); setShowQuote(true); }} style={iconBtn()}>
                            <Icon name="pencil" size={18} w={2.5} />
                          </button>
                          <button aria-label="Delete quote" title="Delete quote" onClick={() => deleteQuote(q, c.id)} style={iconBtn(cDanger)}>
                            <Icon name="trash" size={18} w={2.5} />
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => setExpandedQuote(expandedQuote === q.id ? null : q.id)}
                        style={{ background: "transparent", border: "none", padding: "8px 0 0", color: cTeal, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                      >
                        {expandedQuote === q.id ? "Hide details" : "View full quote"}
                      </button>
                      {expandedQuote === q.id && (q.whatsapp_message ? (
                        <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: theme.text, whiteSpace: "pre-wrap", lineHeight: 1.6, fontFamily: "inherit", maxHeight: 260, overflowY: "auto", marginTop: 8 }}>
                          {q.whatsapp_message}
                        </div>
                      ) : q.items && (
                        <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: theme.subText, marginTop: 8 }}>
                          {q.items.blinds && q.items.blinds.length > 0 && <div>Blinds: {q.items.blinds.map(r => r.qty + " " + r.type).join(", ")}</div>}
                          {q.items.screens && q.items.screens.length > 0 && <div>Screens: {q.items.screens.map(r => r.qty + " " + r.type).join(", ")}</div>}
                          {q.items.automation && q.items.automation.length > 0 && <div>Automation: {q.items.automation.map(r => r.qty + " " + r.type).join(", ")}</div>}
                          <div style={{ marginTop: 4, fontWeight: 600, color: theme.text }}>{quoteTaxSummary(q)}</div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ borderTop: "1px solid " + theme.cardBorder }}>
            {listRow("ruler", "Measurements", cnt.measurements, () => { setShowMeasurementHistory(!showMeasurementHistory); if (!showMeasurementHistory) fetchMeasurementHistory(c.id); }, showMeasurementHistory)}
            {showMeasurementHistory && (
              <div style={{ padding: "0 14px 12px" }}>
                {measurementHistory.length === 0 && <div style={Object.assign({}, hint, { textAlign: "center", padding: 8 })}>No measurements saved yet.</div>}
                {measurementHistory.map((m) => (
                  <div key={m.id} style={{ padding: "12px 0", borderTop: "1px solid " + theme.cardBorder }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>
                          {(m.total_sqft || 0).toFixed(2)} sq ft · {m.total_windows || 0} window{m.total_windows !== 1 ? "s" : ""}
                        </div>
                        <div style={hint}>{fmtDate(m.created_at)}{m.job_description ? " · " + m.job_description : ""}</div>
                        {m.total_price > 0 && <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{money(m.total_price || 0)}</div>}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <button aria-label="Edit measurements" title="Edit" onClick={() => { setEditingMeasurement(m); setShowMeasurements(true); }} style={iconBtn()}>
                          <Icon name="pencil" size={18} w={2.5} />
                        </button>
                        <button
                          aria-label="Delete measurements"
                          title="Delete"
                          onClick={async () => {
                            if (!window.confirm("Delete this measurement record?")) return;
                            var { error } = await supabase.from("measurements").delete().eq("id", m.id);
                            if (error) { showToast("Error deleting measurement", "error"); return; }
                            showToast("Measurement deleted");
                            fetchMeasurementHistory(c.id);
                            fetchCustCounts(c.id);
                          }}
                          style={iconBtn(cDanger)}
                        >
                          <Icon name="trash" size={18} w={2.5} />
                        </button>
                      </div>
                    </div>
                    {m.rows && m.rows.length > 0 && (
                      <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: "8px 10px", fontSize: 13, color: theme.subText, marginTop: 8 }}>
                        {m.rows.slice(0, 5).map((r, i) => (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                            <span>#{r.num} · W {r.width_whole}{r.width_frac ? " " + r.width_frac : ""}" × H {r.height_whole}{r.height_frac ? " " + r.height_frac : ""}"</span>
                            <span style={{ fontWeight: 600, color: theme.text }}>{(r.sqft || 0).toFixed(2)} sf</span>
                          </div>
                        ))}
                        {m.rows.length > 5 && <div style={{ marginTop: 4 }}>+{m.rows.length - 5} more windows</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ borderTop: "1px solid " + theme.cardBorder }}>
            {listRow("whatsapp", "Send WhatsApp template", null, () => setShowWhatsAppTemplates(!showWhatsAppTemplates), showWhatsAppTemplates, true)}
            {showWhatsAppTemplates && (
              <div style={{ padding: "0 14px 12px" }}>
                {waTemplates.length === 0 && <div style={hint}>No templates yet.</div>}
                {waTemplates.map((t) => {
                  var msg = t.text.split("{name}").join(c.full_name || "there");
                  return (
                    <div key={t.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 0", borderTop: "1px solid " + theme.cardBorder }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{t.label}</div>
                        <div style={Object.assign({}, hint, { whiteSpace: "pre-wrap", marginTop: 2 })}>{msg}</div>
                      </div>
                      <a
                        aria-label={"Send " + t.label}
                        title="Send"
                        href={"https://wa.me/" + waNum(c.phone) + "?text=" + encodeURIComponent(msg)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => updateLastContacted(c.id)}
                        style={iconBtn(waC)}
                      >
                        <Icon name="whatsapp" size={20} w={2.5} />
                      </a>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ borderTop: "1px solid " + theme.cardBorder }}>
            {listRow("download", "Save to phone contacts", null, () => saveToPhone(c))}
          </div>
        </div>

        {/* Schedule job / Add reminder */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button
            onClick={() => { setShowSchedule(!showSchedule); setShowReminder(false); }}
            style={Object.assign({}, btnS, { flex: 1, borderColor: showSchedule ? C.teal : theme.inputBorder })}
          >
            <Icon name="calendar" size={18} /> Schedule job
          </button>
          <button
            onClick={() => { setShowReminder(!showReminder); setShowSchedule(false); }}
            style={Object.assign({}, btnS, { flex: 1, borderColor: showReminder ? C.teal : theme.inputBorder })}
          >
            <Icon name="bell" size={18} /> Add reminder
          </button>
        </div>
        {showSchedule && (
          <div style={card}>
            <h3 style={h3}>Schedule job</h3>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}><div style={lbl}>Date</div><input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} style={inp} /></div>
              <div style={{ flex: 1 }}><div style={lbl}>Time</div><input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} style={inp} /></div>
            </div>
            <div style={lbl}>Price (optional)</div>
            <input type="number" value={schedPrice} onChange={(e) => setSchedPrice(e.target.value)} style={inp} />
            <div style={lbl}>Job notes (optional)</div>
            <textarea value={schedNotes} onChange={(e) => setSchedNotes(e.target.value)} rows={3} style={Object.assign({}, inp, { resize: "vertical", fontFamily: "inherit" })} />
            <div style={lbl}>Also remind me</div>
            {renderOffsetPicker(schedReminderOffsets, setSchedReminderOffsets, schedCustomOffsetValue, setSchedCustomOffsetValue, schedCustomOffsetUnit, setSchedCustomOffsetUnit)}
            <button
              onClick={async () => {
                if (!schedDate || !schedTime) {
                  showToast("Please select date and time", "error");
                  return;
                }
                var scheduledAtISO = new Date(schedDate + "T" + schedTime).toISOString();
                var { error: jobError } = await supabase
                  .from("jobs")
                  .insert([{ contact_id: c.id, scheduled_at: scheduledAtISO, job_type: "Installation", price: parseFloat(schedPrice) || 0, notes: schedNotes, company_id: appConfig.companyId }]);
                if (jobError) {
                  showToast("Error scheduling job", "error");
                  return;
                }
                if (schedReminderOffsets.length > 0) {
                  await createOffsetReminders(c.id, c.full_name, scheduledAtISO, schedReminderOffsets);
                }
                showToast("Job scheduled!");
                setShowSchedule(false);
                setSchedDate("");
                setSchedTime("");
                setSchedPrice("");
                setSchedNotes("");
                setSchedReminderOffsets([]);
                setSchedCustomOffsetValue("");
                fetchJobs(c.id);
                fetchUpcomingJobs();
              }}
              style={Object.assign({}, btnP, { width: "100%" })}
            >
              Schedule job
            </button>
          </div>
        )}
        {showReminder && (
          <div style={card}>
            <h3 style={h3}>Add reminder</h3>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}><div style={lbl}>Date</div><input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} style={inp} /></div>
              <div style={{ flex: 1 }}><div style={lbl}>Time</div><input type="time" value={reminderTime} onChange={(e) => setReminderTime(e.target.value)} style={inp} /></div>
            </div>
            <div style={lbl}>Note</div>
            <input placeholder="e.g. Follow up" value={reminderMessage} onChange={(e) => setReminderMessage(e.target.value)} style={inp} />
            <button
              onClick={() => {
                if (!reminderDate || !reminderTime) {
                  showToast("Please select date and time", "error");
                  return;
                }
                supabase
                  .from("reminders")
                  .insert([{ contact_id: c.id, remind_at: new Date(reminderDate + "T" + reminderTime).toISOString(), message: reminderMessage || "Follow up", done: false, company_id: appConfig.companyId }])
                  .select()
                  .then(({ data: newJobReminder, error: _srErr }) => {
                    if (_srErr) { showToast("Error saving reminder", "error"); return; }
                    showToast("Reminder set!");
                    if (newJobReminder && newJobReminder[0]) scheduleReminderNotification(newJobReminder[0].id, newJobReminder[0].remind_at, "🔔 " + (c.full_name || "Reminder"), reminderMessage || "Follow up");
                    setShowReminder(false);
                    setReminderDate("");
                    setReminderTime("");
                    setReminderMessage("");
                    fetchUpcomingReminders();
                  });
              }}
              style={Object.assign({}, btnP, { width: "100%" })}
            >
              Set reminder
            </button>
          </div>
        )}

        {/* Job status */}
        <div style={card}>
          <h3 style={h3}>Job status</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {SL.map((status) => {
              var ia = normStatus(c.job_status) === status; // PATCH oct21-ui-dash: bug 4
              return (
                <button
                  key={status}
                  onClick={() => updateCustomer(c.id, { job_status: status })}
                  aria-pressed={ia}
                  style={{ minHeight: 40, padding: "0 14px", borderRadius: 99, border: "1px solid " + (ia ? C.teal : theme.inputBorder), background: ia ? C.teal : theme.card, color: ia ? "white" : theme.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Follow up */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={Object.assign({}, h3, { margin: 0 })}>Follow up</h3>
            <button
              onClick={() => { setShowFollowUpHistory(!showFollowUpHistory); if (!showFollowUpHistory) fetchFollowUpHistory(c.id); }}
              style={{ background: "transparent", border: "none", color: cTeal, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", minHeight: 36 }}
            >
              {showFollowUpHistory ? "Hide history" : "History"}
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.keys(FU).map((action) => {
              var s = FU[action];
              var fuOn = lastFU && lastFU.cid === c.id && lastFU.action === action;
              return (
                <button
                  key={action}
                  onClick={async () => {
                    updateLastContacted(c.id);
                    var logEntry = { contact_id: c.id, action, company_id: appConfig.companyId };
                    if (s.statusUpdate) {
                      var { error: _fsErr } = await supabase.from("customers").update({ job_status: s.statusUpdate }).eq("id", c.id);
                      if (_fsErr) { showToast("Error updating status", "error"); return; }
                      logEntry.action = action + " → status: " + s.statusUpdate;
                      setSelectedCustomer({ ...c, job_status: s.statusUpdate });
                      fetchCustomers(search, filterStatus, filterArea);
                      fetchStatusCounts();
                      showToast(action + " logged. Status set to " + s.statusUpdate.charAt(0).toUpperCase() + s.statusUpdate.slice(1));
                    } else {
                      showToast(action + " logged");
                    }
                    var { error: _flErr } = await supabase.from("follow_up_logs").insert([logEntry]); // lint-ignore-company-id — logEntry already includes company_id, built above
                    if (_flErr) showToast("Error saving follow-up", "error"); else setLastFU({ cid: c.id, action: action });
                    if (showFollowUpHistory) fetchFollowUpHistory(c.id);
                  }}
                  aria-pressed={!!fuOn} style={{ minHeight: 40, padding: "0 12px", borderRadius: 99, border: "1px solid " + (fuOn ? C.teal : theme.inputBorder), background: fuOn ? C.teal : theme.card, color: fuOn ? "white" : theme.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6, outline: fuOn ? "none" : undefined }}
                >
                  <span style={{ color: fuOn ? "white" : theme.subText }}><Icon name={fuOn ? "check" : (FU_ICON[action] || "check")} size={16} w={fuOn ? 2.5 : 2} /></span> {action}
                </button>
              );
            })}
          </div>
          <div style={Object.assign({}, hint, { marginTop: 10 })}>Sent quote and Installation done also update the job status.</div>
          {showFollowUpHistory && (
            <div style={{ marginTop: 12, borderTop: "1px solid " + theme.cardBorder, paddingTop: 10 }}>
              {followUpHistory.length === 0 && <div style={hint}>No history yet.</div>}
              {followUpHistory.map((log) => {
                var ak = log.action.split(" → ")[0];
                var hasSC = log.action.includes(" → status:");
                return (
                  <div key={log.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0" }}>
                    <span style={{ color: theme.subText, marginTop: 2 }}><Icon name={FU_ICON[ak] || "check"} size={16} /></span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, color: theme.text, fontWeight: 600 }}>{ak}</div>
                      {hasSC && <div style={{ fontSize: 13, color: theme.subText }}>Status changed to {log.action.split("status: ")[1]}</div>}
                      <div style={hint}>{fmtDateTime(log.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Scheduled jobs */}
        {customerJobs.length > 0 && (
          <div style={card}>
            <h3 style={h3}>Scheduled jobs</h3>
            {customerJobs.map((job, ji) => {
              var isEd = editingJob && editingJob.id === job.id;
              if (isEd)
                return (
                  <div key={job.id} style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 12, marginBottom: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 10 }}>Edit job</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <div style={{ flex: 1 }}><div style={lbl}>Date</div><input type="date" defaultValue={editingJob.date} onChange={(e) => setEditingJob({ ...editingJob, date: e.target.value })} style={inp} /></div>
                      <div style={{ flex: 1 }}><div style={lbl}>Time</div><input type="time" defaultValue={editingJob.time} onChange={(e) => setEditingJob({ ...editingJob, time: e.target.value })} style={inp} /></div>
                    </div>
                    <div style={lbl}>Price</div>
                    <input type="number" defaultValue={editingJob.price} onChange={(e) => setEditingJob({ ...editingJob, price: e.target.value })} style={inp} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => updateJob(job.id, { scheduled_at: new Date(editingJob.date + "T" + editingJob.time).toISOString(), price: parseFloat(editingJob.price) || 0 })}
                        style={Object.assign({}, btnP, { flex: 1 })}
                      >
                        Save
                      </button>
                      <button onClick={() => setEditingJob(null)} style={Object.assign({}, btnS, { flex: 1 })}>Cancel</button>
                      <button aria-label="Delete job" title="Delete job" onClick={() => { if (window.confirm("Delete this job?")) deleteJob(job.id); }} style={iconBtn(cDanger)}>
                        <Icon name="trash" size={18} w={2.5} />
                      </button>
                    </div>
                  </div>
                );
              var dt = new Date(job.scheduled_at);
              var dateStr = localDateStr(dt);
              var timeStr = dt.toTimeString().slice(0, 5);
              return (
                <div key={job.id} style={{ padding: "10px 0", borderTop: ji ? "1px solid " + theme.cardBorder : "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{fmtDateTime(dt)}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                      {job.price > 0 ? (
                        <span style={{ fontSize: 15, color: theme.text }}>{money(job.price)}</span>
                      ) : (
                        <span style={{ fontSize: 12, fontWeight: 600, color: theme.subText, background: theme.sectionBg, border: "1px solid " + theme.cardBorder, padding: "2px 8px", borderRadius: 99 }}>No price</span>
                      )}
                      {job.price > 0 && job.payment_status && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: theme.text, border: "1px solid " + theme.cardBorder, padding: "2px 8px", borderRadius: 99 }}>
                          <span style={{ width: 7, height: 7, borderRadius: 99, background: PS_COLOR[job.payment_status] || theme.mutedText }}></span>
                          {(PS_LABEL[job.payment_status] || job.payment_status).replace(" ✓", "")}
                        </span>
                      )}
                    </div>
                  </div>
                  <button aria-label="Edit job" title="Edit job" onClick={() => setEditingJob({ id: job.id, date: dateStr, time: timeStr, price: job.price || "" })} style={iconBtn()}>
                    <Icon name="pencil" size={18} w={2.5} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Details */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={Object.assign({}, h3, { margin: 0 })}>Details</h3>
            <button onClick={() => setEditMode(!editMode)} style={Object.assign({}, btnS, { minHeight: 36, padding: "0 14px", fontSize: 14 })}>
              {editMode ? "Cancel" : <><Icon name="pencil" size={16} /> Edit</>}
            </button>
          </div>
          {editMode ? (
            <div>
              {[
                ["full_name", "Full name"],
                ["phone", "Phone"],
                ["email", "Email"],
                ["address", "Address"],
                ["area", "City"],
                ["product_interest", "Product interest"],
              ].map(([k, label]) => (
                <div key={k}>
                  <div style={lbl}>{label}</div>
                  <input defaultValue={c[k] || ""} id={"edit_" + k} autoComplete="bp-off" type={k === "phone" ? "tel" : "text"} onInput={() => setDetailsDirty(true)} style={inp} />
                </div>
              ))}
              <div style={lbl}>Customer type</div>
              <select defaultValue={c.customer_type} id="edit_customer_type" onChange={() => setDetailsDirty(true)} style={inp}>
                <option value="lead">New Lead</option>
                <option value="existing">Existing Customer</option>
              </select>
              {!detailsDirty && <div style={hint}>Change a field to save.</div>}
            </div>
          ) : (
            <div>
              {[
                ["Phone", c.phone],
                ["Email", c.email],
                ["Address", c.address],
                ["City", c.area],
                ["Product interest", c.product_interest],
              ].map(([label, val]) =>
                val ? (
                  <div key={label} style={{ marginBottom: 12 }}>
                    <div style={hint}>{label}</div>
                    <div style={{ color: theme.text, fontSize: label === "Phone" ? 18 : 15, fontWeight: label === "Phone" ? 600 : 400 }}>{label === "Phone" ? fp(val) : val}</div>
                  </div>
                ) : null
              )}
              {referredList.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid " + theme.cardBorder }}>
                  <div style={Object.assign({}, hint, { fontWeight: 600, marginBottom: 6 })}>Referred {referredList.length} customer{referredList.length === 1 ? "" : "s"}</div>
                  {referredList.map((r) => (
                    <button key={r.id} onClick={() => { setSelectedCustomer(r); fetchJobs(r.id); }} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", padding: "6px 0", color: cTeal, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                      <Icon name="link" size={16} /> {r.full_name}{r.area ? " · " + r.area : ""}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid " + theme.cardBorder }}>
                <div style={Object.assign({}, hint, { fontWeight: 600, marginBottom: 6 })}>Referred by</div>
                {c.referred_by_name ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: "4px 4px 4px 12px" }}>
                    <button
                      onClick={async () => {
                        var { data } = await supabase.from("customers").select("*").eq("id", c.referred_by_id).single();
                        if (data) {
                          setSelectedCustomer(data);
                          fetchJobs(data.id);
                        }
                      }}
                      style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: cTeal, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left", padding: 0 }}
                    >
                      <Icon name="link" size={16} /> {c.referred_by_name}
                    </button>
                    <button onClick={clearReferral} style={Object.assign({}, btnD, { padding: "0 10px", fontSize: 14 })}>Remove</button>
                  </div>
                ) : (
                  <div style={{ position: "relative" }}>
                    <input autoComplete="bp-off" placeholder="Search to link a referrer..." value={referralSearch} onChange={(e) => searchReferral(e.target.value)} style={Object.assign({}, inp, { marginBottom: 0 })} />
                    {referralResults.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 10, zIndex: 999, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", marginTop: 4, overflow: "hidden" }}>
                        {referralResults.map((r) => (
                          <button key={r.id} onClick={() => setReferral(r)} style={{ width: "100%", padding: "10px 14px", border: "none", borderBottom: "1px solid " + theme.cardBorder, background: "transparent", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", textAlign: "left", fontFamily: "inherit" }}>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{r.full_name}</div>
                              <div style={hint}>{[r.phone, r.area].filter(Boolean).join(" · ")}</div>
                            </div>
                            <span style={{ color: theme.mutedText }}><Icon name="chevronRight" size={18} /></span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Notes & Photos */}
        <div style={card}>
          <h3 style={h3}>Notes & photos</h3>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              placeholder="Add a note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addNote(c.id); }}
              style={Object.assign({}, inp, { flex: 1, marginBottom: 0 })}
            />
            <button onClick={() => addNote(c.id)} disabled={!newNote.trim()} style={Object.assign({}, btnP, { opacity: newNote.trim() ? 1 : 0.5 })}>Add</button>
          </div>
          <label style={Object.assign({}, btnS, { width: "100%", marginBottom: 12, cursor: "pointer" })}>
            <Icon name="camera" size={18} /> Upload photo
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { if (e.target.files[0]) uploadPhoto(c.id, e.target.files[0]); }} />
          </label>
          {c.notes && (
            <div style={{ maxHeight: 460, overflowY: "auto" }}>
              {/* PATCH oct18-notes: each note can be edited or deleted */}
              {parseNotes(c.notes).map((blk, bi) => {
                var bodyLines = blk.body ? blk.body.split("\n") : [];
                var isPhoto = bodyLines.some((ln) => ln.startsWith("http") && ln.includes("customer-photos"));
                var isEditing = editingNote && editingNote.cid === c.id && editingNote.header === blk.header && editingNote.body === blk.body;
                return (
                  <div key={bi} style={{ borderTop: "1px solid " + theme.cardBorder, padding: "10px 0" }}>
                    {blk.header && <div style={Object.assign({}, hint, { marginBottom: 4 })}>{noteHead(blk.header)}</div>}
                    {isEditing ? (
                      <div>
                        <textarea value={editingNote.text} onChange={(e) => setEditingNote({ ...editingNote, text: e.target.value })} rows={3} style={Object.assign({}, inp, { resize: "vertical", fontFamily: "inherit" })} />
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => saveNoteEdit(c.id, blk, editingNote.text)} style={Object.assign({}, btnP, { flex: 1 })}>Save</button>
                          <button onClick={() => setEditingNote(null)} style={Object.assign({}, btnS, { flex: 1 })}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                        <div style={{ flex: 1, minWidth: 0, fontSize: 15, color: theme.text }}>
                          {bodyLines.map((line, li) => {
                            if (line.startsWith("http") && line.includes("customer-photos")) {
                              let pu = line;
                              return (
                                <div key={li} style={{ marginBottom: 8, background: theme.sectionBg, borderRadius: 10, padding: 8, border: "1px solid " + theme.cardBorder }}>
                                  <img src={pu} alt="Customer" style={{ width: "100%", borderRadius: 8, display: "block", marginBottom: 8 }} />
                                  <div style={{ display: "flex", gap: 8 }}>
                                    <a href={pu} target="_blank" rel="noreferrer" style={Object.assign({}, btnS, { flex: 1 })}>View</a>
                                    <button onClick={() => deletePhoto(c.id, pu)} style={Object.assign({}, btnD, { flex: 1 })}>Delete photo</button>
                                  </div>
                                </div>
                              );
                            }
                            if (line.startsWith("http")) {
                              return (
                                <div key={li}>
                                  <a href={line} target="_blank" rel="noreferrer" style={{ color: dirBlue }}>View photo</a>
                                </div>
                              );
                            }
                            return (
                              <div key={li} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {unescNote(line)}
                              </div>
                            );
                          })}
                        </div>
                        {!isPhoto && (
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button aria-label="Edit note" title="Edit note" onClick={() => setEditingNote({ cid: c.id, header: blk.header, body: blk.body, text: unescNote(blk.body) })} style={iconBtn()}>
                              <Icon name="pencil" size={18} w={2.5} />
                            </button>
                            <button aria-label="Delete note" title="Delete note" onClick={() => deleteNote(c.id, blk)} style={iconBtn(cDanger)}>
                              <Icon name="trash" size={18} w={2.5} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button onClick={() => setConfirmDelete(c.id)} style={Object.assign({}, btnD, { width: "100%", marginBottom: 12 })}>
          Delete customer
        </button>

        {editMode && detailsDirty && (
          <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: theme.card, borderTop: "1px solid " + theme.cardBorder, padding: 12, zIndex: 200 }}>
            <div style={{ maxWidth: 568, margin: "0 auto", display: "flex", gap: 8 }}>
              <button onClick={() => setEditMode(false)} style={Object.assign({}, btnS, { flex: 1 })}>Cancel</button>
              <button
                onClick={() =>
                  updateCustomer(c.id, {
                    full_name: document.getElementById("edit_full_name").value,
                    phone: document.getElementById("edit_phone").value,
                    email: document.getElementById("edit_email").value,
                    address: document.getElementById("edit_address").value,
                    area: document.getElementById("edit_area").value,
                    product_interest: document.getElementById("edit_product_interest").value,
                    customer_type: document.getElementById("edit_customer_type").value,
                  })
                }
                style={Object.assign({}, btnP, { flex: 2 })}
              >
                Save changes
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── LANDING SCREEN ──
  // PATCH oct21-ui-home: tile colours
  var tealTint = darkMode ? "#1C2E2C" : "#E6F0EF";
  var tealIcon = darkMode ? "#7CC2BB" : C.teal;
  var waTint = darkMode ? "#16301F" : "#E5F4EA";
  var waIcon = darkMode ? "#4CC38A" : "#1E7A45";
  return (
    <div style={{ fontFamily: "inherit", maxWidth: 600, margin: "0 auto", padding: 16, background: theme.bg, minHeight: "100vh", paddingBottom: 80 }}>
      <Toast />
      <LogoHeader sub={companyCustomerCount + " customers"} />
      {dueReminders.length > 0 && (
        <div style={{ marginBottom: 4 }}>
          {/* PATCH oct21-ui-more: calm reminder cards */}
          {dueReminders.map((r) => (
            <div key={r.id} style={{ ...UI.card, padding: 14, marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ width: 40, height: 40, borderRadius: 12, background: UI.tint, color: UI.tealT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="bell" size={20} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: UI.tealT }}>{r.contact_id ? "Reminder due" : "Personal reminder"}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{r.contact_id ? r.customers?.full_name : r.message}</div>
                {r.contact_id && r.message && <div style={{ fontSize: 15, color: theme.subText }}>{r.message}</div>}
                <div style={UI.hint}>{fmtDateTime(r.remind_at)}</div>
              </div>
              <button onClick={() => dismissReminder(r.id)} style={{ ...UI.btnP, flexShrink: 0 }}><Icon name="check" size={18} /> Done</button>
            </div>
          ))}
        </div>
      )}
      {showWaReminders && waReminders.length > 0 && (
        <div style={{ ...UI.card, padding: 14 }}>
          {boxHead("Jobs tomorrow: send a reminder?", () => setShowWaReminders(false))}
          {waReminders.map((job) => {
            var name = job.customers?.full_name || "Customer";
            var phone = job.customers?.phone || "";
            var time = new Date(job.scheduled_at).toLocaleString("en-CA", { hour: "2-digit", minute: "2-digit" });
            var msg =
              "Hi " +
              name +
              ", just a reminder that your window blinds installation is scheduled for tomorrow at " +
              time +
              ". Please let us know if you have any questions. Thank you! " + appConfig.reminderSignature;
            return (
              <div key={job.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid " + theme.cardBorder }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{name}</div>
                  <div style={UI.hint}>Tomorrow · {fmtTime(job.scheduled_at)}</div>
                </div>
                <a
                  aria-label={"Send reminder to " + name}
                  title="Send reminder"
                  href={"https://wa.me/" + waNum(phone) + "?text=" + encodeURIComponent(msg)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => updateLastContacted(job.contact_id)}
                  style={UI.iconBtn(UI.wa, UI.waTint)}
                >
                  <Icon name="whatsapp" size={20} w={2.5} />
                </a>
              </div>
            );
          })}
        </div>
      )}

      {/* Icon Grid - 4 rows × 3 cols */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
        {[
          {
            icon: "search",
            label: "Search",
            active: showCustomerList,
            action: () => {
              setShowCustomerList(!showCustomerList);
              setTimeout(() => {
                if (searchInputRef.current) searchInputRef.current.focus();
              }, 100);
            },
          },
          { icon: "userPlus", label: "New Customer", active: showAddForm, action: () => setShowAddForm(!showAddForm) },
          { icon: "phone", label: "Recent Calls", active: showRecentCalls, action: () => setShowRecentCalls(!showRecentCalls) },
          {
            icon: "bell",
            label: "Upcoming",
            badge: upcomingJobs.length + upcomingReminders.filter(r => r.contact_id).length,
            active: showUpcoming,
            action: () => {
              setShowUpcoming(!showUpcoming);
              fetchUpcomingJobs();
              fetchUpcomingReminders();
            },
          },
          { icon: "clock", label: "Reminders", badge: personalReminders.length, active: showPersonalReminder, action: () => setShowPersonalReminder(!showPersonalReminder) },
          { icon: "megaphone", wa: true, label: "WA Blast", active: showBlast, action: () => setShowBlast(!showBlast) },
          { icon: "chat", label: "Team Chat", badge: unreadCount, active: currentScreen === "chat", action: () => setCurrentScreen("chat") },
          {
            icon: "file",
            label: "New Quote",
            active: showQuote,
            action: () => {
              setQuoteCustomer(null);
              setEditingQuote(null);
              setShowQuote(true);
              window.history.pushState({ page: "quote" }, "");
            },
          },
          {
            icon: "ruler",
            label: "Measurements",
            active: showMeasurements,
            action: () => {
              setShowMeasurements(true);
              window.history.pushState({ page: "measurements" }, "");
            },
          },
          {
            icon: "map",
            label: "Route Today",
            active: currentScreen === "route",
            action: () => {
              setCurrentScreen("route");
              fetchRouteJobs();
            },
          },

          {
            icon: "whatsapp",
            wa: true,
            label: "WA Templates",
            active: showWaTemplatesHome,
            action: () => setShowWaTemplatesHome(!showWaTemplatesHome),
          },
          {
            icon: "users",
            label: "Contacts",
            active: showManageContacts,
            action: () => setShowManageContacts(!showManageContacts),
          },
        ].map((btn, i) => (
          <button
            key={i}
            onClick={btn.action}
            style={{
              background: theme.card,
              border: (btn.active ? "1.5px solid " + C.teal : "1px solid " + theme.cardBorder),
              borderRadius: 14,
              padding: "14px 6px 12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <div style={{ position: "relative", width: 40, height: 40, borderRadius: 12, background: btn.wa ? waTint : tealTint, color: btn.wa ? waIcon : tealIcon, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name={btn.icon} size={22} />
              {btn.badge > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -7,
                    right: -9,
                    background: C.teal,
                    color: "white",
                    border: "2px solid " + theme.card,
                    borderRadius: 99,
                    fontSize: 11,
                    fontWeight: 700,
                    minWidth: 18,
                    height: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 4px",
                  }}
                >
                  {btn.badge}
                </span>
              )}
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: theme.text, textAlign: "center", lineHeight: 1.2 }}>{btn.label}</span>
          </button>
        ))}
      </div>

      {/* PATCH oct21-ui-home: white status pills with a coloured dot; the chosen one is teal */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        {SL.map((s) => {
          if (!(statusCounts[s] > 0)) return null;
          var on = filterStatus === s;
          return (
            <button
              key={s}
              onClick={() => {
                setFilterStatus(s);
                setShowCustomerList(true);
                fetchCustomers(search, s, filterArea);
              }}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, background: on ? C.teal : theme.card, color: on ? "white" : theme.text, border: "1px solid " + (on ? C.teal : theme.cardBorder), padding: "7px 12px", borderRadius: 99, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 99, background: on ? "white" : SC[s] }}></span>
              {s.charAt(0).toUpperCase() + s.slice(1)} {statusCounts[s]}
            </button>
          );
        })}
        {filterStatus !== "all" && (
          <button
            onClick={() => {
              setFilterStatus("all");
              fetchCustomers(search, "all", filterArea);
            }}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, background: theme.sectionBg, color: theme.subText, border: "1px solid " + theme.cardBorder, padding: "7px 12px", borderRadius: 99, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
          >
            <Icon name="x" size={14} /> Clear
          </button>
        )}
      </div>

      {showRecentCalls && (
        <div style={UI.card}>
          {/* PATCH oct21-ui-more */}
          {boxHead("Recent calls", () => setShowRecentCalls(false))}
          {recentCallsList.length === 0 && <div style={UI.hint}>No recent calls yet.</div>}
          {[...recentCallsList]
            .sort((a, b) => {
              if (!a.last_contacted) return 1;
              if (!b.last_contacted) return -1;
              return new Date(b.last_contacted) - new Date(a.last_contacted);
            })
            .slice(0, 50)
            .map((cu, ci) => {
              var daysAgo = cu.last_contacted ? Math.floor((Date.now() - new Date(cu.last_contacted)) / 86400000) : null;
              var when = !cu.last_contacted ? "Never contacted" : daysAgo === 0 ? "Today · " + fmtTime(cu.last_contacted) : daysAgo === 1 ? "Yesterday · " + fmtTime(cu.last_contacted) : fmtDateTime(cu.last_contacted);
              return (
                <div
                  key={cu.id}
                  onClick={() => {
                    setSelectedCustomer(cu);
                    fetchJobs(cu.id);
                  }}
                  style={{ padding: "10px 0", borderTop: ci ? "1px solid " + theme.cardBorder : "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{cu.full_name}</div>
                    {cu.phone && <div style={{ fontSize: 15, color: theme.text }}>{fp(cu.phone)}</div>}
                    <div style={UI.hint}>{when}</div>
                  </div>
                  {cu.phone && (
                    <>
                      <button aria-label={"Call " + cu.full_name} onClick={(e) => { e.stopPropagation(); makeCall(cu.phone); updateLastContacted(cu.id); }} style={UI.round(C.teal)}>
                        <Icon name="phone" size={18} />
                      </button>
                      <a aria-label={"WhatsApp " + cu.full_name} href={"https://wa.me/" + waNum(cu.phone)} target="_blank" rel="noreferrer" onClick={(e) => { e.stopPropagation(); updateLastContacted(cu.id); }} style={UI.round("#1E7A45")}>
                        <Icon name="whatsapp" size={18} />
                      </a>
                    </>
                  )}
                </div>
              );
            })}
        </div>
      )}
      {showUpcoming && (
        <div style={UI.card}>
          {/* PATCH oct21-ui-more */}
          {boxHead("Upcoming", () => { setShowUpcoming(false); setEditingHomeJob(null); setEditingHomeReminder(null); })}
          {(() => {
            var combinedUpcoming = [
              ...upcomingJobs.map((j) => ({ _kind: "job", _at: j.scheduled_at, data: j })),
              ...upcomingReminders.filter((r) => r.contact_id).map((r) => ({ _kind: "reminder", _at: r.remind_at, data: r })),
            ].sort((a, b) => new Date(a._at) - new Date(b._at));
            if (combinedUpcoming.length === 0) return <p style={{ ...UI.hint, margin: 0 }}>Nothing upcoming</p>;
            return combinedUpcoming.map((item, ui) => {
              var rowWrap = { borderTop: ui ? "1px solid " + theme.cardBorder : "none", padding: "10px 0" };
              if (item._kind === "job") {
                var job = item.data;
                var isEditingThisJob = editingHomeJob && editingHomeJob.id === job.id;
                var jdt = new Date(job.scheduled_at);
                var openJob = () => setEditingHomeJob({ id: job.id, date: localDateStr(jdt), time: jdt.toTimeString().slice(0, 5), price: job.price || "", notes: job.notes || "" });
                return (
                  <div key={"job-" + job.id} style={rowWrap}>
                    {isEditingThisJob ? (
                      <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 12 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 10 }}>Edit job: {job.customers?.full_name}</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <div style={{ flex: 1 }}><div style={UI.lbl}>Date</div><input type="date" value={editingHomeJob.date} onChange={(e) => setEditingHomeJob({ ...editingHomeJob, date: e.target.value })} style={UI.inp} /></div>
                          <div style={{ flex: 1 }}><div style={UI.lbl}>Time</div><input type="time" value={editingHomeJob.time} onChange={(e) => setEditingHomeJob({ ...editingHomeJob, time: e.target.value })} style={UI.inp} /></div>
                        </div>
                        <div style={UI.lbl}>Price</div>
                        <input type="number" value={editingHomeJob.price} onChange={(e) => setEditingHomeJob({ ...editingHomeJob, price: e.target.value })} style={UI.inp} />
                        <div style={UI.lbl}>Notes</div>
                        <textarea value={editingHomeJob.notes || ""} onChange={(e) => setEditingHomeJob({ ...editingHomeJob, notes: e.target.value })} rows={2} style={{ ...UI.inp, resize: "vertical" }} />
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={async () => {
                            var { error } = await supabase.from("jobs").update({ scheduled_at: new Date(editingHomeJob.date + "T" + editingHomeJob.time).toISOString(), price: parseFloat(editingHomeJob.price) || 0, notes: editingHomeJob.notes || "" }).eq("id", job.id);
                            if (!error) { showToast("Job updated!"); setEditingHomeJob(null); fetchUpcomingJobs(); }
                            else showToast("Error updating job", "error");
                          }} style={{ ...UI.btnP, flex: 1 }}>Save</button>
                          <button onClick={() => setEditingHomeJob(null)} style={{ ...UI.btnS, flex: 1 }}>Cancel</button>
                          <button aria-label="Delete job" title="Delete job" onClick={async () => {
                            if (!window.confirm("Delete this job?")) return; var { error: _hjErr } = await supabase.from("jobs").delete().eq("id", job.id); if (_hjErr) { showToast("Error deleting job", "error"); return; } // PATCH oct16-fixes (#1, #2)
                            setEditingHomeJob(null); fetchUpcomingJobs(); showToast("Job deleted");
                          }} style={UI.iconBtn(UI.danger)}><Icon name="trash" size={18} w={2.5} /></button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={openJob}>
                          <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{job.customers?.full_name}</div>
                          <div style={UI.hint}>Job · {fmtDateTime(jdt)}</div>
                          {job.price > 0 && <div style={{ fontSize: 15, color: theme.text }}>{money(job.price)}</div>}
                          {job.notes && <div style={{ fontSize: 13, color: theme.subText }}>{job.notes}</div>}
                        </div>
                        <button aria-label="Add reminders for this job" title="Add reminders" onClick={() => { setAddingReminderJobId(addingReminderJobId === job.id ? null : job.id); setAddReminderOffsets([]); }} style={UI.iconBtn()}><Icon name="bell" size={18} w={2.5} /></button>
                        <button aria-label="Edit job" title="Edit job" onClick={openJob} style={UI.iconBtn()}><Icon name="pencil" size={18} w={2.5} /></button>
                        <button aria-label="Mark job done" title="Mark done" onClick={async () => { var { error: _jdErr } = await supabase.from("jobs").update({ completed: true }).eq("id", job.id); if (_jdErr) { showToast("Error updating job", "error"); return; } fetchUpcomingJobs(); showToast("Job marked done!"); }} style={UI.iconBtn(UI.green)}><Icon name="check" size={20} w={2.5} /></button>
                      </div>
                    )}
                    {addingReminderJobId === job.id && (
                      <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 12, marginTop: 8 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 10 }}>Add reminders for this job</div>
                        {renderOffsetPicker(addReminderOffsets, setAddReminderOffsets, addReminderCustomValue, setAddReminderCustomValue, addReminderCustomUnit, setAddReminderCustomUnit)}
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={async () => {
                            if (addReminderOffsets.length === 0) { showToast("Pick at least one option", "error"); return; }
                            await createOffsetReminders(job.contact_id, job.customers?.full_name, job.scheduled_at, addReminderOffsets);
                            showToast("Reminder(s) added!");
                            setAddingReminderJobId(null);
                            setAddReminderOffsets([]);
                          }} style={{ ...UI.btnP, flex: 1 }}>Add</button>
                          <button onClick={() => { setAddingReminderJobId(null); setAddReminderOffsets([]); }} style={{ ...UI.btnS, flex: 1 }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              var r = item.data;
              var isEditingThisReminder = editingHomeReminder && editingHomeReminder.id === r.id;
              var rdt = new Date(r.remind_at);
              var openRem = () => setEditingHomeReminder({ id: r.id, date: localDateStr(rdt), time: rdt.toTimeString().slice(0, 5), message: r.message || "" });
              return (
                <div key={"reminder-" + r.id} style={rowWrap}>
                  {isEditingThisReminder ? (
                    renderReminderEditForm(r, { title: "Edit reminder: " + (r.customers?.full_name || ""), messagePlaceholder: "Message...", messageFirst: false, isPersonal: false })
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={openRem}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{r.customers?.full_name}</div>
                        <div style={UI.hint}>Reminder · {fmtDateTime(rdt)}</div>
                        {r.message && <div style={{ fontSize: 13, color: theme.subText }}>{r.message}</div>}
                      </div>
                      <button aria-label="Edit reminder" title="Edit reminder" onClick={openRem} style={UI.iconBtn()}><Icon name="pencil" size={18} w={2.5} /></button>
                      <button aria-label="Mark reminder done" title="Done" onClick={async () => { var { error: _rdErr } = await supabase.from("reminders").update({ done: true }).eq("id", r.id); if (_rdErr) { showToast("Error updating reminder", "error"); return; } cancelReminderNotification(r.id); fetchUpcomingReminders(); showToast("Reminder done!"); }} style={UI.iconBtn(UI.green)}><Icon name="check" size={20} w={2.5} /></button>
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}
      {showPersonalReminder && (
        <div style={UI.card}>
          {/* PATCH oct21-ui-more */}
          {boxHead("Reminders", () => { setShowPersonalReminder(false); setShowPersonalReminderForm(false); })}
          {personalReminders.length === 0 && !showPersonalReminderForm && <p style={{ ...UI.hint, margin: "0 0 8px" }}>No personal reminders</p>}
          {personalReminders.map((r, pi) => {
            var isEditingThisPersonal = editingHomeReminder && editingHomeReminder.id === r.id;
            var prdt = new Date(r.remind_at);
            var openP = () => setEditingHomeReminder({ id: r.id, date: localDateStr(prdt), time: prdt.toTimeString().slice(0, 5), message: r.message || "" });
            return (
              <div key={r.id} style={{ borderTop: pi ? "1px solid " + theme.cardBorder : "none", padding: "10px 0" }}>
                {isEditingThisPersonal ? (
                  renderReminderEditForm(r, { title: "Edit reminder", messagePlaceholder: "What to remember?", messageFirst: true, isPersonal: true })
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={openP}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{r.message}</div>
                      <div style={UI.hint}>{fmtDateTime(prdt)}</div>
                    </div>
                    <button aria-label="Edit reminder" title="Edit reminder" onClick={openP} style={UI.iconBtn()}><Icon name="pencil" size={18} w={2.5} /></button>
                    <button aria-label="Mark reminder done" title="Done" onClick={async () => { var { error: _rdErr } = await supabase.from("reminders").update({ done: true }).eq("id", r.id); if (_rdErr) { showToast("Error updating reminder", "error"); return; } cancelReminderNotification(r.id); setPersonalReminders(prev => prev.filter(x => x.id !== r.id)); fetchUpcomingReminders(); showToast("Reminder dismissed"); }} style={UI.iconBtn(UI.green)}><Icon name="check" size={20} w={2.5} /></button>
                  </div>
                )}
              </div>
            );
          })}
          {showPersonalReminderForm ? (
            <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 12, marginTop: 8 }}>
              <div style={UI.lbl}>What to remember</div>
              <input placeholder="e.g. Order stock" value={personalReminderMsg} onChange={(e) => setPersonalReminderMsg(e.target.value)} onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)} style={UI.inp} />
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}><div style={UI.lbl}>Date</div><input type="date" value={personalReminderDate} onChange={(e) => setPersonalReminderDate(e.target.value)} style={UI.inp} /></div>
                <div style={{ flex: 1 }}><div style={UI.lbl}>Time</div><input type="time" value={personalReminderTime} onChange={(e) => setPersonalReminderTime(e.target.value)} style={UI.inp} /></div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={addPersonalReminder} style={{ ...UI.btnP, flex: 1 }}>Set reminder</button>
                <button onClick={() => setShowPersonalReminderForm(false)} style={{ ...UI.btnS, flex: 1 }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowPersonalReminderForm(true)} style={{ ...UI.btnP, width: "100%", marginTop: 8 }}>
              + Add reminder
            </button>
          )}
        </div>
      )}
      {showAddForm && (
        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 14, padding: 16, marginBottom: 12 }}>
          {/* PATCH oct21-ui-lists: labels above fields, City with suggestions */}
          {(() => {
            var lbl = { display: "block", fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 6 };
            var inp = { ...IS, minHeight: 44, borderRadius: 10, fontSize: 15, marginBottom: 12, padding: "10px 12px", fontFamily: "inherit" };
            var cities = allAreas.filter((x) => x && x !== "all");
            var setF = (k, v) => setNewCustomer({ ...newCustomer, [k]: v });
            return (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.text }}>New customer</h3>
                  <button aria-label="Close" onClick={() => setShowAddForm(false)} style={{ width: 44, height: 44, background: "transparent", border: "none", color: theme.subText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name="x" size={20} />
                  </button>
                </div>
                {[
                  ["full_name", "Full name", "text", ""],
                  ["phone", "Phone", "tel", ""],
                  ["email", "Email", "email", ""],
                  ["address", "Address", "text", "e.g. 123 Main St"],
                ].map(([k, label, type, ph]) => (
                  <div key={k}>
                    <label htmlFor={"nc_" + k} style={lbl}>{label}</label>
                    <input id={"nc_" + k} autoComplete="bp-off" placeholder={ph} type={type} value={newCustomer[k]} onChange={(e) => setF(k, e.target.value)} style={inp} />
                  </div>
                ))}
                <label htmlFor="nc_area" style={lbl}>City</label>
                <input id="nc_area" list="bp-city-list" placeholder="e.g. Surrey" autoComplete="bp-off" value={newCustomer.area} onChange={(e) => setF("area", e.target.value)} style={inp} />
                <datalist id="bp-city-list">
                  {cities.map((ct) => <option key={ct} value={ct} />)}
                </datalist>
                <label htmlFor="nc_product_interest" style={lbl}>Product interest</label>
                <input id="nc_product_interest" autoComplete="bp-off" placeholder="e.g. Zebra blinds" value={newCustomer.product_interest} onChange={(e) => setF("product_interest", e.target.value)} style={inp} />
                <div style={lbl}>Customer type</div>
                <div style={{ display: "flex", padding: 3, background: theme.sectionBg, border: "1px solid " + theme.inputBorder, borderRadius: 10, marginBottom: 12 }}>
                  {[["lead", "New lead"], ["existing", "Existing"]].map(([v, t]) => (
                    <button key={v} onClick={() => setF("customer_type", v)} aria-pressed={newCustomer.customer_type === v}
                      style={{ flex: 1, minHeight: 38, borderRadius: 8, border: "none", background: newCustomer.customer_type === v ? C.teal : "transparent", color: newCustomer.customer_type === v ? "white" : theme.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                      {t}
                    </button>
                  ))}
                </div>
                <label htmlFor="nc_notes" style={lbl}>Notes</label>
                <textarea id="nc_notes" autoComplete="bp-off" value={newCustomer.notes} onChange={(e) => setF("notes", e.target.value)} style={{ ...inp, height: 80, resize: "vertical" }} />
                <button onClick={addCustomer}
                  style={{ width: "100%", minHeight: 44, background: C.teal, color: "white", border: "1px solid " + C.teal, borderRadius: 10, fontSize: 15, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>
                  Save customer
                </button>
              </>
            );
          })()}
        </div>
      )}

      {showBlast && (
        <div style={UI.card}>
          {/* PATCH oct21-ui-more: list scrolls with the page, bigger checkboxes */}
          {boxHead("WhatsApp blast", () => setShowBlast(false))}
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <select aria-label="Status" value={blastStatus} onChange={(e) => setBlastStatus(e.target.value)} style={{ ...UI.inp, flex: 1, minWidth: 0, marginBottom: 0 }}>
              <option value="all">All Statuses</option>
              {SL.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
              <option value="existing">Existing</option>
            </select>
            <select aria-label="City" value={blastArea} onChange={(e) => setBlastArea(e.target.value)} style={{ ...UI.inp, flex: 1, minWidth: 0, marginBottom: 0 }}>
              {allAreas.map((a) => (
                <option key={a} value={a}>{a === "all" ? "All Cities" : a}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44, background: theme.inputBg, border: "1px solid " + theme.inputBorder, borderRadius: 10, padding: "0 4px 0 12px", marginBottom: 10, boxSizing: "border-box" }}>
            <span style={{ color: theme.mutedText }}><Icon name="search" size={18} /></span>
            <input aria-label="Search by name" autoComplete="bp-off" placeholder="Search by name" value={blastSearch} onChange={(e) => setBlastSearch(e.target.value)}
              style={{ flex: 1, minWidth: 0, minHeight: 42, border: "none", outline: "none", fontSize: 16, background: "transparent", color: theme.text, fontFamily: "inherit" }} />
            {blastSearch !== "" && (
              <button aria-label="Clear search" onClick={() => setBlastSearch("")} style={{ width: 36, height: 36, background: "transparent", border: "none", color: theme.mutedText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
          {blastSelected.length > 0 && (
            <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: "10px 12px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 13, color: theme.subText, flex: 1 }}>
                <b style={{ color: theme.text }}>{blastSelected.length} selected:</b>{" "}
                {blastSelected.slice(0, 10).map((p) => blastNameMap.current[p] || p).join(", ")}
                {blastSelected.length > 10 ? " +" + (blastSelected.length - 10) + " more" : ""}
              </div>
              <button onClick={() => setBlastSelected([])} style={{ background: "transparent", border: "none", color: UI.tealT, cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "inherit", minHeight: 36, flexShrink: 0 }}>Clear</button>
            </div>
          )}
          {(() => {
            // v103b: results come from the database (all customers), see blast effect
            var filtered = blastResults;
            return (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={UI.hint}>{filtered.length} customers · {blastSelected.length} selected</span>
                  <button
                    onClick={() => {
                      if (blastSelected.length === filtered.length) setBlastSelected([]);
                      else setBlastSelected(filtered.map((cu) => cu.phone));
                    }}
                    style={{ ...UI.btnS, minHeight: 36, padding: "0 12px", fontSize: 14 }}
                  >
                    {blastSelected.length === filtered.length ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div style={{ marginBottom: 12, border: "1px solid " + theme.cardBorder, borderRadius: 10, overflow: "hidden" }}>
                  {filtered.slice(0, 300).map((cu, bi) => {
                    var on = blastSelected.includes(cu.phone);
                    return (
                      <div
                        key={cu.id}
                        role="checkbox"
                        aria-checked={on}
                        tabIndex={0}
                        onMouseDown={(e) => e.preventDefault()}
                        onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); e.currentTarget.click(); } }}
                        onClick={() => {
                          if (on) setBlastSelected(blastSelected.filter((p) => p !== cu.phone));
                          else setBlastSelected([...blastSelected, cu.phone]);
                        }}
                        style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 52, padding: "6px 12px", borderTop: bi ? "1px solid " + theme.cardBorder : "none", cursor: "pointer", background: on ? UI.tint : "transparent" }}
                      >
                        <div style={{ width: 24, height: 24, borderRadius: 6, border: "2px solid " + (on ? C.teal : theme.inputBorder), background: on ? C.teal : "transparent", color: "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxSizing: "border-box" }}>
                          {on && <Icon name="check" size={16} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{cu.full_name}</div>
                          <div style={UI.hint}>{[fp(cu.phone), cu.area].filter(Boolean).join(" · ")}</div>
                        </div>
                      </div>
                    );
                  })}
                  {filtered.length > 300 && <div style={{ padding: 10, ...UI.hint, textAlign: "center", borderTop: "1px solid " + theme.cardBorder }}>Showing 300 of {filtered.length}. Search or filter to narrow. Select all includes all {filtered.length}.</div>}
                  {filtered.length === 0 && <div style={{ padding: 12, fontSize: 15, color: theme.subText, textAlign: "center" }}>No customers found</div>}
                </div>
              </div>
            );
          })()}
          <div style={UI.lbl}>Message</div>
          {waTemplates.length > 0 && (
            <select
              aria-label="Pick a template"
              value={blastTemplate}
              onChange={(e) => {
                setBlastTemplate(e.target.value);
                if (e.target.value) setBlastMessage(waTemplates.find((t) => t.id === e.target.value)?.text || "");
              }}
              style={{ ...UI.inp, marginBottom: 8 }}
            >
              <option value="">Pick a template</option>
              {waTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          )}
          <textarea aria-label="Message" placeholder="Type your message here..." value={blastMessage} onChange={(e) => setBlastMessage(e.target.value)} style={{ ...UI.inp, height: 90, resize: "vertical" }} />
          {blastSelected.length > 0 && (
            <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 6 }}>How to send a WhatsApp broadcast</div>
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: theme.subText, lineHeight: 1.6 }}>
                <li>Tap Copy numbers below.</li>
                <li>Open WhatsApp, then New broadcast.</li>
                <li>Paste the numbers and add them.</li>
                <li>Type or paste your message and send.</li>
                <li>Each customer gets it as a personal message.</li>
              </ol>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => {
                if (blastSelected.length === 0) {
                  showToast("Select at least one customer", "error");
                  return;
                }
                var numbers = blastSelected.join(", ");
                navigator.clipboard
                  .writeText(numbers)
                  .then(() => showToast(blastSelected.length + " numbers copied!"))
                  .catch(() => showToast("Copy failed, please copy manually", "error"));
              }}
              style={{ ...UI.btnP, flex: 1 }}
            >
              Copy {blastSelected.length} numbers
            </button>
            {blastMessage && blastSelected.length > 0 && (
              <button
                onClick={() => {
                  navigator.clipboard
                    .writeText(blastMessage)
                    .then(() => showToast("Message copied!"))
                    .catch(() => {});
                }}
                style={{ ...UI.btnS, flex: 1 }}
              >
                Copy message
              </button>
            )}
          </div>
        </div>
      )}

      {showWaTemplatesHome && (
        <div style={UI.card}>
          {/* PATCH oct21-ui-more: + New template opens the form, pencil and trash on each template */}
          {boxHead("WhatsApp templates", () => { setShowWaTemplatesHome(false); setShowTplForm(false); setEditingTemplate(null); })}
          {showTplForm ? (
            <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 12, marginBottom: 12 }}>
              <div style={UI.lbl}>Name</div>
              <input placeholder="e.g. Thank you" value={newTemplateLabel} onChange={(e) => setNewTemplateLabel(e.target.value)} style={UI.inp} />
              <div style={UI.lbl}>Message</div>
              <textarea placeholder="Hi {name}, thank you for choosing us." value={newTemplateText} onChange={(e) => setNewTemplateText(e.target.value)} style={{ ...UI.inp, height: 90, resize: "vertical", marginBottom: 6 }} />
              <div style={{ ...UI.hint, marginBottom: 12 }}>Type {"{name}"} where the customer's name should go.</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={addWaTemplate} style={{ ...UI.btnP, flex: 1 }}>Save template</button>
                <button onClick={() => { setShowTplForm(false); setNewTemplateLabel(""); setNewTemplateText(""); }} style={{ ...UI.btnS, flex: 1 }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => { setShowTplForm(true); setEditingTemplate(null); }} style={{ ...UI.btnP, width: "100%", marginBottom: 12 }}>+ New template</button>
          )}
          {waTemplates.length === 0 && <div style={UI.hint}>No templates yet.</div>}
          {waTemplates.map((t, ti) => {
            var isEd = editingTemplate && editingTemplate.id === t.id;
            var openT = () => { setEditingTemplate({ id: t.id, label: t.label, text: t.text }); setShowTplForm(false); };
            return (
              <div key={t.id} style={{ borderTop: ti ? "1px solid " + theme.cardBorder : "none", padding: "10px 0" }}>
                {isEd ? (
                  <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 12 }}>
                    <div style={UI.lbl}>Name</div>
                    <input value={editingTemplate.label} onChange={(e) => setEditingTemplate({ ...editingTemplate, label: e.target.value })} style={UI.inp} />
                    <div style={UI.lbl}>Message</div>
                    <textarea value={editingTemplate.text} onChange={(e) => setEditingTemplate({ ...editingTemplate, text: e.target.value })} style={{ ...UI.inp, height: 90, resize: "vertical" }} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => updateWaTemplate(editingTemplate.id, editingTemplate.label, editingTemplate.text)} style={{ ...UI.btnP, flex: 1 }}>Save</button>
                      <button onClick={() => setEditingTemplate(null)} style={{ ...UI.btnS, flex: 1 }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button onClick={openT} style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", textAlign: "left", cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{t.label}</div>
                      <div style={{ ...UI.hint, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{t.text}</div>
                    </button>
                    <button aria-label={"Edit " + t.label} title="Edit" onClick={openT} style={UI.iconBtn()}><Icon name="pencil" size={18} w={2.5} /></button>
                    <button aria-label={"Delete " + t.label} title="Delete" onClick={() => deleteWaTemplate(t.id)} style={UI.iconBtn(UI.danger)}><Icon name="trash" size={18} w={2.5} /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {showManageContacts && (
        <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 14, padding: 16, marginBottom: 12 }}>
          {/* PATCH oct21-ui-lists: three aligned buttons, info inside Import from Phone */}
          {(() => {
            var rowBtn = { width: "100%", minHeight: 52, display: "flex", alignItems: "center", gap: 12, padding: "0 14px", borderRadius: 10, border: "1px solid " + theme.inputBorder, background: theme.card, color: theme.text, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left", marginBottom: 8, boxSizing: "border-box" };
            var ic = { width: 24, display: "flex", justifyContent: "center", color: tealIcon };
            return (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: theme.text }}>Contacts</h3>
                  <button aria-label="Close" onClick={() => setShowManageContacts(false)} style={{ width: 44, height: 44, background: "transparent", border: "none", color: theme.subText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name="x" size={20} />
                  </button>
                </div>
                <button onClick={() => setCurrentScreen("csv")} style={rowBtn}>
                  <span style={ic}><Icon name="download" size={20} /></span>
                  <span style={{ flex: 1 }}>Import from CSV</span>
                </button>
                <div style={{ position: "relative", marginBottom: 8 }}>
                  <button onClick={() => {
              if (!window.AndroidContacts) { showToast("Only available on Android app", "error"); return; }
              setShowManageContacts(false);
              setPhoneImportLoading(true);
              setCurrentScreen("phoneimport");
              window.__phoneContactsDone = async (contacts) => {
                delete window.__phoneContactsDone;
                var list = contacts || [];
                // v103b: page through all seen rows (1000 row cap)
                var { data: seenRows } = await fetchAllRows(() => supabase
                  .from("phone_import_seen")
                  .select("phone_digits")
                  .eq("company_id", appConfig.companyId)
                  .order("phone_digits", { ascending: true }));
                var seenSet = new Set((seenRows || []).map((r) => r.phone_digits));
                var hiddenCount = list.filter((c) => seenSet.has(normalizePhone(c.phone))).length;
                var newRows = list
                  .map((c) => normalizePhone(c.phone))
                  .filter((d) => d && !seenSet.has(d))
                  .filter((d, i, arr) => arr.indexOf(d) === i)
                  .map((d) => ({ company_id: appConfig.companyId, phone_digits: d }));
                if (newRows.length > 0) {
                  await supabase.from("phone_import_seen").upsert(newRows, { onConflict: "company_id,phone_digits", ignoreDuplicates: true });
                }
                setPhoneImportContacts(list);
                setPhoneImportSeenSet(seenSet);
                setPhoneImportLoading(false);
                if (hiddenCount > 0) showToast(hiddenCount + " previously reviewed contacts hidden (search by name to find them)");
              };
              window.AndroidContacts.getPhoneContacts();
            }} style={{ ...rowBtn, marginBottom: 0, paddingRight: 56 }}>
                    <span style={ic}><Icon name="smartphone" size={20} /></span>
                    <span style={{ flex: 1 }}>Import from Phone</span>
                  </button>
                  <button aria-label="How importing works" aria-expanded={showImportPhoneInfo} onClick={() => setShowImportPhoneInfo(!showImportPhoneInfo)}
                    style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", width: 44, height: 44, borderRadius: 8, border: "none", background: showImportPhoneInfo ? tealTint : "transparent", color: tealIcon, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name="info" size={20} />
                  </button>
                </div>
                {showImportPhoneInfo && (
                  <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: 12, marginBottom: 8, fontSize: 13, color: theme.subText, lineHeight: 1.5 }}>
                    New contacts should sync to a Google account (not just "Phone" storage) before importing. This can take a minute after saving on your phone. Contacts you've already reviewed are hidden automatically next time; search by name if you need to find one you skipped before. Importing a phone number that's already a customer is automatically skipped, so it's safe to import more than once.
                  </div>
                )}
                <button onClick={exportToCSV} style={{ ...rowBtn, marginBottom: 0, background: C.teal, border: "1px solid " + C.teal, color: "white" }}>
                  <span style={{ ...ic, color: "white" }}><Icon name="upload" size={20} /></span>
                  <span style={{ flex: 1 }}>Export to CSV ({companyCustomerCount} contacts)</span>
                </button>
              </>
            );
          })()}
        </div>
      )}
      {/* Customer List */}
      <div style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 12, marginBottom: 12, overflow: "hidden" }}>
        <button
          onClick={() => setShowCustomerList(!showCustomerList)}
          style={{ width: "100%", padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 600, color: theme.text }}><Icon name="users" size={20} /> Customers</span>
          <span style={{ color: theme.subText }}><Icon name={showCustomerList ? "chevronUp" : "chevronDown"} size={20} /></span>
        </button>
        {showCustomerList && (
          <div style={{ padding: "0 12px 12px" }}>
            {/* PATCH oct21-ui-lists: one search box for name, phone, city or product */}
            {(() => {
              var sel = { flex: 1, minWidth: 0, minHeight: 44, padding: "0 10px", borderRadius: 10, border: "1px solid " + theme.inputBorder, fontSize: 15, background: theme.inputBg, color: theme.text, fontFamily: "inherit" };
              var looksPhone = /^[\d\s()+.\-]+$/.test(search.trim()) && search.replace(/\D/g, "").length >= 7;
              var filtered = search.trim() !== "" || filterStatus !== "all" || filterArea !== "all";
              var roundSm = (bg) => ({ width: 44, height: 44, borderRadius: 99, background: bg, color: "white", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, textDecoration: "none", padding: 0 });
              return (
                <>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, minHeight: 44, background: theme.inputBg, border: "1px solid " + theme.inputBorder, borderRadius: 10, padding: "0 4px 0 12px", boxSizing: "border-box" }}>
                      <span style={{ color: theme.mutedText }}><Icon name="search" size={18} /></span>
                      <input
                        ref={searchInputRef}
                        aria-label="Search customers" autoComplete="bp-off"
                        placeholder="Name, phone, city or product"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            fetchCustomers(search, filterStatus, filterArea);
                            if (searchInputRef.current) searchInputRef.current.blur();
                          }
                        }}
                        style={{ flex: 1, minWidth: 0, minHeight: 42, border: "none", outline: "none", fontSize: 16, background: "transparent", color: theme.text, fontFamily: "inherit" }}
                      />
                      {search !== "" && (
                        <button aria-label="Clear search" onClick={() => { setSearch(""); fetchCustomers("", filterStatus, filterArea); }}
                          style={{ width: 36, height: 36, background: "transparent", border: "none", color: theme.mutedText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon name="x" size={16} />
                        </button>
                      )}
                    </div>
                    <button aria-label="How search works" aria-expanded={showSearchInfo} onClick={() => setShowSearchInfo(!showSearchInfo)}
                      style={{ width: 44, height: 44, borderRadius: 10, border: "1px solid " + theme.cardBorder, background: showSearchInfo ? tealTint : theme.card, color: tealIcon, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon name="info" size={20} />
                    </button>
                  </div>
                  {showSearchInfo && (
                    <div style={{ background: theme.sectionBg, border: "1px solid " + theme.cardBorder, borderRadius: 10, padding: 12, marginBottom: 8, fontSize: 13, color: theme.subText, lineHeight: 1.5 }}>
                      Type any part of a name, phone number, city or product, for example smith, surrey or 5551. Upper or lower case does not matter. Phone numbers work in any format, with or without dashes, spaces, brackets or the leading 1. Results update as you type. Tap the x to clear and see all customers again.
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <select aria-label="Status" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); fetchCustomers(search, e.target.value, filterArea); }} style={sel}>
                      <option value="all">All Statuses</option>
                      {SL.map((s) => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                      <option value="existing">Existing</option>
                    </select>
                    <select aria-label="City" value={filterArea} onChange={(e) => { setFilterArea(e.target.value); fetchCustomers(search, filterStatus, e.target.value); }} style={sel}>
                      {allAreas.map((a) => (
                        <option key={a} value={a}>{a === "all" ? "All Cities" : a}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: "flex", padding: 3, background: theme.sectionBg, border: "1px solid " + theme.inputBorder, borderRadius: 10, marginBottom: 10 }}>
                    {[[null, "Recent"], ["name", "A to Z"], ["newest", "Newest"]].map(([k, t]) => {
                      var on = sortMode === k;
                      return (
                        <button key={t} aria-pressed={on} onClick={() => { setSortMode(k); fetchCustomers(search, filterStatus, filterArea, false, k); }}
                          style={{ flex: 1, minHeight: 38, borderRadius: 8, border: "none", background: on ? C.teal : "transparent", color: on ? "white" : theme.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                          {t}
                        </button>
                      );
                    })}
                  </div>
                  {filtered && !loading && <div style={{ fontSize: 13, color: theme.mutedText, margin: "0 2px 8px" }}>{totalCount} found</div>}
                  {loading && <p style={{ textAlign: "center", color: theme.subText }}>Loading...</p>}
                  {!loading && customers.length === 0 && (
                    <div style={{ textAlign: "center", padding: "12px 0" }}>
                      <p style={{ color: theme.subText, margin: "0 0 10px", fontSize: 15 }}>No customers found</p>
                      {looksPhone && (
                        <button
                          onClick={() => {
                            setNewCustomer({ ...newCustomer, phone: search.trim() });
                            setShowAddForm(true);
                            setSearch("");
                            fetchCustomers("", "all", "all");
                            window.scrollTo(0, 0);
                          }}
                          style={{ minHeight: 44, padding: "0 18px", background: C.teal, color: "white", border: "1px solid " + C.teal, borderRadius: 10, cursor: "pointer", fontSize: 15, fontWeight: 600, fontFamily: "inherit" }}
                        >
                          + Add as new customer
                        </button>
                      )}
                    </div>
                  )}
                  {!loading &&
                    customers.map((cu) => (
                      <div
                        key={cu.id}
                        onClick={() => {
                          setSelectedCustomer(cu);
                          fetchJobs(cu.id);
                        }}
                        style={{ background: theme.card, border: "1px solid " + theme.cardBorder, borderRadius: 12, padding: 14, marginBottom: 8, cursor: "pointer" }}
                      >
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{cu.full_name}</div>
                            {cu.phone && <div style={{ fontSize: 15, color: theme.text, marginTop: 2 }}>{fp(cu.phone)}</div>}
                            {(cu.area || cu.product_interest) && <div style={{ fontSize: 13, color: theme.mutedText, marginTop: 2 }}>{[cu.area, cu.product_interest].filter(Boolean).join(" · ")}</div>}
                            {cu.last_contacted && <div style={{ fontSize: 13, color: theme.mutedText }}>Last contacted {fmtDate(cu.last_contacted)}</div>}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                            {cu.job_status && (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: theme.card, border: "1px solid " + theme.cardBorder, color: theme.text, padding: "3px 10px", borderRadius: 99, fontSize: 13, fontWeight: 600 }}>
                                <span style={{ width: 8, height: 8, borderRadius: 99, background: SC[normStatus(cu.job_status)] || theme.mutedText }}></span>
                                {normStatus(cu.job_status).charAt(0).toUpperCase() + normStatus(cu.job_status).slice(1)}
                              </span>
                            )}
                            {cu.customer_type === "existing" && (
                              <span style={{ fontSize: 13, fontWeight: 600, color: theme.subText, background: theme.sectionBg, border: "1px solid " + theme.cardBorder, padding: "3px 10px", borderRadius: 99 }}>Existing</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                          <input
                            aria-label={"Quick note for " + cu.full_name}
                            placeholder="Quick note..."
                            value={quickNote[cu.id] || ""}
                            onChange={(e) => setQuickNote({ ...quickNote, [cu.id]: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") addQuickNote(cu.id); }}
                            style={{ flex: 1, minWidth: 0, minHeight: 44, padding: "0 12px", borderRadius: 10, border: "1px solid " + theme.inputBorder, fontSize: 15, background: theme.inputBg, color: theme.text, fontFamily: "inherit", boxSizing: "border-box" }}
                          />
                          {quickNote[cu.id] && quickNote[cu.id].trim() && (
                            <button onClick={() => addQuickNote(cu.id)}
                              style={{ minHeight: 44, padding: "0 14px", background: C.teal, color: "white", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 15, fontWeight: 600, fontFamily: "inherit", flexShrink: 0 }}>
                              Add
                            </button>
                          )}
                          {cu.phone && (
                            <>
                              <button aria-label={"Call " + cu.full_name} onClick={() => { makeCall(cu.phone); updateLastContacted(cu.id); }} style={roundSm(C.teal)}>
                                <Icon name="phone" size={18} />
                              </button>
                              <a aria-label={"WhatsApp " + cu.full_name} href={"https://wa.me/" + waNum(cu.phone)} target="_blank" rel="noreferrer" onClick={() => updateLastContacted(cu.id)} style={roundSm("#1E7A45")}>
                                <Icon name="whatsapp" size={18} />
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                </>
              );
            })()}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

export default App;

